import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError, descargarArchivo } from "../api";
import { useAuth } from "../auth/AuthContext";

type CuentaGobierno = {
  id: number; codigo: string; nombre: string; clase: string; naturaleza: "debito" | "credito";
  cuenta_padre_id: number | null; nivel: number; acepta_movimiento: boolean; activo: boolean;
};
type Importacion = {
  id: number; nombre_archivo: string; total_filas: number; filas_importadas: number;
  filas_con_error: number; detalle_errores: string | null; creado_en: string;
};
type EstadoModulo = { modulo: string; estado: string; detalle: string };

// ------------------------------------------------- Contabilidad Pública
type ComprobanteGob = {
  id: number; tipo: string; numero: number; fecha: string; descripcion: string | null;
  origen: string; origen_tipo: string | null; origen_id: number | null; estado: string;
};
type SaldoCuentaGob = {
  cuentaId: number; codigo: string; nombre: string; clase: string; naturaleza: string;
  totalDebito: number; totalCredito: number; saldo: number;
};
type BalancePruebaGob = { corte: string; cuentas: SaldoCuentaGob[]; totalDebito: number; totalCredito: number; cuadra: boolean };
type BalanceGeneralGob = {
  corte: string; activo: SaldoCuentaGob[]; pasivo: SaldoCuentaGob[]; patrimonio: SaldoCuentaGob[];
  ingreso: SaldoCuentaGob[]; gasto: SaldoCuentaGob[]; resultadoEjercicio: number;
  totalActivo: number; totalPasivo: number; totalPatrimonio: number; totalIngreso: number; totalGasto: number; cuadra: boolean;
};
type CuentaPredeterminadaGob = { concepto: string; cuenta_id: number; codigo: string; nombre: string };

// --------------------------------------------------- Tesorería Pública
type CuentaBancariaGob = {
  id: number; nombre: string; tipo: string; banco: string | null; numeroCuenta: string | null;
  saldoInicial: number; saldoActual: number; activa: boolean;
};
type MovimientoGob = {
  id: number; tipo: "ingreso" | "egreso" | "transferencia"; cuentaBancariaId: number; cuentaNombre: string;
  cuentaBancariaDestinoId: number | null; cuentaDestinoNombre: string | null; concepto: string;
  descripcion: string | null; valor: number; fecha: string; comprobanteId: number | null;
};

// -------------------------------------------------- terceros (compartido)
type TerceroGob = {
  id: number; tipo_documento: string; numero_documento: string; nombre: string; tipo_persona: string; activo: boolean;
  direccion?: string | null; codigo_dpto?: string | null; codigo_mun?: string | null; codigo_pais?: string | null;
};

// -------------------------------------------------- Presupuesto Público
type ApropiacionGob = {
  id: number; vigencia_fiscal: number; rubro_codigo: string; rubro_nombre: string; valor_apropiado: number;
  saldoDisponible: number; valorVigente: number;
};
type CdpGob = {
  id: number; apropiacion_id: number; numero: number; fecha: string; valor: number; descripcion: string;
  estado: string; rubro_codigo: string; rubro_nombre: string; saldoDisponible: number;
};
type RpGob = {
  id: number; cdp_id: number; numero: number; fecha: string; valor: number; descripcion: string; estado: string;
  beneficiario_tercero_id: number | null; beneficiario_nombre: string | null; saldoDisponible: number;
};
type ObligacionGob = {
  id: number; rp_id: number; numero: number; fecha: string; valor: number; descripcion: string; estado: string;
  rp_numero: number; beneficiario_nombre: string | null; comprobante_id: number | null;
};
type EjecucionRubroGob = {
  apropiacionId: number; rubroCodigo: string; rubroNombre: string; valorApropiado: number;
  comprometido: number; obligado: number; pagado: number; saldoPorComprometer: number;
};
// Alineación con TransforWeb: Ingresos, Modificaciones (Adición/Reducción/Crédito/Contra-crédito),
// Reservas Presupuestales, Cuentas por Pagar presupuestales, Configuración (Fuentes de
// Financiación/Descuentos) y Trazabilidad.
type IngresoPresupuestalGob = {
  id: number; vigencia_fiscal: number; rubro_codigo: string; rubro_nombre: string; categoria: string;
  valor_presupuestado: number; activo: boolean; creado_por: number; creado_en: string;
  valorVigente: number; recaudado: number;
};
type RecaudoIngresoGob = {
  id: number; ingreso_id: number; fecha: string; valor: number; descripcion: string | null;
  comprobante_id: number | null; movimiento_tesoreria_id: number | null; creado_por: number; creado_en: string;
};
type ModificacionPresupuestalGob = {
  id: number; lado: "gasto" | "ingreso"; tipo: "adicion" | "reduccion" | "credito" | "contracredito";
  apropiacion_id: number | null; ingreso_id: number | null; valor: number; fecha: string;
  descripcion: string | null; traspaso_id: number | null; creado_por: number; creado_en: string;
};
type ReservaPresupuestalGob = {
  id: number; vigencia_fiscal: number; rp_id: number | null; descripcion: string; valor: number;
  fecha_constitucion: string; estado: "vigente" | "obligada" | "pagada"; comprobante_id: number | null;
  movimiento_tesoreria_id: number | null; creado_por: number; creado_en: string;
};
type CuentaPorPagarPresupuestalGob = {
  id: number; vigencia_fiscal: number; obligacion_id: number | null; descripcion: string; valor: number;
  fecha_constitucion: string; estado: "vigente" | "pagada"; movimiento_tesoreria_id: number | null;
  creado_por: number; creado_en: string;
};
type FuenteFinanciacionGob = { id: number; codigo: string; nombre: string; activo: boolean; creado_en: string };
type DescuentoPresupuestalGob = {
  id: number; codigo: string; nombre: string; tipo: "porcentaje" | "valor_fijo"; valor: number; activo: boolean; creado_en: string;
};

// -------------------------------------------------- Nómina Pública
type EmpleadoGob = {
  id: number; nombre: string; tipoDocumento: string; numeroDocumento: string; cargo: string | null; estado: string;
};
type ContratoGob = { id: number; empleadoId: number; tipoContrato: string; salarioBase: number; fechaInicio: string; estado: string };
type PeriodoGob = { id: number; anio: number; mes: number; fechaInicio: string; fechaFin: string; estado: string };
type LiquidacionGob = {
  id: number; periodo_id: number; empleado_id: number; empleado_nombre: string; anio: number; mes: number;
  fecha_fin: string; total_devengado: number; total_deducciones: number; neto_pagado: number; estado: string; comprobante_gobierno_id: number | null;
};

// -------------------------------------------------- Activos Fijos públicos
type ActivoGob = {
  id: number; codigo: string; nombre: string; categoria: string; fecha_adquisicion: string; valor_adquisicion: number;
  valor_residual: number; vida_util_meses: number; estado: string; depreciacionAcumulada: number; valorLibros: number;
};

// -------------------------------------------------- Información Exógena pública
type ReporteExogenaGob = {
  id: number; formato: string; anio_gravable: number; cantidad_registros: number; valor_total: number; estado: string; generado_en: string;
};

// -------------------------------------------------- Territorial (predial/ICA)
type PredioGob = {
  id: number; cedula_catastral: string; direccion: string; propietario_nombre: string; tipo_predio: string;
  estrato: number | null; avaluo_catastral: number; activo: boolean;
};
type LiquidacionPredialGob = {
  id: number; predio_id: number; cedula_catastral: string; direccion: string; vigencia_fiscal: number;
  tarifa_por_mil: number; valor_liquidado: number; estado: string; comprobante_id: number | null;
};
type ContribuyenteIcaGob = {
  id: number; tercero_nombre: string; actividad_economica_nombre: string; tipo_actividad: string; activo: boolean;
};
type LiquidacionIcaGob = {
  id: number; contribuyente_id: number; tercero_nombre: string; vigencia_fiscal: number; ingresos_brutos_base: number;
  tarifa_por_mil: number; valor_liquidado: number; estado: string; comprobante_id: number | null;
};
// Alineación con TransforWeb: Acuerdos de Pago, Gestión de Cobro, Cartera, Deterioro de Cartera,
// Prescripciones y Tabla de Intereses (compartidos predial/ICA); Conceptos, Conceptos por Predio,
// Estrato, Tarifas Generales y Tarifas Por Predio (predial); Declaración Rete ICA (ICA).
type AcuerdoPagoGob = {
  id: number; modulo: "predial" | "ica"; liquidacion_id: number; numero_cuotas: number; valor_cuota: number;
  cuotas_pagadas: number; fecha_acuerdo: string; estado: "vigente" | "cumplido" | "incumplido";
  creado_por: number; creado_en: string; referencia?: string; referenciaDetalle?: string;
};
type GestionCobroGob = {
  id: number; modulo: "predial" | "ica"; liquidacion_id: number; fecha: string; tipo_gestion: string;
  observaciones: string | null; usuario_id: number; creado_en: string; referencia?: string; referenciaDetalle?: string;
};
type CarteraItemGob = {
  id: number; vigencia_fiscal: number; valor_liquidado: number; estado: string; diasMora: number; interesCausado: number;
  // Campos propios de predial o de ICA según el módulo consultado (ver TIPO_PREDIO_LABEL/ContribuyenteIcaGob).
  cedula_catastral?: string; direccion?: string; tercero_nombre?: string;
};
type InformeContraloriaGob = {
  modulo: string; vigenciaFiscal: number; totalLiquidaciones: number; totalLiquidado: number; totalRecaudado: number;
  totalPendiente: number; totalAnulado: number; porcentajeRecaudo: number;
};
type DeterioroCarteraGob = {
  id: number; modulo: "predial" | "ica"; liquidacion_id: number; valor: number; fecha: string;
  comprobante_id: number | null; creado_por: number; creado_en: string;
};
type PrescripcionCarteraGob = {
  id: number; modulo: "predial" | "ica"; liquidacion_id: number; valor: number; fecha: string; motivo: string | null;
  comprobante_id: number | null; creado_por: number; creado_en: string;
};
type TablaInteresGob = { id: number; vigencia_fiscal: number; tasa_mensual: number; creado_por: number; creado_en: string };
type PazYSalvoGob = {
  predio: PredioGob & { propietario_documento?: string };
  pazYSalvo: boolean; vigenciasPendientes: { vigenciaFiscal: number; valor: number }[]; vigenciasPagadas: number[];
};
type ConceptoPredialGob = { id: number; codigo: string; nombre: string; tipo: "recargo" | "descuento"; activo: boolean; creado_en: string };
type ConceptoPredioGob = {
  id: number; predio_id: number; concepto_id: number; vigencia_fiscal: number; valor: number;
  codigo: string; nombre: string; tipo: "recargo" | "descuento"; creado_en: string;
};
type EstratoPredialGob = { id: number; estrato: number; descripcion: string | null; tarifa_sugerida_por_mil: number | null; creado_en: string };
type TarifaGeneralPredialGob = {
  id: number; vigencia_fiscal: number; tipo_predio: string; estrato_desde: number | null; estrato_hasta: number | null;
  tarifa_por_mil: number; creado_en: string;
};
type TarifaPredioGob = { id: number; predio_id: number; vigencia_fiscal: number; tarifa_por_mil: number; motivo: string | null; creado_en: string };
type ReteIcaGob = {
  id: number; contribuyente_id: number; periodo: string; base_retencion: number; tarifa_por_mil: number;
  valor_retenido: number; estado: "pendiente" | "pagado" | "anulado"; comprobante_id: number | null;
  movimiento_tesoreria_id: number | null; creado_por: number; creado_en: string;
  tercero_nombre: string; actividad_economica_nombre: string;
};

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const ETIQUETA_FORMATO_EP: Record<string, string> = { pdf: "PDF", xlsx: "Excel", docx: "Word", txt: "TXT (DIAN)" };

// Control reutilizable de descarga de reportes de SIGEPRO-EP (mismo patrón y
// mismo motor genérico app/exportes_utils.py que usan Nómina/Contabilidad del
// núcleo privado — ver DescargaReporte en Contabilidad.tsx): selector de
// formato + botón, contra cualquier endpoint "<ruta>/exportar" de SIGEPRO-EP.
function DescargaReporteEP({ endpoint, params, nombreBase, formatos = ["pdf", "xlsx", "docx"], label = "Descargar" }: {
  endpoint: string; params?: Record<string, string | number | undefined>; nombreBase: string; formatos?: string[]; label?: string;
}) {
  const [formato, setFormato] = useState(formatos[0]);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descargar = async () => {
    setDescargando(true);
    setError(null);
    try {
      const entradas = Object.entries({ formato, ...params }).filter(([, v]) => v !== undefined && v !== "") as [string, string | number][];
      const qs = new URLSearchParams(entradas.map(([k, v]) => [k, String(v)]));
      await descargarArchivo(`${endpoint}?${qs.toString()}`, `${nombreBase}.${formato}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo generar el reporte.");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
        value={formato} onChange={(e) => setFormato(e.target.value)}
      >
        {formatos.map((f) => <option key={f} value={f}>{ETIQUETA_FORMATO_EP[f] || f}</option>)}
      </select>
      <button className="btn-ghost" disabled={descargando} onClick={descargar}>{descargando ? "Generando..." : label}</button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

const TIPO_CUENTA_LABEL_GOB: Record<string, string> = { ahorros: "Ahorros", corriente: "Corriente", caja_menor: "Caja menor", tarjeta: "Tarjeta" };
const TIPO_MOV_BADGE_GOB: Record<string, string> = {
  ingreso: "bg-accent-light text-accent-dark", egreso: "bg-red-50 text-red-700", transferencia: "bg-amber-50 text-amber-700",
};

const CLASE_LABEL: Record<string, string> = {
  activo: "1 — Activos", pasivo: "2 — Pasivos", patrimonio: "3 — Patrimonio",
  ingreso: "4 — Ingresos", gasto: "5 — Gastos",
  costo_ventas: "6 — Costos de Ventas", costo_transformacion: "7 — Costos de Transformación",
  cuenta_orden_deudora: "8 — Cuentas de Orden Deudoras", cuenta_orden_acreedora: "9 — Cuentas de Orden Acreedoras",
};

const ESTADO_BADGE: Record<string, string> = {
  disponible: "bg-accent-light text-accent-dark",
  "investigado, motor pendiente": "bg-amber-50 text-amber-700",
};

export function SigeproEP() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("sigepro_ep:crear");
  const puedeImportar = tienePermiso("sigepro_ep:importar");
  const puedeVerContab = tienePermiso("sigepro_ep_contabilidad:ver");
  const puedeCrearContab = tienePermiso("sigepro_ep_contabilidad:crear");
  const puedeVerTesoreria = tienePermiso("sigepro_ep_tesoreria:ver");
  const puedeCrearTesoreria = tienePermiso("sigepro_ep_tesoreria:crear");
  const puedeVerPresupuesto = tienePermiso("sigepro_ep_presupuesto:ver");
  const puedeCrearPresupuesto = tienePermiso("sigepro_ep_presupuesto:crear");
  const puedeCausarPresupuesto = tienePermiso("sigepro_ep_presupuesto:causar");
  const puedePagarPresupuesto = tienePermiso("sigepro_ep_presupuesto:pagar");
  const puedeModificarPresupuesto = tienePermiso("sigepro_ep_presupuesto:modificar");
  const puedeConfigurarPresupuesto = tienePermiso("sigepro_ep_presupuesto:configurar");
  const puedeVerNomina = tienePermiso("sigepro_ep_nomina:ver");
  const puedeCrearNomina = tienePermiso("sigepro_ep_nomina:crear");
  const puedeLiquidarNomina = tienePermiso("sigepro_ep_nomina:liquidar");
  const puedePagarNomina = tienePermiso("sigepro_ep_nomina:pagar");
  const puedeVerActivosGob = tienePermiso("sigepro_ep_activos:ver");
  const puedeCrearActivosGob = tienePermiso("sigepro_ep_activos:crear");
  const puedeDepreciarActivosGob = tienePermiso("sigepro_ep_activos:depreciar");
  const puedeBajaActivosGob = tienePermiso("sigepro_ep_activos:baja");
  const puedeVerExogenaGob = tienePermiso("sigepro_ep_exogena:ver");
  const puedeGenerarExogenaGob = tienePermiso("sigepro_ep_exogena:generar");
  const puedeVerTerritorial = tienePermiso("sigepro_ep_territorial:ver");
  const puedeCrearTerritorial = tienePermiso("sigepro_ep_territorial:crear");
  const puedeLiquidarTerritorial = tienePermiso("sigepro_ep_territorial:liquidar");
  const puedePagarTerritorial = tienePermiso("sigepro_ep_territorial:pagar");
  const puedeAjustarTerritorial = tienePermiso("sigepro_ep_territorial:ajustar");
  const puedeVerInformes = tienePermiso("sigepro_ep_informes:ver");
  const puedeGenerarInformes = tienePermiso("sigepro_ep_informes:generar");

  const [tab, setTab] = useState("catalogo");
  const [cuentas, setCuentas] = useState<CuentaGobierno[]>([]);
  const [importaciones, setImportaciones] = useState<Importacion[]>([]);
  const [estado, setEstado] = useState<EstadoModulo[]>([]);
  const [comprobantesGob, setComprobantesGob] = useState<ComprobanteGob[]>([]);
  const [balancePrueba, setBalancePrueba] = useState<BalancePruebaGob | null>(null);
  const [balanceGeneral, setBalanceGeneral] = useState<BalanceGeneralGob | null>(null);
  const [cuentasPred, setCuentasPred] = useState<CuentaPredeterminadaGob[]>([]);
  const [cuentasBancariasGob, setCuentasBancariasGob] = useState<CuentaBancariaGob[]>([]);
  const [movimientosGob, setMovimientosGob] = useState<MovimientoGob[]>([]);
  const [terceros, setTerceros] = useState<TerceroGob[]>([]);
  const [apropiaciones, setApropiaciones] = useState<ApropiacionGob[]>([]);
  const [cdps, setCdps] = useState<CdpGob[]>([]);
  const [rps, setRps] = useState<RpGob[]>([]);
  const [obligaciones, setObligaciones] = useState<ObligacionGob[]>([]);
  const [ingresosPresupuesto, setIngresosPresupuesto] = useState<IngresoPresupuestalGob[]>([]);
  const [modificacionesPresupuesto, setModificacionesPresupuesto] = useState<ModificacionPresupuestalGob[]>([]);
  const [reservasPresupuesto, setReservasPresupuesto] = useState<ReservaPresupuestalGob[]>([]);
  const [cuentasPorPagarPresupuesto, setCuentasPorPagarPresupuesto] = useState<CuentaPorPagarPresupuestalGob[]>([]);
  const [fuentesFinanciacion, setFuentesFinanciacion] = useState<FuenteFinanciacionGob[]>([]);
  const [descuentosPresupuesto, setDescuentosPresupuesto] = useState<DescuentoPresupuestalGob[]>([]);
  const [empleadosGob, setEmpleadosGob] = useState<EmpleadoGob[]>([]);
  const [contratosGob, setContratosGob] = useState<ContratoGob[]>([]);
  const [periodosGob, setPeriodosGob] = useState<PeriodoGob[]>([]);
  const [liquidacionesGob, setLiquidacionesGob] = useState<LiquidacionGob[]>([]);
  const [activosGob, setActivosGob] = useState<ActivoGob[]>([]);
  const [reportesExogenaGob, setReportesExogenaGob] = useState<ReporteExogenaGob[]>([]);
  const [prediosGob, setPrediosGob] = useState<PredioGob[]>([]);
  const [liquidacionesPredialGob, setLiquidacionesPredialGob] = useState<LiquidacionPredialGob[]>([]);
  const [contribuyentesIcaGob, setContribuyentesIcaGob] = useState<ContribuyenteIcaGob[]>([]);
  const [liquidacionesIcaGob, setLiquidacionesIcaGob] = useState<LiquidacionIcaGob[]>([]);
  const [acuerdosPagoGob, setAcuerdosPagoGob] = useState<AcuerdoPagoGob[]>([]);
  const [gestionesCobroGob, setGestionesCobroGob] = useState<GestionCobroGob[]>([]);
  const [deterioroCarteraGob, setDeterioroCarteraGob] = useState<DeterioroCarteraGob[]>([]);
  const [prescripcionesGob, setPrescripcionesGob] = useState<PrescripcionCarteraGob[]>([]);
  const [tablaInteresesGob, setTablaInteresesGob] = useState<TablaInteresGob[]>([]);
  const [conceptosPredialGob, setConceptosPredialGob] = useState<ConceptoPredialGob[]>([]);
  const [estratosPredialGob, setEstratosPredialGob] = useState<EstratoPredialGob[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `esRecarga` evita el parpadeo de "Cargando..." (y el desmontaje de los
  // tabs, que borraría su estado local — p.ej. el resumen de una importación
  // recién hecha) en recargas posteriores a la primera; solo la carga inicial
  // muestra el estado de carga a pantalla completa.
  const cargar = async (esRecarga = false) => {
    if (!esRecarga) setCargando(true);
    setError(null);
    try {
      const [c, i, e] = await Promise.all([
        api.get("/sigepro-ep/cuentas"),
        api.get("/sigepro-ep/importaciones"),
        api.get("/sigepro-ep/estado"),
      ]);
      setCuentas(c); setImportaciones(i); setEstado(e);
      // Contabilidad Pública y Tesorería Pública son módulos con permisos propios
      // (sigepro_ep_contabilidad:ver / sigepro_ep_tesoreria:ver) — a diferencia del
      // catálogo (sigepro_ep:*), no todo usuario con acceso a SIGEPRO-EP los tiene
      // necesariamente, así que solo se consultan si el permiso está presente (evita
      // 403 innecesarios y permite ocultar las pestañas para quien no puede verlos).
      if (puedeVerContab) {
        const [comp, bp, bg, cp] = await Promise.all([
          api.get("/sigepro-ep/contabilidad/comprobantes"),
          api.get("/sigepro-ep/contabilidad/balance-prueba"),
          api.get("/sigepro-ep/contabilidad/balance-general"),
          api.get("/sigepro-ep/contabilidad/cuentas-predeterminadas"),
        ]);
        setComprobantesGob(comp); setBalancePrueba(bp); setBalanceGeneral(bg); setCuentasPred(cp);
      }
      if (puedeVerTesoreria) {
        const [cb, mv] = await Promise.all([
          api.get("/sigepro-ep/tesoreria/cuentas"),
          api.get("/sigepro-ep/tesoreria/movimientos"),
        ]);
        setCuentasBancariasGob(cb); setMovimientosGob(mv);
      }
      // terceros es el registro compartido (Presupuesto/Exógena/Territorial) —
      // se consulta si cualquiera de esos tres módulos es visible.
      if (puedeVerPresupuesto || puedeVerExogenaGob || puedeVerTerritorial) {
        setTerceros(await api.get("/sigepro-ep/terceros"));
      }
      if (puedeVerPresupuesto) {
        const [ap, cd, rp, ob, ing, mod, res, cxp, ff, desc] = await Promise.all([
          api.get("/sigepro-ep/presupuesto/apropiaciones"),
          api.get("/sigepro-ep/presupuesto/cdps"),
          api.get("/sigepro-ep/presupuesto/rps"),
          api.get("/sigepro-ep/presupuesto/obligaciones"),
          api.get("/sigepro-ep/presupuesto/ingresos"),
          api.get("/sigepro-ep/presupuesto/modificaciones"),
          api.get("/sigepro-ep/presupuesto/reservas"),
          api.get("/sigepro-ep/presupuesto/cuentas-por-pagar"),
          api.get("/sigepro-ep/presupuesto/fuentes-financiacion"),
          api.get("/sigepro-ep/presupuesto/descuentos"),
        ]);
        setApropiaciones(ap); setCdps(cd); setRps(rp); setObligaciones(ob);
        setIngresosPresupuesto(ing); setModificacionesPresupuesto(mod); setReservasPresupuesto(res);
        setCuentasPorPagarPresupuesto(cxp); setFuentesFinanciacion(ff); setDescuentosPresupuesto(desc);
      }
      if (puedeVerNomina) {
        const [em, ct, pe, li] = await Promise.all([
          api.get("/sigepro-ep/nomina/empleados"),
          api.get("/sigepro-ep/nomina/contratos"),
          api.get("/sigepro-ep/nomina/periodos"),
          api.get("/sigepro-ep/nomina/liquidaciones"),
        ]);
        setEmpleadosGob(em); setContratosGob(ct); setPeriodosGob(pe); setLiquidacionesGob(li);
      }
      if (puedeVerActivosGob) {
        setActivosGob(await api.get("/sigepro-ep/activos"));
      }
      if (puedeVerExogenaGob) {
        setReportesExogenaGob(await api.get("/sigepro-ep/exogena/reportes"));
      }
      if (puedeVerTerritorial) {
        const [pr, lp, ci, li, ap, gc, dc, pre, ti, cp, es] = await Promise.all([
          api.get("/sigepro-ep/territorial/predial/predios"),
          api.get("/sigepro-ep/territorial/predial/liquidaciones"),
          api.get("/sigepro-ep/territorial/ica/contribuyentes"),
          api.get("/sigepro-ep/territorial/ica/liquidaciones"),
          api.get("/sigepro-ep/territorial/acuerdos-pago"),
          api.get("/sigepro-ep/territorial/gestion-cobro"),
          api.get("/sigepro-ep/territorial/deterioro-cartera"),
          api.get("/sigepro-ep/territorial/prescripciones"),
          api.get("/sigepro-ep/territorial/tabla-intereses"),
          api.get("/sigepro-ep/territorial/predial/conceptos"),
          api.get("/sigepro-ep/territorial/predial/estratos"),
        ]);
        setPrediosGob(pr); setLiquidacionesPredialGob(lp); setContribuyentesIcaGob(ci); setLiquidacionesIcaGob(li);
        setAcuerdosPagoGob(ap); setGestionesCobroGob(gc); setDeterioroCarteraGob(dc); setPrescripcionesGob(pre);
        setTablaInteresesGob(ti); setConceptosPredialGob(cp); setEstratosPredialGob(es);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar SIGEPRO-EP.");
    } finally {
      if (!esRecarga) setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);
  const recargar = () => cargar(true);

  return (
    <Shell titulo="SIGEPRO-EP — Entidades Públicas" breadcrumb="SIGEPRO-EP / Entidades Públicas">
      <div className="rounded-md bg-navy/5 border border-navy/10 px-4 py-3 mb-4">
        <p className="text-sm text-slate-700">
          SIGEPRO-EP es el paquete de módulos de SIGEPRO ERP para <strong>entidades públicas</strong> colombianas
          (alcaldías, gobernaciones y entidades del orden nacional o territorial). Está deliberadamente separado del
          núcleo privado (Fases 1-13): una entidad pública no usa el PUC del Decreto 2650/1993, sino el{" "}
          <strong>Catálogo General de Cuentas (CGC)</strong> del Marco Normativo para Entidades de Gobierno de la
          Contaduría General de la Nación (CGN) — un plan de cuentas de 9 clases, no de 6. Por eso este catálogo
          (<code className="text-xs bg-white/60 px-1 rounded">cuentas_gobierno</code>) nunca comparte tablas con el
          plan de cuentas privado. Ver la sección "SIGEPRO-EP" del README para el detalle de la investigación
          normativa (con fuentes) y el alcance real de esta entrega en la pestaña "Estado y hoja de ruta".
        </p>
      </div>

      <Tabs
        tabs={[
          { key: "catalogo", label: "Catálogo (CGC)" },
          { key: "importar", label: "Importar catálogo" },
          ...(puedeVerContab ? [{ key: "contabilidad", label: "Contabilidad Pública" }] : []),
          ...(puedeVerTesoreria ? [{ key: "tesoreria", label: "Tesorería Pública" }] : []),
          ...(puedeVerPresupuesto ? [{ key: "presupuesto", label: "Presupuesto Público" }] : []),
          ...(puedeVerNomina ? [{ key: "nomina", label: "Nómina Pública" }] : []),
          ...(puedeVerActivosGob ? [{ key: "activos", label: "Activos Fijos" }] : []),
          ...(puedeVerExogenaGob ? [{ key: "exogena", label: "Información Exógena" }] : []),
          ...(puedeVerTerritorial ? [{ key: "territorial", label: "Impuestos Territoriales" }] : []),
          ...(puedeVerInformes ? [{ key: "informes", label: "Informes Ejecutivos" }] : []),
          ...(puedeVerPresupuesto || puedeVerContab ? [{ key: "auditorias", label: "Auditorías" }] : []),
          { key: "estado", label: "Estado y hoja de ruta" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "catalogo" && <TabCatalogo cuentas={cuentas} puedeCrear={puedeCrear} recargar={recargar} />}
          {tab === "importar" && (
            <TabImportar importaciones={importaciones} puedeImportar={puedeImportar} recargar={recargar} />
          )}
          {tab === "contabilidad" && puedeVerContab && (
            <TabContabilidadGob
              comprobantes={comprobantesGob} balancePrueba={balancePrueba} balanceGeneral={balanceGeneral}
              cuentasPred={cuentasPred} cuentas={cuentas} puedeCrear={puedeCrearContab} recargar={recargar}
            />
          )}
          {tab === "tesoreria" && puedeVerTesoreria && (
            <TabTesoreriaGob
              cuentasBancarias={cuentasBancariasGob} movimientos={movimientosGob}
              puedeCrear={puedeCrearTesoreria} recargar={recargar}
            />
          )}
          {tab === "presupuesto" && puedeVerPresupuesto && (
            <TabPresupuestoGob
              apropiaciones={apropiaciones} cdps={cdps} rps={rps} obligaciones={obligaciones}
              ingresos={ingresosPresupuesto} modificaciones={modificacionesPresupuesto} reservas={reservasPresupuesto}
              cuentasPorPagar={cuentasPorPagarPresupuesto} fuentesFinanciacion={fuentesFinanciacion} descuentos={descuentosPresupuesto}
              terceros={terceros} cuentasBancarias={cuentasBancariasGob}
              puedeCrear={puedeCrearPresupuesto} puedeCausar={puedeCausarPresupuesto} puedePagar={puedePagarPresupuesto}
              puedeModificar={puedeModificarPresupuesto} puedeConfigurar={puedeConfigurarPresupuesto}
              irAContabilidad={() => setTab("contabilidad")}
              recargar={recargar}
            />
          )}
          {tab === "nomina" && puedeVerNomina && (
            <TabNominaGob
              empleados={empleadosGob} contratos={contratosGob} periodos={periodosGob} liquidaciones={liquidacionesGob}
              cuentasBancarias={cuentasBancariasGob}
              puedeCrear={puedeCrearNomina} puedeLiquidar={puedeLiquidarNomina} puedePagar={puedePagarNomina}
              recargar={recargar}
            />
          )}
          {tab === "activos" && puedeVerActivosGob && (
            <TabActivosGob
              activos={activosGob} cuentasBancarias={cuentasBancariasGob}
              puedeCrear={puedeCrearActivosGob} puedeDepreciar={puedeDepreciarActivosGob} puedeBaja={puedeBajaActivosGob}
              recargar={recargar}
            />
          )}
          {tab === "exogena" && puedeVerExogenaGob && (
            <TabExogenaGob reportes={reportesExogenaGob} puedeGenerar={puedeGenerarExogenaGob} recargar={recargar} />
          )}
          {tab === "territorial" && puedeVerTerritorial && (
            <TabTerritorialGob
              predios={prediosGob} liquidacionesPredial={liquidacionesPredialGob}
              contribuyentesIca={contribuyentesIcaGob} liquidacionesIca={liquidacionesIcaGob}
              acuerdosPago={acuerdosPagoGob} gestionesCobro={gestionesCobroGob}
              deterioroCartera={deterioroCarteraGob} prescripciones={prescripcionesGob}
              tablaIntereses={tablaInteresesGob} conceptosPredial={conceptosPredialGob} estratosPredial={estratosPredialGob}
              terceros={terceros} cuentasBancarias={cuentasBancariasGob}
              puedeCrear={puedeCrearTerritorial} puedeLiquidar={puedeLiquidarTerritorial} puedePagar={puedePagarTerritorial}
              puedeAjustar={puedeAjustarTerritorial}
              recargar={recargar}
            />
          )}
          {tab === "informes" && puedeVerInformes && <TabInformesGob puedeGenerar={puedeGenerarInformes} />}
          {tab === "auditorias" && (puedeVerPresupuesto || puedeVerContab) && (
            <TabAuditoriasGob puedeVerPresupuesto={puedeVerPresupuesto} puedeVerContab={puedeVerContab} />
          )}
          {tab === "estado" && <TabEstado estado={estado} />}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- catálogo
function TabCatalogo({ cuentas, puedeCrear, recargar }: {
  cuentas: CuentaGobierno[]; puedeCrear: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [clase, setClase] = useState("activo");
  const [naturaleza, setNaturaleza] = useState<"debito" | "credito">("debito");
  const [codigoPadre, setCodigoPadre] = useState("");
  const [enviando, setEnviando] = useState(false);

  const sembrar = async () => {
    setSembrando(true);
    setMensaje(null);
    setError(null);
    try {
      const r = await api.post("/sigepro-ep/cuentas/sembrar");
      setMensaje(
        r.cuentasCreadas > 0
          ? `Estructura base sembrada: ${r.cuentasCreadas} cuenta(s) creada(s).`
          : "La estructura base ya estaba sembrada — no había cuentas nuevas por crear (operación segura de reintentar)."
      );
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo sembrar la estructura base.");
    } finally {
      setSembrando(false);
    }
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigo || !nombre) { setError("Código y nombre son obligatorios."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/cuentas", {
        codigo, nombre, clase, naturaleza, codigoPadre: codigoPadre || null, aceptaMovimiento: true,
      });
      setModalAbierto(false);
      setCodigo(""); setNombre(""); setCodigoPadre("");
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear la cuenta.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Catálogo General de Cuentas ({cuentas.length} cuentas)</h3>
        {puedeCrear && (
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={sembrar} disabled={sembrando}>
              {sembrando ? "Sembrando..." : "Sembrar estructura base"}
            </button>
            <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva cuenta</button>
          </div>
        )}
      </div>
      {mensaje && <div className="rounded-md bg-slate-50 border border-borderc px-3 py-2 text-sm text-slate-700 mb-3">{mensaje}</div>}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
      <p className="text-xs text-textmuted mb-3">
        "Sembrar estructura base" crea las 9 clases y los grupos (1-2 dígitos) reales del CGC — es seguro
        reintentar: los códigos que ya existen se omiten. El detalle (3+ dígitos) vigente a la fecha se importa
        desde la pestaña "Importar catálogo" o se crea cuenta por cuenta aquí.
      </p>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Clase</th>
            <th className="table-th">Naturaleza</th><th className="table-th">Nivel</th><th className="table-th">Acepta movimiento</th>
          </tr></thead>
          <tbody>
            {cuentas.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs" style={{ paddingLeft: `${8 + (c.nivel - 1) * 16}px` }}>{c.codigo}</td>
                <td className="table-td">{c.nombre}</td>
                <td className="table-td text-xs">{CLASE_LABEL[c.clase] || c.clase}</td>
                <td className="table-td text-xs capitalize">{c.naturaleza}</td>
                <td className="table-td text-xs">{c.nivel}</td>
                <td className="table-td text-xs">{c.acepta_movimiento ? "Sí" : "No"}</td>
              </tr>
            ))}
            {cuentas.length === 0 && (
              <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">
                Catálogo vacío todavía. {puedeCrear ? 'Use "Sembrar estructura base" para empezar.' : ""}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nueva cuenta del CGC</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
                <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Ej: 1105" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Código padre (opcional)</label>
                <input className="input-field" value={codigoPadre} onChange={(e) => setCodigoPadre(e.target.value)} placeholder="Ej: 11" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Clase</label>
                <select className="input-field" value={clase} onChange={(e) => setClase(e.target.value)}>
                  {Object.entries(CLASE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Naturaleza</label>
                <select className="input-field" value={naturaleza} onChange={(e) => setNaturaleza(e.target.value as any)}>
                  <option value="debito">Débito</option>
                  <option value="credito">Crédito</option>
                </select>
              </div>
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear cuenta"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- importación
function TabImportar({ importaciones, puedeImportar, recargar }: {
  importaciones: Importacion[]; puedeImportar: boolean; recargar: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState<{ totalFilas: number; filasImportadas: number; filasConError: number; errores: any[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const descargarPlantilla = async () => {
    try {
      await descargarArchivo("/sigepro-ep/plantilla", "plantilla_catalogo_cgc_sigepro_ep.csv");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo descargar la plantilla.");
    }
  };

  const importar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!archivo) { setError("Seleccione un archivo CSV o XLSX."); return; }
    setSubiendo(true);
    setError(null);
    setResultado(null);
    try {
      const form = new FormData();
      form.append("archivo", archivo);
      const r = await api.postForm("/sigepro-ep/importar", form);
      setResultado(r);
      setArchivo(null);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo importar el archivo.");
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div>
      <div className="card p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Importar catálogo desde CSV o XLSX</h3>
        <p className="text-xs text-textmuted mb-3">
          Encabezados esperados: <code className="bg-slate-50 px-1 rounded">codigo, nombre, clase, naturaleza,
          codigo_padre (opcional), acepta_movimiento (opcional, Sí/No)</code>. Las filas deben venir en orden
          jerárquico (primero el padre, luego los hijos). Ninguna fila con error aborta el archivo completo — el
          resumen indica exactamente cuántas filas se importaron y cuántas fallaron, y por qué.
        </p>
        <button type="button" className="text-navy hover:underline text-xs font-semibold mb-4" onClick={descargarPlantilla}>
          Descargar plantilla de ejemplo (con las cuentas reales del CGC)
        </button>
        {puedeImportar ? (
          <form onSubmit={importar} className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Archivo (.csv o .xlsx)</label>
              <input
                className="input-field"
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={subiendo || !archivo}>
              {subiendo ? "Importando..." : "Importar"}
            </button>
          </form>
        ) : (
          <p className="text-xs text-textmuted">No tiene permiso para importar (sigepro_ep:importar).</p>
        )}
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mt-3">{error}</div>}
        {resultado && (
          <div className="rounded-md bg-slate-50 border border-borderc px-3 py-3 mt-3 text-sm">
            <p className="text-slate-700">
              {resultado.totalFilas} fila(s) leídas — <span className="text-accent-dark font-semibold">{resultado.filasImportadas} importada(s)</span>
              {resultado.filasConError > 0 && <span className="text-red-700 font-semibold"> · {resultado.filasConError} con error</span>}.
            </p>
            {resultado.errores.length > 0 && (
              <table className="w-full mt-2">
                <thead><tr className="border-b border-borderc">
                  <th className="table-th">Fila</th><th className="table-th">Código</th><th className="table-th">Error</th>
                </tr></thead>
                <tbody>
                  {resultado.errores.map((er: any, idx: number) => (
                    <tr key={idx} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs">{er.fila}</td>
                      <td className="table-td text-xs font-mono">{er.codigo}</td>
                      <td className="table-td text-xs text-red-700">{er.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <h3 className="text-sm font-semibold text-slate-800 mb-3">Historial de importaciones</h3>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Fecha</th><th className="table-th">Archivo</th><th className="table-th">Filas</th>
            <th className="table-th">Importadas</th><th className="table-th">Con error</th>
          </tr></thead>
          <tbody>
            {importaciones.map((i) => (
              <tr key={i.id} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs">{i.creado_en}</td>
                <td className="table-td text-xs">{i.nombre_archivo}</td>
                <td className="table-td text-xs">{i.total_filas}</td>
                <td className="table-td text-xs text-accent-dark font-semibold">{i.filas_importadas}</td>
                <td className="table-td text-xs">{i.filas_con_error > 0 ? <span className="text-red-700 font-semibold">{i.filas_con_error}</span> : "0"}</td>
              </tr>
            ))}
            {importaciones.length === 0 && (
              <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin importaciones todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- estado
function TabEstado({ estado }: { estado: EstadoModulo[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-800 mb-1">Estado y hoja de ruta de SIGEPRO-EP</h3>
      <p className="text-xs text-textmuted mb-4">
        Estado honesto de esta entrega: solo se marca "disponible" lo que está realmente construido y verificado.
        Ver la sección "SIGEPRO-EP" del README para las fuentes normativas citadas.
      </p>
      <div className="space-y-3">
        {estado.map((m, idx) => (
          <div key={idx} className="card p-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-sm font-semibold text-slate-800">{m.modulo}</div>
              <span className={`badge text-[10px] ${ESTADO_BADGE[m.estado] || "bg-slate-100 text-slate-600"}`}>{m.estado}</span>
            </div>
            <p className="text-xs text-textmuted">{m.detalle}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------- Informes Ejecutivos
function TabInformesGob({ puedeGenerar }: { puedeGenerar: boolean }) {
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ultimaDescarga, setUltimaDescarga] = useState<string | null>(null);
  const [corte, setCorte] = useState(new Date().toISOString().slice(0, 10));
  const [vigencia, setVigencia] = useState<number | "">("");

  const generar = async () => {
    setGenerando(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ hasta: corte });
      if (vigencia !== "") qs.set("vigencia", String(vigencia));
      await descargarArchivo(`/sigepro-ep/informes/ejecutivo?${qs.toString()}`, `informe_ejecutivo_sigepro_ep_${corte}.pdf`);
      setUltimaDescarga(new Date().toLocaleString());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo generar el informe ejecutivo.");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <p className="text-xs text-textmuted">
          Informe ejecutivo en PDF con las cifras reales de la entidad: balance general público, ejecución
          presupuestal de la vigencia fiscal elegida, tesorería pública y recaudo territorial (predial + ICA) —
          cada uno con su gráfico — más un resumen ejecutivo redactado por IA a partir de esos mismos datos.
        </p>
        <span className="badge bg-amber-50 text-amber-700 border border-amber-200 text-[10px] shrink-0">Módulo premium</span>
      </div>

      <div className="card p-6 max-w-2xl">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Informe ejecutivo · SIGEPRO-EP</h3>
        <p className="text-sm text-textmuted mb-4">
          Por defecto toma la fecha de hoy como corte y la vigencia fiscal con apropiaciones más reciente, pero
          puede elegir otra fecha de corte (por ejemplo, el cierre de un mes anterior) y otra vigencia fiscal. La
          generación puede tardar unos segundos mientras se calculan las cifras, se dibujan los gráficos y se
          redacta el análisis.
        </p>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de corte</label>
            <input type="date" className="input-field" value={corte} onChange={(e) => setCorte(e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input
              type="number" className="input-field w-32" placeholder="Más reciente"
              value={vigencia} onChange={(e) => setVigencia(e.target.value ? Number(e.target.value) : "")}
            /></div>
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
        {ultimaDescarga && !error && (
          <div className="rounded-md bg-accent-light border border-accent/20 px-3 py-2 text-sm text-accent-dark mb-4">
            Informe descargado ({ultimaDescarga}).
          </div>
        )}

        {puedeGenerar ? (
          <button className="btn-primary" onClick={generar} disabled={generando}>
            {generando ? "Generando informe..." : "Generar informe ejecutivo (PDF)"}
          </button>
        ) : (
          <p className="text-sm text-textmuted italic">Su rol no tiene permiso para generar informes ejecutivos ("sigepro_ep_informes:generar").</p>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------- Contabilidad Pública
function TabContabilidadGob({ comprobantes, balancePrueba, balanceGeneral, cuentasPred, cuentas, puedeCrear, recargar }: {
  comprobantes: ComprobanteGob[]; balancePrueba: BalancePruebaGob | null; balanceGeneral: BalanceGeneralGob | null;
  cuentasPred: CuentaPredeterminadaGob[]; cuentas: CuentaGobierno[]; puedeCrear: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [detalle, setDetalle] = useState<any | null>(null);
  const [subtab, setSubtab] = useState("resumen");

  const abrirDetalle = async (id: number) => {
    setDetalleId(id);
    setDetalle(null);
    try { setDetalle(await api.get(`/sigepro-ep/contabilidad/comprobantes/${id}`)); } catch { /* noop */ }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Contabilidad de partida doble sobre el Catálogo General de Cuentas (CGC), separada del motor privado (PUC)
        de Fase 7 — comparte el mismo diseño (comprobantes, cuentas predeterminadas, balance de prueba y general),
        pero contabiliza contra <code className="bg-slate-50 px-1 rounded">cuentas_gobierno</code> en sus propias
        tablas (<code className="bg-slate-50 px-1 rounded">comprobantes_gobierno</code>,{" "}
        <code className="bg-slate-50 px-1 rounded">comprobante_detalle_gobierno</code>). Ver la pestaña "Estado y
        hoja de ruta" y el README para el detalle de alcance (sin períodos contables ni cierre de ejercicio todavía).
      </p>

      {balanceGeneral && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <div className="text-xs text-textmuted">Total activo</div>
            <div className="text-lg font-bold text-slate-800">{money(balanceGeneral.totalActivo)}</div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-textmuted">Total pasivo</div>
            <div className="text-lg font-bold text-slate-800">{money(balanceGeneral.totalPasivo)}</div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-textmuted">Total patrimonio (incl. resultado del ejercicio)</div>
            <div className="text-lg font-bold text-slate-800">{money(balanceGeneral.totalPatrimonio)}</div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-textmuted">Cuadra (activo = pasivo + patrimonio)</div>
            <div className={`text-lg font-bold ${balanceGeneral.cuadra ? "text-accent-dark" : "text-red-700"}`}>
              {balanceGeneral.cuadra ? "Sí" : "No"}
            </div>
          </div>
        </div>
      )}

      <Tabs
        tabs={[
          { key: "resumen", label: "Comprobantes y balances" },
          { key: "libro-diario", label: "Libro diario" },
          { key: "libro-mayor", label: "Libro mayor" },
          { key: "estado-resultados", label: "Estado de resultados" },
        ]}
        activo={subtab} onChange={setSubtab}
      />
      <div className="h-4" />

      {subtab === "resumen" && (
        <>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Comprobantes ({comprobantes.length})</h3>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo comprobante</button>}
      </div>
      <div className="card overflow-hidden mb-6">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Número</th><th className="table-th">Fecha</th><th className="table-th">Tipo</th>
            <th className="table-th">Origen</th><th className="table-th">Descripción</th><th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {comprobantes.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{c.numero}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{c.fecha}</td>
                <td className="table-td capitalize">{c.tipo}</td>
                <td className="table-td"><span className="badge bg-navy/10 text-navy text-[10px]">{c.origen === "tesoreria_gobierno" ? "Tesorería Pública" : "Manual"}</span></td>
                <td className="table-td">{c.descripcion}</td>
                <td className="table-td"><span className={`badge ${c.estado === "contabilizado" ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>{c.estado}</span></td>
                <td className="table-td text-right"><button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => abrirDetalle(c.id)}>Ver</button></td>
              </tr>
            ))}
            {comprobantes.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin comprobantes registrados.</td></tr>}
          </tbody>
        </table>
      </div>

      {balancePrueba && (
        <>
          <div className="flex justify-between items-end mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-slate-800">
              Balance de prueba (corte {balancePrueba.corte}) — {balancePrueba.cuadra ? "cuadra" : "NO cuadra"}
            </h3>
            <DescargaReporteEP endpoint="/sigepro-ep/contabilidad/balance-prueba/exportar" params={{ hasta: balancePrueba.corte }} nombreBase={`balance_prueba_ep_${balancePrueba.corte}`} label="Descargar balance de prueba" />
          </div>
          <div className="card overflow-hidden mb-6">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Código</th><th className="table-th">Cuenta</th><th className="table-th">Débitos</th>
                <th className="table-th">Créditos</th><th className="table-th">Saldo</th>
              </tr></thead>
              <tbody>
                {balancePrueba.cuentas.map((c) => (
                  <tr key={c.cuentaId} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs">{c.codigo}</td>
                    <td className="table-td">{c.nombre}</td>
                    <td className="table-td">{money(c.totalDebito)}</td>
                    <td className="table-td">{money(c.totalCredito)}</td>
                    <td className="table-td font-semibold">{money(c.saldo)}</td>
                  </tr>
                ))}
                {balancePrueba.cuentas.length === 0 && (
                  <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin movimientos contabilizados todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {balanceGeneral && (
        <div className="flex justify-end mb-6">
          <DescargaReporteEP endpoint="/sigepro-ep/contabilidad/balance-general/exportar" params={{ hasta: balanceGeneral.corte }} nombreBase={`balance_general_ep_${balanceGeneral.corte}`} label="Descargar balance general (estado de situación financiera)" />
        </div>
      )}

      <h3 className="text-sm font-semibold text-slate-800 mb-3">Cuentas predeterminadas</h3>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Concepto</th><th className="table-th">Cuenta</th>
          </tr></thead>
          <tbody>
            {cuentasPred.map((c) => (
              <tr key={c.concepto} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{c.concepto}</td>
                <td className="table-td">{c.codigo} — {c.nombre}</td>
              </tr>
            ))}
            {cuentasPred.length === 0 && (
              <tr><td colSpan={2} className="table-td text-center text-textmuted py-6">Sin cuentas predeterminadas configuradas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      {subtab === "libro-diario" && <TabLibroDiarioGob />}
      {subtab === "libro-mayor" && <TabLibroMayorGob cuentas={cuentas} />}
      {subtab === "estado-resultados" && <TabEstadoResultadosGob />}

      {detalleId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          {/* Mismo bug real corregido que en Contabilidad.tsx (núcleo privado):
              sin max-h + overflow-y-auto, un comprobante con muchas líneas
              (p. ej. una liquidación de nómina pública) crecía más alto que
              la pantalla y dejaba los botones "Cerrar"/"Anular" inalcanzables. */}
          <div className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h3 className="text-base font-semibold text-slate-800">Comprobante #{detalle?.numero}</h3>
              <button className="text-textmuted hover:text-slate-700 text-sm" onClick={() => setDetalleId(null)}>✕</button>
            </div>
            {detalle ? (
              <>
                <p className="text-xs text-textmuted">{detalle.descripcion} · {detalle.fecha}</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-borderc"><th className="table-th">Cuenta</th><th className="table-th">Débito</th><th className="table-th">Crédito</th></tr></thead>
                  <tbody>
                    {detalle.detalle?.map((d: any) => (
                      <tr key={d.id} className="border-b border-borderc last:border-0">
                        <td className="table-td">{d.cuenta_codigo} — {d.cuenta_nombre}{d.descripcion ? <span className="block text-[11px] text-textmuted">{d.descripcion}</span> : null}</td>
                        <td className="table-td">{d.debito ? money(d.debito) : "—"}</td>
                        <td className="table-td">{d.credito ? money(d.credito) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end gap-2 pt-2">
                  <button className="btn-primary" onClick={() => setDetalleId(null)}>Cerrar</button>
                </div>
              </>
            ) : <p className="text-sm text-textmuted">Cargando...</p>}
          </div>
        </div>
      )}

      {modalAbierto && (
        <ModalComprobanteManualGob
          cuentas={cuentas}
          onClose={() => setModalAbierto(false)}
          onCreado={() => { setModalAbierto(false); recargar(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------- libro diario (Contabilidad Pública)
function TabLibroDiarioGob() {
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  useEffect(() => {
    setCargando(true);
    const qs = new URLSearchParams();
    if (desde) qs.set("desde", desde);
    if (hasta) qs.set("hasta", hasta);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    api.get(`/sigepro-ep/contabilidad/libro-diario${suffix}`).then(setMovimientos).finally(() => setCargando(false));
  }, [desde, hasta]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
            <input type="date" className="input-field" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
            <input type="date" className="input-field" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
          {(desde || hasta) && (
            <button type="button" className="btn-ghost text-xs" onClick={() => { setDesde(""); setHasta(""); }}>Limpiar</button>
          )}
        </div>
        <DescargaReporteEP
          endpoint="/sigepro-ep/contabilidad/libro-diario/exportar" params={{ desde: desde || undefined, hasta: hasta || undefined }}
          nombreBase={`libro_diario_ep${desde ? `_${desde}` : ""}${hasta ? `_${hasta}` : ""}`} label="Descargar libro diario"
        />
      </div>
      {cargando ? <p className="text-sm text-textmuted">Cargando...</p> : null}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">No.</th><th className="table-th">Cuenta</th><th className="table-th">Débito</th><th className="table-th">Crédito</th><th className="table-th">Descripción</th></tr></thead>
          <tbody>
            {movimientos.map((m, i) => (
              <tr key={i} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{m.fecha}</td>
                <td className="table-td font-mono text-xs">{m.numero}</td>
                <td className="table-td">{m.cuentaCodigo} — {m.cuentaNombre}</td>
                <td className="table-td">{m.debito ? money(m.debito) : "—"}</td>
                <td className="table-td">{m.credito ? money(m.credito) : "—"}</td>
                <td className="table-td text-xs text-textmuted">{m.descripcion || m.comprobanteDescripcion}</td>
              </tr>
            ))}
            {movimientos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin movimientos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ----------------------------------------------------------- libro mayor (Contabilidad Pública)
function TabLibroMayorGob({ cuentas }: { cuentas: CuentaGobierno[] }) {
  const [cuentaId, setCuentaId] = useState("");
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const cuentasMovimiento = cuentas.filter((c) => c.acepta_movimiento);

  useEffect(() => {
    if (!cuentaId) { setMovimientos([]); return; }
    const qs = new URLSearchParams({ cuentaId });
    if (desde) qs.set("desde", desde);
    if (hasta) qs.set("hasta", hasta);
    api.get(`/sigepro-ep/contabilidad/libro-mayor?${qs.toString()}`).then(setMovimientos).catch(() => setMovimientos([]));
  }, [cuentaId, desde, hasta]);

  const cuentaSeleccionada = cuentasMovimiento.find((c) => String(c.id) === cuentaId);

  return (
    <div>
      <div className="mb-3 flex justify-between items-end flex-wrap gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="max-w-sm w-full">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta</label>
            <select className="input-field" value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              <option value="">Seleccione una cuenta...</option>
              {cuentasMovimiento.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
            <input type="date" className="input-field" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
            <input type="date" className="input-field" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
        </div>
        {cuentaId && (
          <DescargaReporteEP
            endpoint="/sigepro-ep/contabilidad/libro-mayor/exportar" params={{ cuentaId, desde: desde || undefined, hasta: hasta || undefined }}
            nombreBase={`libro_mayor_ep_${cuentaSeleccionada?.codigo || cuentaId}${desde ? `_${desde}` : ""}${hasta ? `_${hasta}` : ""}`} label="Descargar libro mayor"
          />
        )}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">No.</th><th className="table-th">Débito</th><th className="table-th">Crédito</th><th className="table-th">Saldo</th><th className="table-th">Descripción</th></tr></thead>
          <tbody>
            {movimientos.map((m, i) => (
              <tr key={i} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{m.fecha}</td>
                <td className="table-td font-mono text-xs">{m.numero}</td>
                <td className="table-td">{m.debito ? money(m.debito) : "—"}</td>
                <td className="table-td">{m.credito ? money(m.credito) : "—"}</td>
                <td className="table-td font-semibold">{money(m.saldoAcumulado)}</td>
                <td className="table-td text-xs text-textmuted">{m.descripcion || m.comprobanteDescripcion}</td>
              </tr>
            ))}
            {!cuentaId && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Seleccione una cuenta para ver su movimiento.</td></tr>}
            {cuentaId && movimientos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin movimientos en esta cuenta.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------- estado de resultados (Contabilidad Pública)
function TabEstadoResultadosGob() {
  const [datos, setDatos] = useState<any | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get("/sigepro-ep/contabilidad/estado-resultados").then(setDatos).finally(() => setCargando(false));
  }, []);

  if (cargando) return <p className="text-sm text-textmuted">Cargando...</p>;
  if (!datos) return null;

  const _tabla = (grupo: any[]) => (
    <table className="w-full mb-4">
      <thead><tr className="border-b border-borderc"><th className="table-th">Código</th><th className="table-th">Cuenta</th><th className="table-th">Valor</th></tr></thead>
      <tbody>
        {grupo.map((c) => (
          <tr key={c.cuentaId} className="border-b border-borderc last:border-0">
            <td className="table-td font-mono text-xs">{c.codigo}</td><td className="table-td">{c.nombre}</td><td className="table-td">{money(c.saldo)}</td>
          </tr>
        ))}
        {grupo.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-4">Sin movimiento.</td></tr>}
      </tbody>
    </table>
  );

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Estado de resultados (corte {datos.corte})</h3>
        <DescargaReporteEP endpoint="/sigepro-ep/contabilidad/estado-resultados/exportar" params={{ hasta: datos.corte }} nombreBase={`estado_resultados_ep_${datos.corte}`} label="Descargar" />
      </div>
      <div className="card p-4 mb-4">
        <h4 className="text-xs font-semibold text-slate-600 mb-2">Ingresos — total {money(datos.totalIngresos)}</h4>
        {_tabla(datos.ingresos)}
        <h4 className="text-xs font-semibold text-slate-600 mb-2">Costos — total {money(datos.totalCostos)}</h4>
        {_tabla(datos.costos)}
        <p className="text-sm font-semibold text-slate-800 mb-4">Excedente bruto (ingresos − costos): {money(datos.excedenteBruto)}</p>
        <h4 className="text-xs font-semibold text-slate-600 mb-2">Gastos — total {money(datos.totalGastos)}</h4>
        {_tabla(datos.gastos)}
        <p className="text-sm font-bold text-slate-800">Excedente / déficit neto del ejercicio: {money(datos.excedenteNeto)}</p>
      </div>
    </div>
  );
}

function ModalComprobanteManualGob({ cuentas, onClose, onCreado }: {
  cuentas: CuentaGobierno[]; onClose: () => void; onCreado: () => void;
}) {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [descripcion, setDescripcion] = useState("");
  const [lineas, setLineas] = useState([
    { cuentaId: "", debito: "", credito: "", descripcion: "" },
    { cuentaId: "", debito: "", credito: "", descripcion: "" },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cuentasMovimiento = cuentas.filter((c) => c.acepta_movimiento && c.activo);
  const totalDebito = lineas.reduce((s, l) => s + (Number(l.debito) || 0), 0);
  const totalCredito = lineas.reduce((s, l) => s + (Number(l.credito) || 0), 0);
  const cuadra = totalDebito === totalCredito && totalDebito > 0;

  const actualizarLinea = (i: number, campo: string, valor: string) => {
    setLineas(lineas.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
  };
  const agregarLinea = () => setLineas([...lineas, { cuentaId: "", debito: "", credito: "", descripcion: "" }]);
  const quitarLinea = (i: number) => setLineas(lineas.filter((_, idx) => idx !== i));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuadra) { setError("La partida debe cuadrar (débitos = créditos, mayor a cero) antes de contabilizar."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/contabilidad/comprobantes", {
        fecha, descripcion: descripcion || null,
        lineas: lineas
          .filter((l) => l.cuentaId && (Number(l.debito) || Number(l.credito)))
          .map((l) => ({
            cuentaId: Number(l.cuentaId), debito: Number(l.debito) || 0, credito: Number(l.credito) || 0,
            descripcion: l.descripcion || null,
          })),
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el comprobante.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={enviar} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nuevo comprobante manual (Contabilidad Pública)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
            <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">
            Líneas — total débito {money(totalDebito)} / total crédito {money(totalCredito)} {cuadra ? "✓" : ""}
          </label>
          <div className="space-y-2">
            {lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <select className="input-field col-span-5" value={l.cuentaId} onChange={(e) => actualizarLinea(i, "cuentaId", e.target.value)}>
                  <option value="">Cuenta...</option>
                  {cuentasMovimiento.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                </select>
                <input className="input-field col-span-2" type="number" min={0} step="1000" placeholder="Débito" value={l.debito} onChange={(e) => actualizarLinea(i, "debito", e.target.value)} />
                <input className="input-field col-span-2" type="number" min={0} step="1000" placeholder="Crédito" value={l.credito} onChange={(e) => actualizarLinea(i, "credito", e.target.value)} />
                <input className="input-field col-span-2" placeholder="Descripción" value={l.descripcion} onChange={(e) => actualizarLinea(i, "descripcion", e.target.value)} />
                <button type="button" className="col-span-1 text-red-600 text-xs" onClick={() => quitarLinea(i)} disabled={lineas.length <= 2}>✕</button>
              </div>
            ))}
          </div>
          <button type="button" className="btn-ghost text-xs mt-2" onClick={agregarLinea}>+ Agregar línea</button>
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando || !cuadra}>{enviando ? "Registrando..." : "Contabilizar"}</button>
        </div>
      </form>
    </div>
  );
}

// --------------------------------------------------- Tesorería Pública
function TabTesoreriaGob({ cuentasBancarias, movimientos, puedeCrear, recargar }: {
  cuentasBancarias: CuentaBancariaGob[]; movimientos: MovimientoGob[]; puedeCrear: boolean; recargar: () => void;
}) {
  const [tabInterno, setTabInterno] = useState<"cuentas" | "movimientos">("cuentas");
  const [modalCuentaAbierto, setModalCuentaAbierto] = useState(false);
  const [modalMovAbierto, setModalMovAbierto] = useState(false);
  const [desdeMov, setDesdeMov] = useState("");
  const [hastaMov, setHastaMov] = useState("");
  const [cuentaMov, setCuentaMov] = useState("");

  const movimientosFiltrados = movimientos.filter((m) => {
    if (desdeMov && m.fecha < desdeMov) return false;
    if (hastaMov && m.fecha > hastaMov) return false;
    if (cuentaMov && String(m.cuentaBancariaId) !== cuentaMov && String(m.cuentaBancariaDestinoId) !== cuentaMov) return false;
    return true;
  });

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Cuentas bancarias y movimientos de tesorería sobre el CGC, en tablas separadas del núcleo privado (
        <code className="bg-slate-50 px-1 rounded">cuentas_bancarias_gobierno</code>,{" "}
        <code className="bg-slate-50 px-1 rounded">movimientos_tesoreria_gobierno</code>) porque sus movimientos
        generan comprobantes contra <code className="bg-slate-50 px-1 rounded">cuentas_gobierno</code>, no contra el
        PUC. Primera entrega: ingreso, egreso y transferencia entre cuentas propias — sin cuentas por pagar a
        proveedores públicos ni conciliación bancaria todavía (ver "Estado y hoja de ruta").
      </p>

      <Tabs
        tabs={[
          { key: "cuentas", label: "Cuentas bancarias" },
          { key: "movimientos", label: "Movimientos" },
        ]}
        activo={tabInterno} onChange={(k) => setTabInterno(k as any)}
      />

      {tabInterno === "cuentas" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Cuentas bancarias</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalCuentaAbierto(true)}>+ Nueva cuenta</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cuentasBancarias.map((c) => (
              <div key={c.id} className="card p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold text-slate-800">{c.nombre}</div>
                  <span className="badge bg-slate-100 text-slate-500 text-[10px]">{TIPO_CUENTA_LABEL_GOB[c.tipo] || c.tipo}</span>
                </div>
                <div className="text-xs text-textmuted">{c.banco || "—"}{c.numeroCuenta ? ` · ${c.numeroCuenta}` : ""}</div>
                <div className="text-lg font-bold text-slate-800 mt-2">{money(c.saldoActual)}</div>
                <div className="text-[11px] text-textmuted">Saldo inicial: {money(c.saldoInicial)}</div>
                {!c.activa && <span className="badge bg-red-50 text-red-700 text-[10px] mt-2 inline-block">Inactiva</span>}
              </div>
            ))}
            {cuentasBancarias.length === 0 && <p className="text-sm text-textmuted col-span-3">Sin cuentas bancarias registradas.</p>}
          </div>
        </div>
      )}

      {tabInterno === "movimientos" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Movimientos de tesorería</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalMovAbierto(true)}>+ Nuevo movimiento</button>}
          </div>

          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta</label>
                  <select className="input-field" value={cuentaMov} onChange={(e) => setCuentaMov(e.target.value)}>
                    <option value="">Todas</option>
                    {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                  <input type="date" className="input-field" value={desdeMov} onChange={(e) => setDesdeMov(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaMov} onChange={(e) => setHastaMov(e.target.value)} /></div>
                {(desdeMov || hastaMov || cuentaMov) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeMov(""); setHastaMov(""); setCuentaMov(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/tesoreria/movimientos/exportar" formatos={["xlsx", "pdf"]}
                params={{ desde: desdeMov || undefined, hasta: hastaMov || undefined, cuentaId: cuentaMov || undefined }}
                nombreBase={`movimientos_tesoreria_ep${desdeMov ? `_${desdeMov}` : ""}${hastaMov ? `_${hastaMov}` : ""}`}
                label="Descargar extracto"
              />
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Tipo</th><th className="table-th">Cuenta</th><th className="table-th">Destino</th>
                <th className="table-th">Concepto</th><th className="table-th">Valor</th><th className="table-th">Fecha</th>
              </tr></thead>
              <tbody>
                {movimientosFiltrados.map((m) => (
                  <tr key={m.id} className="border-b border-borderc last:border-0">
                    <td className="table-td"><span className={`badge ${TIPO_MOV_BADGE_GOB[m.tipo]}`}>{m.tipo}</span></td>
                    <td className="table-td">{m.cuentaNombre}</td>
                    <td className="table-td text-xs">{m.cuentaDestinoNombre || "—"}</td>
                    <td className="table-td text-xs">{m.concepto}</td>
                    <td className="table-td font-semibold">{money(m.valor)}</td>
                    <td className="table-td text-xs">{m.fecha}</td>
                  </tr>
                ))}
                {movimientosFiltrados.length === 0 && (
                  <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">
                    {movimientos.length === 0 ? "Sin movimientos registrados todavía." : "Ningún movimiento coincide con el filtro."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalCuentaAbierto && (
        <ModalCuentaBancariaGob
          onClose={() => setModalCuentaAbierto(false)}
          onCreado={() => { setModalCuentaAbierto(false); recargar(); }}
        />
      )}
      {modalMovAbierto && (
        <ModalMovimientoGob
          cuentas={cuentasBancarias}
          onClose={() => setModalMovAbierto(false)}
          onCreado={() => { setModalMovAbierto(false); recargar(); }}
        />
      )}
    </div>
  );
}

function ModalCuentaBancariaGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("ahorros");
  const [banco, setBanco] = useState("");
  const [numeroCuenta, setNumeroCuenta] = useState("");
  const [saldoInicial, setSaldoInicial] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/tesoreria/cuentas", {
        nombre, tipo, banco: banco || null, numeroCuenta: numeroCuenta || null, saldoInicial,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear la cuenta bancaria.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva cuenta bancaria (Tesorería Pública)</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="ahorros">Ahorros</option>
              <option value="corriente">Corriente</option>
              <option value="caja_menor">Caja menor</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Saldo inicial</label>
            <input className="input-field" type="number" min={0} step="1000" value={saldoInicial} onChange={(e) => setSaldoInicial(Number(e.target.value))} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Banco (opcional)</label>
          <input className="input-field" value={banco} onChange={(e) => setBanco(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Número de cuenta (opcional)</label>
          <input className="input-field" value={numeroCuenta} onChange={(e) => setNumeroCuenta(e.target.value)} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear cuenta"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalMovimientoGob({ cuentas, onClose, onCreado }: {
  cuentas: CuentaBancariaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [tipo, setTipo] = useState<"ingreso" | "egreso" | "transferencia">("ingreso");
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [cuentaBancariaDestinoId, setCuentaBancariaDestinoId] = useState<number | "">("");
  const [concepto, setConcepto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuentaBancariaId || valor <= 0 || !concepto) {
      setError("Cuenta bancaria, concepto y un valor mayor a cero son obligatorios.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/tesoreria/movimientos", {
        tipo, cuentaBancariaId: Number(cuentaBancariaId),
        cuentaBancariaDestinoId: tipo === "transferencia" && cuentaBancariaDestinoId ? Number(cuentaBancariaDestinoId) : null,
        concepto, descripcion: descripcion || null, valor, fecha,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el movimiento.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo movimiento de tesorería (Público)</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
          <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
            <option value="transferencia">Transferencia entre cuentas propias</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {tipo === "transferencia" ? "Cuenta origen" : "Cuenta bancaria"}
            </label>
            <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          {tipo === "transferencia" && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta destino</label>
              <select className="input-field" value={cuentaBancariaDestinoId} onChange={(e) => setCuentaBancariaDestinoId(e.target.value ? Number(e.target.value) : "")} required>
                <option value="">Seleccionar...</option>
                {cuentas.filter((c) => c.id !== cuentaBancariaId).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
          <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Concepto</label>
          <input className="input-field" value={concepto} onChange={(e) => setConcepto(e.target.value)}
                 placeholder={tipo === "ingreso" ? "Ej: Giro SGP" : tipo === "egreso" ? "Ej: Pago servicios públicos" : "Ej: Fondeo de caja menor"} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (opcional)</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar movimiento"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- terceros (modal compartido)
function ModalTerceroGob({ onClose, onCreado }: { onClose: () => void; onCreado: (id: number) => void }) {
  const [tipoDocumento, setTipoDocumento] = useState("nit");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipoPersona, setTipoPersona] = useState("juridica");
  const [direccion, setDireccion] = useState("");
  const [codigoDpto, setCodigoDpto] = useState("");
  const [codigoMun, setCodigoMun] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post("/sigepro-ep/terceros", {
        tipoDocumento, numeroDocumento, nombre, tipoPersona,
        direccion: direccion || undefined, codigoDpto: codigoDpto || undefined, codigoMun: codigoMun || undefined,
      });
      onCreado(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el tercero.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo tercero</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de documento</label>
            <select className="input-field" value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)}>
              <option value="nit">NIT</option>
              <option value="cc">Cédula de ciudadanía</option>
              <option value="ce">Cédula de extranjería</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de persona</label>
            <select className="input-field" value={tipoPersona} onChange={(e) => setTipoPersona(e.target.value)}>
              <option value="juridica">Jurídica</option>
              <option value="natural">Natural</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Número de documento</label>
          <input className="input-field" value={numeroDocumento} onChange={(e) => setNumeroDocumento(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre / Razón social</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Dirección (opcional)</label>
          <input className="input-field" value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Para Información Exógena (DIAN)" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Código DANE depto. (opcional)</label>
            <input className="input-field" value={codigoDpto} onChange={(e) => setCodigoDpto(e.target.value)} placeholder="Ej: 13" maxLength={2} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Código DANE municipio (opcional)</label>
            <input className="input-field" value={codigoMun} onChange={(e) => setCodigoMun(e.target.value)} placeholder="Ej: 052" maxLength={3} />
          </div>
        </div>
        <p className="text-[11px] text-textmuted -mt-1">
          Estos tres campos son opcionales y solo se usan para completar la Información Exógena (DIAN) del tercero — el resto de SIGEPRO-EP funciona igual sin ellos.
        </p>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear tercero"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Presupuesto Público
const ESTADO_RESERVA_BADGE: Record<string, string> = {
  vigente: "bg-slate-100 text-slate-600", obligada: "bg-amber-50 text-amber-700", pagada: "bg-accent-light text-accent-dark",
};
const ESTADO_CXP_PRESUPUESTAL_BADGE: Record<string, string> = {
  vigente: "bg-slate-100 text-slate-600", pagada: "bg-accent-light text-accent-dark",
};
const CATEGORIA_INGRESO_LABEL: Record<string, string> = {
  saldo_inicial: "Saldo inicial", tributario: "Tributario", no_tributario: "No tributario",
  transferencias: "Transferencias", recursos_balance: "Recursos del balance", recursos_capital: "Recursos de capital",
};
const TIPO_MODIFICACION_LABEL: Record<string, string> = {
  adicion: "Adición", reduccion: "Reducción", credito: "Crédito", contracredito: "Contra-crédito",
};

function TabPresupuestoGob({
  apropiaciones, cdps, rps, obligaciones, ingresos, modificaciones, reservas, cuentasPorPagar,
  fuentesFinanciacion, descuentos, terceros, cuentasBancarias,
  puedeCrear, puedeCausar, puedePagar, puedeModificar, puedeConfigurar, irAContabilidad, recargar,
}: {
  apropiaciones: ApropiacionGob[]; cdps: CdpGob[]; rps: RpGob[]; obligaciones: ObligacionGob[];
  ingresos: IngresoPresupuestalGob[]; modificaciones: ModificacionPresupuestalGob[]; reservas: ReservaPresupuestalGob[];
  cuentasPorPagar: CuentaPorPagarPresupuestalGob[]; fuentesFinanciacion: FuenteFinanciacionGob[]; descuentos: DescuentoPresupuestalGob[];
  terceros: TerceroGob[]; cuentasBancarias: CuentaBancariaGob[];
  puedeCrear: boolean; puedeCausar: boolean; puedePagar: boolean; puedeModificar: boolean; puedeConfigurar: boolean;
  irAContabilidad: () => void; recargar: () => void;
}) {
  const [tabInterno, setTabInterno] = useState<
    "apropiaciones" | "cdps" | "rps" | "obligaciones" | "ejecucion" | "ingresos" | "modificaciones" | "reservas" | "cxp" | "configuracion" | "trazabilidad" | "reportes"
  >("apropiaciones");
  const [modalApAbierto, setModalApAbierto] = useState(false);
  const [modalCdpAbierto, setModalCdpAbierto] = useState(false);
  const [modalRpAbierto, setModalRpAbierto] = useState(false);
  const [modalObAbierto, setModalObAbierto] = useState(false);
  const [pagandoId, setPagandoId] = useState<number | null>(null);
  const [ejecucion, setEjecucion] = useState<EjecucionRubroGob[] | null>(null);
  const [vigenciaEjecucion, setVigenciaEjecucion] = useState(new Date().getFullYear());
  const [vigenciaReportes, setVigenciaReportes] = useState(new Date().getFullYear());
  const [cuentaPago, setCuentaPago] = useState<number | "">("");
  const [errorPago, setErrorPago] = useState<string | null>(null);
  const [desdeMovPpto, setDesdeMovPpto] = useState("");
  const [hastaMovPpto, setHastaMovPpto] = useState("");

  // ---- Ingresos
  const [modalIngresoAbierto, setModalIngresoAbierto] = useState(false);
  const [recaudandoIngreso, setRecaudandoIngreso] = useState<IngresoPresupuestalGob | null>(null);
  const [viendoRecaudosDe, setViendoRecaudosDe] = useState<IngresoPresupuestalGob | null>(null);
  const [recaudosIngreso, setRecaudosIngreso] = useState<RecaudoIngresoGob[] | null>(null);

  const verRecaudos = async (ingreso: IngresoPresupuestalGob) => {
    setViendoRecaudosDe(ingreso);
    setRecaudosIngreso(null);
    try { setRecaudosIngreso(await api.get(`/sigepro-ep/presupuesto/recaudos?ingresoId=${ingreso.id}`)); } catch { setRecaudosIngreso([]); }
  };

  // ---- Modificaciones
  const [modalModAbierto, setModalModAbierto] = useState(false);
  const [modalTraspasoAbierto, setModalTraspasoAbierto] = useState(false);
  const [ladoModFiltro, setLadoModFiltro] = useState<"gasto" | "ingreso">("gasto");

  // ---- Reservas y Cuentas por Pagar
  const [modalReservaAbierto, setModalReservaAbierto] = useState(false);
  const [modalCxpAbierto, setModalCxpAbierto] = useState(false);
  const [accionandoReserva, setAccionandoReserva] = useState<{ id: number; accion: "obligar" | "pagar" } | null>(null);
  const [cuentaPagoReserva, setCuentaPagoReserva] = useState<number | "">("");
  const [pagandoCxp, setPagandoCxp] = useState<number | null>(null);
  const [cuentaPagoCxp, setCuentaPagoCxp] = useState<number | "">("");
  const [errorReservaCxp, setErrorReservaCxp] = useState<string | null>(null);

  const obligarReserva = async (id: number) => {
    setErrorReservaCxp(null);
    try {
      await api.post(`/sigepro-ep/presupuesto/reservas/${id}/obligar`, {});
      setAccionandoReserva(null);
      recargar();
    } catch (err) {
      setErrorReservaCxp(err instanceof ApiError ? err.message : "No se pudo causar la obligación de la reserva.");
    }
  };
  const pagarReserva = async (id: number) => {
    if (!cuentaPagoReserva) { setErrorReservaCxp("Seleccione la cuenta bancaria de origen del pago."); return; }
    setErrorReservaCxp(null);
    try {
      await api.post(`/sigepro-ep/presupuesto/reservas/${id}/pagar`, { cuentaBancariaId: Number(cuentaPagoReserva) });
      setAccionandoReserva(null);
      recargar();
    } catch (err) {
      setErrorReservaCxp(err instanceof ApiError ? err.message : "No se pudo pagar la reserva.");
    }
  };
  const pagarCxp = async (id: number) => {
    if (!cuentaPagoCxp) { setErrorReservaCxp("Seleccione la cuenta bancaria de origen del pago."); return; }
    setErrorReservaCxp(null);
    try {
      await api.post(`/sigepro-ep/presupuesto/cuentas-por-pagar/${id}/pagar`, { cuentaBancariaId: Number(cuentaPagoCxp) });
      setPagandoCxp(null);
      recargar();
    } catch (err) {
      setErrorReservaCxp(err instanceof ApiError ? err.message : "No se pudo pagar la cuenta por pagar.");
    }
  };

  // ---- Configuración
  const [modalFuenteAbierto, setModalFuenteAbierto] = useState(false);
  const [modalDescuentoAbierto, setModalDescuentoAbierto] = useState(false);

  // ---- Trazabilidad
  const [rpTrazaId, setRpTrazaId] = useState<number | "">("");
  const [traza, setTraza] = useState<any | null>(null);
  const [errorTraza, setErrorTraza] = useState<string | null>(null);
  const consultarTraza = async () => {
    if (!rpTrazaId) { setErrorTraza("Seleccione el RP a trazar."); return; }
    setErrorTraza(null);
    setTraza(null);
    try {
      setTraza(await api.get(`/sigepro-ep/presupuesto/trazabilidad/${rpTrazaId}`));
    } catch (err) {
      setErrorTraza(err instanceof ApiError ? err.message : "No se pudo consultar la trazabilidad.");
    }
  };

  const consultarEjecucion = async () => {
    try {
      setEjecucion(await api.get(`/sigepro-ep/presupuesto/ejecucion?vigenciaFiscal=${vigenciaEjecucion}`));
    } catch (err) {
      setEjecucion([]);
    }
  };

  const pagar = async (obligacionId: number) => {
    if (!cuentaPago) { setErrorPago("Seleccione la cuenta bancaria de origen del pago."); return; }
    setErrorPago(null);
    try {
      await api.post(`/sigepro-ep/presupuesto/obligaciones/${obligacionId}/pagar`, { cuentaBancariaId: Number(cuentaPago) });
      setPagandoId(null);
      recargar();
    } catch (err) {
      setErrorPago(err instanceof ApiError ? err.message : "No se pudo pagar la obligación.");
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Ciclo de ejecución presupuestal del Estatuto Orgánico de Presupuesto (Decreto 111 de 1996):{" "}
        <strong>Apropiación → CDP → RP → Obligación → Pago</strong>. Cada nivel valida el saldo disponible del nivel
        anterior antes de afectarlo — un CDP no puede superar el saldo de su apropiación, un RP no puede superar el
        saldo de su CDP, y así sucesivamente. La obligación genera el comprobante contable real (débito de gasto,
        crédito de cuentas por pagar); el pago se registra desde Tesorería Pública contra esa cuenta por pagar, sin
        duplicar el gasto.
      </p>

      <Tabs
        tabs={[
          { key: "apropiaciones", label: "Apropiaciones" },
          { key: "cdps", label: "CDP" },
          { key: "rps", label: "RP" },
          { key: "obligaciones", label: "Obligaciones y pago" },
          { key: "ejecucion", label: "Ejecución presupuestal" },
          { key: "ingresos", label: "Ingresos" },
          { key: "modificaciones", label: "Modificaciones" },
          { key: "reservas", label: "Reservas Presupuestales" },
          { key: "cxp", label: "Cuentas por Pagar" },
          { key: "configuracion", label: "Configuración" },
          { key: "trazabilidad", label: "Trazabilidad" },
          { key: "reportes", label: "Reportes regulatorios (CGR/CUIPO/FUT)" },
        ]}
        activo={tabInterno} onChange={(k) => setTabInterno(k as any)}
      />

      {tabInterno === "apropiaciones" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Apropiaciones por rubro y vigencia fiscal</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalApAbierto(true)}>+ Nueva apropiación</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Vigencia</th><th className="table-th">Rubro</th><th className="table-th">Apropiado</th>
                <th className="table-th">Vigente</th><th className="table-th">Saldo disponible</th>
              </tr></thead>
              <tbody>
                {apropiaciones.map((a) => (
                  <tr key={a.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{a.vigencia_fiscal}</td>
                    <td className="table-td text-xs">{a.rubro_codigo} — {a.rubro_nombre}</td>
                    <td className="table-td font-semibold">{money(a.valor_apropiado)}</td>
                    <td className="table-td">{money(a.valorVigente)}</td>
                    <td className="table-td">{money(a.saldoDisponible)}</td>
                  </tr>
                ))}
                {apropiaciones.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin apropiaciones registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "cdps" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Certificados de Disponibilidad Presupuestal (CDP)</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalCdpAbierto(true)}>+ Nuevo CDP</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">No.</th><th className="table-th">Rubro</th><th className="table-th">Objeto</th>
                <th className="table-th">Valor</th><th className="table-th">Saldo disponible</th><th className="table-th">Estado</th>
              </tr></thead>
              <tbody>
                {cdps.map((c) => (
                  <tr key={c.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{c.numero}</td>
                    <td className="table-td text-xs">{c.rubro_codigo} — {c.rubro_nombre}</td>
                    <td className="table-td text-xs">{c.descripcion}</td>
                    <td className="table-td font-semibold">{money(c.valor)}</td>
                    <td className="table-td">{money(c.saldoDisponible)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{c.estado}</span></td>
                  </tr>
                ))}
                {cdps.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin CDP registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "rps" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Registros Presupuestales del Compromiso (RP)</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalRpAbierto(true)}>+ Nuevo RP</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">No.</th><th className="table-th">Beneficiario</th><th className="table-th">Objeto</th>
                <th className="table-th">Valor</th><th className="table-th">Saldo disponible</th><th className="table-th">Estado</th>
              </tr></thead>
              <tbody>
                {rps.map((r) => (
                  <tr key={r.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{r.numero}</td>
                    <td className="table-td text-xs">{r.beneficiario_nombre || "—"}</td>
                    <td className="table-td text-xs">{r.descripcion}</td>
                    <td className="table-td font-semibold">{money(r.valor)}</td>
                    <td className="table-td">{money(r.saldoDisponible)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{r.estado}</span></td>
                  </tr>
                ))}
                {rps.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin RP registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "obligaciones" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Obligaciones presupuestales</h3>
            {puedeCausar && <button className="btn-primary" onClick={() => setModalObAbierto(true)}>+ Causar obligación</button>}
          </div>
          {errorPago && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorPago}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">No.</th><th className="table-th">RP</th><th className="table-th">Beneficiario</th>
                <th className="table-th">Valor</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {obligaciones.map((o) => (
                  <tr key={o.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{o.numero}</td>
                    <td className="table-td text-xs">RP-{o.rp_numero}</td>
                    <td className="table-td text-xs">{o.beneficiario_nombre || "—"}</td>
                    <td className="table-td font-semibold">{money(o.valor)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{o.estado}</span></td>
                    <td className="table-td">
                      {o.estado === "causada" && puedePagar && (
                        pagandoId === o.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPago} onChange={(e) => setCuentaPago(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => pagar(o.id)}>Pagar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagandoId(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagandoId(o.id); setErrorPago(null); }}>Pagar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {obligaciones.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin obligaciones registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "ejecucion" && (
        <div>
          <div className="flex items-end gap-2 mb-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
              <input className="input-field" type="number" value={vigenciaEjecucion} onChange={(e) => setVigenciaEjecucion(Number(e.target.value))} />
            </div>
            <button className="btn-primary" onClick={consultarEjecucion}>Consultar</button>
          </div>
          {ejecucion && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc">
                  <th className="table-th">Rubro</th><th className="table-th">Apropiado</th><th className="table-th">Comprometido</th>
                  <th className="table-th">Obligado</th><th className="table-th">Pagado</th><th className="table-th">Saldo por comprometer</th>
                </tr></thead>
                <tbody>
                  {ejecucion.map((e) => (
                    <tr key={e.apropiacionId} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs">{e.rubroCodigo} — {e.rubroNombre}</td>
                      <td className="table-td">{money(e.valorApropiado)}</td>
                      <td className="table-td">{money(e.comprometido)}</td>
                      <td className="table-td">{money(e.obligado)}</td>
                      <td className="table-td">{money(e.pagado)}</td>
                      <td className="table-td font-semibold">{money(e.saldoPorComprometer)}</td>
                    </tr>
                  ))}
                  {ejecucion.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin apropiaciones en esa vigencia.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tabInterno === "reportes" && (
        <div>
          <div className="card p-4 mb-4 border-l-4 border-l-accent">
            <h4 className="text-sm font-semibold text-slate-800 mb-1">Movimientos presupuestales (rango de fechas)</h4>
            <p className="text-xs text-textmuted mb-3">
              Complemento a los formatos regulatorios de abajo (que son por vigencia fiscal completa, como exige la
              norma): CDP, RP, Obligaciones y Recaudos de ingresos de un período específico dentro del año, ya que
              aquí se maneja información diaria y no siempre hace falta esperar al corte de la vigencia.
            </p>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                  <input type="date" className="input-field" value={desdeMovPpto} onChange={(e) => setDesdeMovPpto(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaMovPpto} onChange={(e) => setHastaMovPpto(e.target.value)} /></div>
                {(desdeMovPpto || hastaMovPpto) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeMovPpto(""); setHastaMovPpto(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/presupuesto/movimientos/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdeMovPpto || undefined, hasta: hastaMovPpto || undefined }}
                nombreBase={`movimientos_presupuestales_ep${desdeMovPpto ? `_${desdeMovPpto}` : ""}${hastaMovPpto ? `_${hastaMovPpto}` : ""}`}
                label="Descargar movimientos"
              />
            </div>
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
            <p className="text-xs text-amber-800">
              Estos formatos se generan con la estructura oficial (Contraloría General de la República y DNP-CHIP,
              instructivo de la Categoría Única CUIPO) a partir de los datos reales de Presupuesto Público de esta
              entidad. No fue posible verificarlos contra una muestra descargada de TransforWeb: el módulo "Informes"
              de ese entorno de referencia no entregó archivos con datos al generarlos (solo una línea de
              confirmación de trabajo), por lo que quedan pendientes de validar contra una muestra real cuando esté
              disponible — ver README.
            </p>
          </div>
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input
              className="input-field w-32" type="number" value={vigenciaReportes}
              onChange={(e) => setVigenciaReportes(Number(e.target.value))}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CGR — Ejecución de ingresos ALCADIA</h4>
              <p className="text-xs text-textmuted mb-3">Por rubro: presupuesto inicial, definitivo, recaudo acumulado y % de ejecución.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cgr/ejecucion-ingresos" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cgr_ejecucion_ingresos_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CGR — Ejecución de gastos ALCADIA</h4>
              <p className="text-xs text-textmuted mb-3">Por rubro: apropiación inicial, definitiva, compromisos, obligaciones, pagos y saldo.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cgr/ejecucion-gastos" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cgr_ejecucion_gastos_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CUIPO — Ingresos (Programación, Formulario A)</h4>
              <p className="text-xs text-textmuted mb-3">CODIGO, NOMBRE, PRESUPUESTO INICIAL, PRESUPUESTO DEFINITIVO.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cuipo/ingresos-programacion" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cuipo_ingresos_programacion_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CUIPO — Ingresos (Ejecución, Formulario B)</h4>
              <p className="text-xs text-textmuted mb-3">CODIGO, NOMBRE, RECAUDO VIGENCIA ACTUAL/ANTERIOR, TOTAL RECAUDO.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cuipo/ingresos-ejecucion" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cuipo_ingresos_ejecucion_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CUIPO — Gastos (Programación, Formulario C)</h4>
              <p className="text-xs text-textmuted mb-3">CODIGO, NOMBRE, APROPIACION INICIAL, APROPIACION DEFINITIVA.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cuipo/gastos-programacion" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cuipo_gastos_programacion_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CUIPO — Gastos (Ejecución, Formulario D)</h4>
              <p className="text-xs text-textmuted mb-3">CODIGO, NOMBRE, COMPROMISOS, OBLIGACIONES, PAGOS.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cuipo/gastos-ejecucion" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cuipo_gastos_ejecucion_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CGR — Ejecución de gastos por fuente de financiación</h4>
              <p className="text-xs text-textmuted mb-3">Desglose de la ejecución de gastos agrupado por fuente configurada (SGP, regalías, etc.).</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cgr/ejecucion-por-fuente" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cgr_ejecucion_por_fuente_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CGR — Reservas Presupuestales</h4>
              <p className="text-xs text-textmuted mb-3">Compromisos reservados al cierre de vigencia (Decreto 111/1996 Art. 89), por rubro.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cgr/reservas-presupuestales" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cgr_reservas_presupuestales_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">CGR — Cuentas por Pagar presupuestales</h4>
              <p className="text-xs text-textmuted mb-3">Obligaciones causadas y exigibles sin pagar al cierre de vigencia, por rubro.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/cgr/cuentas-por-pagar" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`cgr_cuentas_por_pagar_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">FUT — Registros Presupuestales</h4>
              <p className="text-xs text-textmuted mb-3">Listado de RP (compromisos perfeccionados) de la vigencia, con beneficiario y rubro.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/fut/registros-presupuestales" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigenciaReportes }} nombreBase={`fut_registros_presupuestales_ep_${vigenciaReportes}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">FUT — Fuentes de Financiación</h4>
              <p className="text-xs text-textmuted mb-3">Catálogo de fuentes de financiación de la entidad (no depende de la vigencia fiscal).</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/reportes-regulatorios/fut/fuentes-financiacion" formatos={["xlsx", "pdf"]}
                nombreBase="fut_fuentes_financiacion_ep"
              />
            </div>
          </div>
        </div>
      )}

      {tabInterno === "ingresos" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Rubros de ingreso presupuestal</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalIngresoAbierto(true)}>+ Nuevo rubro de ingreso</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Vigencia</th><th className="table-th">Rubro</th><th className="table-th">Categoría</th>
                <th className="table-th">Presupuestado</th><th className="table-th">Vigente</th><th className="table-th">Recaudado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {ingresos.map((i) => (
                  <tr key={i.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{i.vigencia_fiscal}</td>
                    <td className="table-td text-xs">{i.rubro_codigo} — {i.rubro_nombre}</td>
                    <td className="table-td text-xs">{CATEGORIA_INGRESO_LABEL[i.categoria] || i.categoria}</td>
                    <td className="table-td font-semibold">{money(i.valor_presupuestado)}</td>
                    <td className="table-td">{money(i.valorVigente)}</td>
                    <td className="table-td">{money(i.recaudado)}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => verRecaudos(i)}>Recaudos</button>
                        {puedePagar && <button className="btn-ghost text-xs px-2 py-1" onClick={() => setRecaudandoIngreso(i)}>Recaudar</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {ingresos.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin rubros de ingreso registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "modificaciones" && (
        <div>
          <p className="text-xs text-textmuted mb-3">
            Adición y Reducción afectan directamente un rubro; Crédito/Contra-crédito se generan siempre en par (un
            traspaso entre dos rubros del mismo lado, con neto presupuestal cero).
          </p>
          <div className="flex justify-between items-end mb-3">
            <Tabs tabs={[{ key: "gasto", label: "Gasto (apropiaciones)" }, { key: "ingreso", label: "Ingreso" }]} activo={ladoModFiltro} onChange={(k) => setLadoModFiltro(k as any)} />
            {puedeModificar && (
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={() => setModalTraspasoAbierto(true)}>+ Traspaso (Crédito/Contra-crédito)</button>
                <button className="btn-primary" onClick={() => setModalModAbierto(true)}>+ Adición/Reducción</button>
              </div>
            )}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Fecha</th><th className="table-th">Tipo</th><th className="table-th">Rubro</th>
                <th className="table-th">Valor</th><th className="table-th">Descripción</th>
              </tr></thead>
              <tbody>
                {modificaciones.filter((m) => m.lado === ladoModFiltro).map((m) => {
                  const rubro = m.lado === "gasto"
                    ? apropiaciones.find((a) => a.id === m.apropiacion_id)
                    : ingresos.find((i) => i.id === m.ingreso_id);
                  return (
                    <tr key={m.id} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs whitespace-nowrap">{m.fecha}</td>
                      <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{TIPO_MODIFICACION_LABEL[m.tipo] || m.tipo}</span></td>
                      <td className="table-td text-xs">{rubro ? `${rubro.rubro_codigo} — ${rubro.rubro_nombre}` : "—"}</td>
                      <td className="table-td font-semibold">{money(m.valor)}</td>
                      <td className="table-td text-xs">{m.descripcion}</td>
                    </tr>
                  );
                })}
                {modificaciones.filter((m) => m.lado === ladoModFiltro).length === 0 && (
                  <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin modificaciones registradas para este lado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "reservas" && (
        <div>
          <p className="text-xs text-textmuted mb-3">
            Reservas Presupuestales (Decreto 111 de 1996, Art. 89): compromisos que al cierre de la vigencia ya
            tienen RP pero cuyo bien/servicio aún no se ha recibido. Ciclo: Constitución (saldo inicial) →
            Obligación (cuando se recibe) → Pago.
          </p>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Reservas presupuestales</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalReservaAbierto(true)}>+ Nueva reserva (saldo inicial)</button>}
          </div>
          {errorReservaCxp && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorReservaCxp}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Vigencia</th><th className="table-th">Descripción</th><th className="table-th">Valor</th>
                <th className="table-th">Fecha constitución</th><th className="table-th">RP</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {reservas.map((r) => (
                  <tr key={r.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{r.vigencia_fiscal}</td>
                    <td className="table-td text-xs">{r.descripcion}</td>
                    <td className="table-td font-semibold">{money(r.valor)}</td>
                    <td className="table-td text-xs">{r.fecha_constitucion}</td>
                    <td className="table-td text-xs">{r.rp_id ? `RP-${r.rp_id}` : "—"}</td>
                    <td className="table-td"><span className={`badge ${ESTADO_RESERVA_BADGE[r.estado]}`}>{r.estado}</span></td>
                    <td className="table-td">
                      {r.estado === "vigente" && puedeCausar && (
                        accionandoReserva?.id === r.id && accionandoReserva.accion === "obligar" ? (
                          <div className="flex items-center gap-1">
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => obligarReserva(r.id)}>Confirmar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setAccionandoReserva(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setAccionandoReserva({ id: r.id, accion: "obligar" }); setErrorReservaCxp(null); }}>Causar obligación</button>
                        )
                      )}
                      {r.estado === "obligada" && puedePagar && (
                        accionandoReserva?.id === r.id && accionandoReserva.accion === "pagar" ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPagoReserva} onChange={(e) => setCuentaPagoReserva(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => pagarReserva(r.id)}>Pagar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setAccionandoReserva(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setAccionandoReserva({ id: r.id, accion: "pagar" }); setErrorReservaCxp(null); }}>Pagar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {reservas.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin reservas presupuestales registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "cxp" && (
        <div>
          <p className="text-xs text-textmuted mb-3">
            Cuentas por Pagar presupuestales: obligaciones ya causadas y exigibles que quedaron sin pagar al cierre
            de la vigencia — el comprobante de causación ya existe; esto solo constituye el saldo a pagar en la
            vigencia siguiente.
          </p>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Cuentas por pagar presupuestales</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalCxpAbierto(true)}>+ Nueva cuenta por pagar (saldo inicial)</button>}
          </div>
          {errorReservaCxp && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorReservaCxp}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Vigencia</th><th className="table-th">Descripción</th><th className="table-th">Valor</th>
                <th className="table-th">Fecha constitución</th><th className="table-th">Obligación</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {cuentasPorPagar.map((c) => (
                  <tr key={c.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{c.vigencia_fiscal}</td>
                    <td className="table-td text-xs">{c.descripcion}</td>
                    <td className="table-td font-semibold">{money(c.valor)}</td>
                    <td className="table-td text-xs">{c.fecha_constitucion}</td>
                    <td className="table-td text-xs">{c.obligacion_id ? `#${c.obligacion_id}` : "—"}</td>
                    <td className="table-td"><span className={`badge ${ESTADO_CXP_PRESUPUESTAL_BADGE[c.estado]}`}>{c.estado}</span></td>
                    <td className="table-td">
                      {c.estado === "vigente" && puedePagar && (
                        pagandoCxp === c.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPagoCxp} onChange={(e) => setCuentaPagoCxp(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((cb) => <option key={cb.id} value={cb.id}>{cb.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => pagarCxp(c.id)}>Pagar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagandoCxp(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagandoCxp(c.id); setErrorReservaCxp(null); }}>Pagar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {cuentasPorPagar.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin cuentas por pagar presupuestales registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "configuracion" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Fuentes de financiación</h3>
            {puedeConfigurar && <button className="btn-primary" onClick={() => setModalFuenteAbierto(true)}>+ Nueva fuente</button>}
          </div>
          <div className="card overflow-hidden mb-6">
            <table className="w-full">
              <thead><tr className="border-b border-borderc"><th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Activo</th></tr></thead>
              <tbody>
                {fuentesFinanciacion.map((f) => (
                  <tr key={f.id} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs">{f.codigo}</td>
                    <td className="table-td">{f.nombre}</td>
                    <td className="table-td">{f.activo ? "Sí" : "No"}</td>
                  </tr>
                ))}
                {fuentesFinanciacion.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-6">Sin fuentes de financiación configuradas.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Descuentos</h3>
            {puedeConfigurar && <button className="btn-primary" onClick={() => setModalDescuentoAbierto(true)}>+ Nuevo descuento</button>}
          </div>
          <div className="card overflow-hidden mb-6">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Tipo</th><th className="table-th">Valor</th><th className="table-th">Activo</th>
              </tr></thead>
              <tbody>
                {descuentos.map((d) => (
                  <tr key={d.id} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs">{d.codigo}</td>
                    <td className="table-td">{d.nombre}</td>
                    <td className="table-td text-xs">{d.tipo === "porcentaje" ? "Porcentaje" : "Valor fijo"}</td>
                    <td className="table-td">{d.tipo === "porcentaje" ? `${d.valor}%` : money(d.valor)}</td>
                    <td className="table-td">{d.activo ? "Sí" : "No"}</td>
                  </tr>
                ))}
                {descuentos.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin descuentos configurados.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="rounded-md bg-navy/5 border border-navy/10 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Configurar asiento / Enlazar cuenta</h3>
            <p className="text-xs text-textmuted">
              El mapeo de cada concepto contable a una cuenta del CGC (equivalente a "Configurar asiento"/"Enlazar
              cuenta" del sistema de referencia) ya existe como una única pantalla compartida por todos los módulos
              de SIGEPRO-EP — no se duplica aquí.{" "}
              <button type="button" className="text-accent-dark hover:underline font-semibold" onClick={irAContabilidad}>
                Ir a Contabilidad Pública → Cuentas predeterminadas
              </button>
            </p>
          </div>
        </div>
      )}

      {tabInterno === "trazabilidad" && (
        <div>
          <p className="text-xs text-textmuted mb-3">
            Expediente completo de un compromiso: Apropiación de origen → CDP → RP → Obligación(es)/Pago(s), más
            las modificaciones presupuestales que afectaron la apropiación de origen.
          </p>
          <div className="flex items-end gap-2 mb-3">
            <div className="flex-1 max-w-sm">
              <label className="block text-xs font-semibold text-slate-600 mb-1">RP</label>
              <select className="input-field" value={rpTrazaId} onChange={(e) => setRpTrazaId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Seleccionar...</option>
                {rps.map((r) => <option key={r.id} value={r.id}>RP-{r.numero} — {r.descripcion}</option>)}
              </select>
            </div>
            <button className="btn-primary" onClick={consultarTraza}>Consultar</button>
          </div>
          {errorTraza && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorTraza}</div>}
          {traza && (
            <div className="space-y-4">
              <div className="card p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Apropiación de origen</h4>
                <p className="text-xs text-textmuted">
                  {traza.apropiacion.rubro_codigo} — {traza.apropiacion.rubro_nombre} (vigencia {traza.apropiacion.vigencia_fiscal}) —{" "}
                  apropiado {money(traza.apropiacion.valor_apropiado)}
                </p>
              </div>
              <div className="card p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-2">CDP-{traza.cdp.numero}</h4>
                <p className="text-xs text-textmuted">{traza.cdp.descripcion} — {money(traza.cdp.valor)} — {traza.cdp.fecha} — <span className="badge bg-slate-100 text-slate-600">{traza.cdp.estado}</span></p>
              </div>
              <div className="card p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-2">RP-{traza.rp.numero}</h4>
                <p className="text-xs text-textmuted">{traza.rp.descripcion} — {money(traza.rp.valor)} — {traza.rp.beneficiario_nombre || "—"} — <span className="badge bg-slate-100 text-slate-600">{traza.rp.estado}</span></p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Obligaciones</h4>
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-borderc"><th className="table-th">No.</th><th className="table-th">Fecha</th><th className="table-th">Valor</th><th className="table-th">Estado</th><th className="table-th">Comprobante</th></tr></thead>
                    <tbody>
                      {(traza.obligaciones || []).map((o: any) => (
                        <tr key={o.id} className="border-b border-borderc last:border-0">
                          <td className="table-td">{o.numero}</td>
                          <td className="table-td text-xs">{o.fecha}</td>
                          <td className="table-td font-semibold">{money(o.valor)}</td>
                          <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{o.estado}</span></td>
                          <td className="table-td text-xs">{o.comprobante_id ? `#${o.comprobante_id}` : "—"}</td>
                        </tr>
                      ))}
                      {(!traza.obligaciones || traza.obligaciones.length === 0) && <tr><td colSpan={5} className="table-td text-center text-textmuted py-4">Sin obligaciones causadas todavía.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Modificaciones sobre la apropiación de origen</h4>
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">Tipo</th><th className="table-th">Valor</th><th className="table-th">Descripción</th></tr></thead>
                    <tbody>
                      {(traza.modificaciones || []).map((m: any) => (
                        <tr key={m.id} className="border-b border-borderc last:border-0">
                          <td className="table-td text-xs">{m.fecha}</td>
                          <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{TIPO_MODIFICACION_LABEL[m.tipo] || m.tipo}</span></td>
                          <td className="table-td font-semibold">{money(m.valor)}</td>
                          <td className="table-td text-xs">{m.descripcion}</td>
                        </tr>
                      ))}
                      {(!traza.modificaciones || traza.modificaciones.length === 0) && <tr><td colSpan={4} className="table-td text-center text-textmuted py-4">Sin modificaciones sobre esta apropiación.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {modalApAbierto && <ModalApropiacionGob fuentesFinanciacion={fuentesFinanciacion} onClose={() => setModalApAbierto(false)} onCreado={() => { setModalApAbierto(false); recargar(); }} />}
      {modalCdpAbierto && <ModalCdpGob apropiaciones={apropiaciones} onClose={() => setModalCdpAbierto(false)} onCreado={() => { setModalCdpAbierto(false); recargar(); }} />}
      {modalRpAbierto && <ModalRpGob cdps={cdps} terceros={terceros} recargarTerceros={recargar} onClose={() => setModalRpAbierto(false)} onCreado={() => { setModalRpAbierto(false); recargar(); }} />}
      {modalObAbierto && <ModalObligacionGob rps={rps} onClose={() => setModalObAbierto(false)} onCreado={() => { setModalObAbierto(false); recargar(); }} />}
      {modalIngresoAbierto && <ModalIngresoGob onClose={() => setModalIngresoAbierto(false)} onCreado={() => { setModalIngresoAbierto(false); recargar(); }} />}
      {recaudandoIngreso && (
        <ModalRecaudarIngresoGob
          ingreso={recaudandoIngreso} cuentasBancarias={cuentasBancarias}
          onClose={() => setRecaudandoIngreso(null)}
          onCreado={() => { setRecaudandoIngreso(null); recargar(); }}
        />
      )}
      {viendoRecaudosDe && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          {/* Mismo bug real corregido que en el modal de comprobante de
              arriba: un rubro con muchos recaudos acumulados en el tiempo
              podía crecer más alto que la pantalla sin scroll. */}
          <div className="card w-full max-w-xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h3 className="text-base font-semibold text-slate-800">Recaudos — {viendoRecaudosDe.rubro_codigo} {viendoRecaudosDe.rubro_nombre}</h3>
              <button className="text-textmuted hover:text-slate-700 text-sm" onClick={() => { setViendoRecaudosDe(null); setRecaudosIngreso(null); }}>✕</button>
            </div>
            {recaudosIngreso ? (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">Valor</th><th className="table-th">Descripción</th></tr></thead>
                <tbody>
                  {recaudosIngreso.map((r) => (
                    <tr key={r.id} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs">{r.fecha}</td>
                      <td className="table-td font-semibold">{money(r.valor)}</td>
                      <td className="table-td text-xs">{r.descripcion || "—"}</td>
                    </tr>
                  ))}
                  {recaudosIngreso.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-6">Sin recaudos registrados para este rubro.</td></tr>}
                </tbody>
              </table>
            ) : <p className="text-sm text-textmuted">Cargando...</p>}
            <div className="flex justify-end pt-2">
              <button className="btn-primary" onClick={() => { setViendoRecaudosDe(null); setRecaudosIngreso(null); }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      {modalModAbierto && (
        <ModalModificacionGob
          apropiaciones={apropiaciones} ingresos={ingresos}
          onClose={() => setModalModAbierto(false)} onCreado={() => { setModalModAbierto(false); recargar(); }}
        />
      )}
      {modalTraspasoAbierto && (
        <ModalTraspasoGob
          apropiaciones={apropiaciones} ingresos={ingresos}
          onClose={() => setModalTraspasoAbierto(false)} onCreado={() => { setModalTraspasoAbierto(false); recargar(); }}
        />
      )}
      {modalReservaAbierto && (
        <ModalReservaGob rps={rps} onClose={() => setModalReservaAbierto(false)} onCreado={() => { setModalReservaAbierto(false); recargar(); }} />
      )}
      {modalCxpAbierto && (
        <ModalCxpGob obligaciones={obligaciones} onClose={() => setModalCxpAbierto(false)} onCreado={() => { setModalCxpAbierto(false); recargar(); }} />
      )}
      {modalFuenteAbierto && (
        <ModalFuenteFinanciacionGob onClose={() => setModalFuenteAbierto(false)} onCreado={() => { setModalFuenteAbierto(false); recargar(); }} />
      )}
      {modalDescuentoAbierto && (
        <ModalDescuentoPresupuestalGob onClose={() => setModalDescuentoAbierto(false)} onCreado={() => { setModalDescuentoAbierto(false); recargar(); }} />
      )}
    </div>
  );
}

function ModalApropiacionGob({ fuentesFinanciacion, onClose, onCreado }: {
  fuentesFinanciacion: FuenteFinanciacionGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [rubroCodigo, setRubroCodigo] = useState("");
  const [rubroNombre, setRubroNombre] = useState("");
  const [valorApropiado, setValorApropiado] = useState(0);
  const [fuenteFinanciacionId, setFuenteFinanciacionId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/apropiaciones", {
        vigenciaFiscal, rubroCodigo, rubroNombre, valorApropiado,
        fuenteFinanciacionId: fuenteFinanciacionId || null,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear la apropiación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva apropiación presupuestal</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
          <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código del rubro</label>
          <input className="input-field" value={rubroCodigo} onChange={(e) => setRubroCodigo(e.target.value)} placeholder="Ej: 2.3.2.02.02" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre del rubro</label>
          <input className="input-field" value={rubroNombre} onChange={(e) => setRubroNombre(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor apropiado</label>
          <input className="input-field" type="number" min={0} step="1000" value={valorApropiado} onChange={(e) => setValorApropiado(Number(e.target.value))} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Fuente de financiación (opcional)</label>
          <select className="input-field" value={fuenteFinanciacionId} onChange={(e) => setFuenteFinanciacionId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Sin especificar</option>
            {fuentesFinanciacion.map((f) => <option key={f.id} value={f.id}>{f.codigo} — {f.nombre}</option>)}
          </select>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear apropiación"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalCdpGob({ apropiaciones, onClose, onCreado }: { apropiaciones: ApropiacionGob[]; onClose: () => void; onCreado: () => void }) {
  const [apropiacionId, setApropiacionId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState(0);
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apropiacionId) { setError("Seleccione la apropiación."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/cdps", { apropiacionId: Number(apropiacionId), fecha, valor, descripcion });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el CDP.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo CDP</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Apropiación</label>
          <select className="input-field" value={apropiacionId} onChange={(e) => setApropiacionId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {apropiaciones.map((a) => <option key={a.id} value={a.id}>{a.rubro_codigo} — {a.rubro_nombre} (saldo {money(a.saldoDisponible)})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
          <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Objeto</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear CDP"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalRpGob({ cdps, terceros, recargarTerceros, onClose, onCreado }: {
  cdps: CdpGob[]; terceros: TerceroGob[]; recargarTerceros: () => void; onClose: () => void; onCreado: () => void;
}) {
  const [cdpId, setCdpId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState(0);
  const [descripcion, setDescripcion] = useState("");
  const [beneficiarioTerceroId, setBeneficiarioTerceroId] = useState<number | "">("");
  const [modalTerceroAbierto, setModalTerceroAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cdpId || !beneficiarioTerceroId) { setError("Seleccione el CDP y el beneficiario."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/rps", { cdpId: Number(cdpId), fecha, valor, descripcion, beneficiarioTerceroId: Number(beneficiarioTerceroId) });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el RP.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo RP (Registro Presupuestal del Compromiso)</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">CDP</label>
          <select className="input-field" value={cdpId} onChange={(e) => setCdpId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {cdps.map((c) => <option key={c.id} value={c.id}>CDP-{c.numero} — {c.descripcion} (saldo {money(c.saldoDisponible)})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Beneficiario (tercero)</label>
          <div className="flex gap-2">
            <select className="input-field" value={beneficiarioTerceroId} onChange={(e) => setBeneficiarioTerceroId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {terceros.map((t) => <option key={t.id} value={t.id}>{t.nombre} ({t.numero_documento})</option>)}
            </select>
            <button type="button" className="btn-ghost text-xs whitespace-nowrap" onClick={() => setModalTerceroAbierto(true)}>+ Nuevo</button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
          <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Objeto del compromiso</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear RP"}</button>
        </div>
      </form>
      {modalTerceroAbierto && (
        <ModalTerceroGob onClose={() => setModalTerceroAbierto(false)} onCreado={(id) => { setBeneficiarioTerceroId(id); recargarTerceros(); setModalTerceroAbierto(false); }} />
      )}
    </div>
  );
}

function ModalObligacionGob({ rps, onClose, onCreado }: { rps: RpGob[]; onClose: () => void; onCreado: () => void }) {
  const [rpId, setRpId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState(0);
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rpId) { setError("Seleccione el RP."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/obligaciones", { rpId: Number(rpId), fecha, valor, descripcion });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo causar la obligación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Causar obligación presupuestal</h3>
        <p className="text-xs text-textmuted">Se causa cuando el bien o servicio ya fue recibido a satisfacción — genera el comprobante contable real.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">RP</label>
          <select className="input-field" value={rpId} onChange={(e) => setRpId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {rps.map((r) => <option key={r.id} value={r.id}>RP-{r.numero} — {r.descripcion} (saldo {money(r.saldoDisponible)})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
          <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Ej: Recibido a satisfacción — acta de entrega" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Causando..." : "Causar obligación"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalIngresoGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [rubroCodigo, setRubroCodigo] = useState("");
  const [rubroNombre, setRubroNombre] = useState("");
  const [categoria, setCategoria] = useState("tributario");
  const [valorPresupuestado, setValorPresupuestado] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/ingresos", { vigenciaFiscal, rubroCodigo, rubroNombre, categoria, valorPresupuestado });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el rubro de ingreso.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo rubro de ingreso</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
          <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código del rubro</label>
          <input className="input-field" value={rubroCodigo} onChange={(e) => setRubroCodigo(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre del rubro</label>
          <input className="input-field" value={rubroNombre} onChange={(e) => setRubroNombre(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
          <select className="input-field" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            {Object.entries(CATEGORIA_INGRESO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor presupuestado</label>
          <input className="input-field" type="number" min={0} step="1000" value={valorPresupuestado} onChange={(e) => setValorPresupuestado(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear rubro"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalRecaudarIngresoGob({ ingreso, cuentasBancarias, onClose, onCreado }: {
  ingreso: IngresoPresupuestalGob; cuentasBancarias: CuentaBancariaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState(0);
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const recaudar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuentaBancariaId) { setError("Seleccione la cuenta bancaria de destino del recaudo."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/sigepro-ep/presupuesto/ingresos/${ingreso.id}/recaudar`, { cuentaBancariaId: Number(cuentaBancariaId), fecha, valor, descripcion: descripcion || null });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el recaudo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={recaudar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Recaudar — {ingreso.rubro_codigo} {ingreso.rubro_nombre}</h3>
        <p className="text-xs text-textmuted">Vigente {money(ingreso.valorVigente)} · recaudado a la fecha {money(ingreso.recaudado)}.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta bancaria de destino</label>
          <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
            <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (opcional)</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Recaudar"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalModificacionGob({ apropiaciones, ingresos, onClose, onCreado }: {
  apropiaciones: ApropiacionGob[]; ingresos: IngresoPresupuestalGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [lado, setLado] = useState<"gasto" | "ingreso">("gasto");
  const [tipo, setTipo] = useState<"adicion" | "reduccion">("adicion");
  const [refId, setRefId] = useState<number | "">("");
  const [valor, setValor] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!refId) { setError("Seleccione el rubro a modificar."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/modificaciones", { lado, tipo, refId: Number(refId), valor, fecha, descripcion });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar la modificación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva Adición / Reducción</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Lado</label>
            <select className="input-field" value={lado} onChange={(e) => { setLado(e.target.value as any); setRefId(""); }}>
              <option value="gasto">Gasto (apropiación)</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
              <option value="adicion">Adición</option>
              <option value="reduccion">Reducción</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Rubro</label>
          <select className="input-field" value={refId} onChange={(e) => setRefId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {lado === "gasto"
              ? apropiaciones.map((a) => <option key={a.id} value={a.id}>{a.rubro_codigo} — {a.rubro_nombre} (vigente {money(a.valorVigente)})</option>)
              : ingresos.map((i) => <option key={i.id} value={i.id}>{i.rubro_codigo} — {i.rubro_nombre} (vigente {money(i.valorVigente)})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
            <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalTraspasoGob({ apropiaciones, ingresos, onClose, onCreado }: {
  apropiaciones: ApropiacionGob[]; ingresos: IngresoPresupuestalGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [lado, setLado] = useState<"gasto" | "ingreso">("gasto");
  const [rubroOrigenId, setRubroOrigenId] = useState<number | "">("");
  const [rubroDestinoId, setRubroDestinoId] = useState<number | "">("");
  const [valor, setValor] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rubroOrigenId || !rubroDestinoId) { setError("Seleccione el rubro de origen y el de destino."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/traspasos", {
        lado, rubroOrigenId: Number(rubroOrigenId), rubroDestinoId: Number(rubroDestinoId), valor, fecha, descripcion,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el traspaso.");
    } finally {
      setEnviando(false);
    }
  };

  const rubros = lado === "gasto" ? apropiaciones : ingresos;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo traspaso (Crédito / Contra-crédito)</h3>
        <p className="text-xs text-textmuted">Crea el par completo: un Contra-crédito en el rubro de origen y un Crédito en el de destino, por el mismo valor.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Lado</label>
          <select className="input-field" value={lado} onChange={(e) => { setLado(e.target.value as any); setRubroOrigenId(""); setRubroDestinoId(""); }}>
            <option value="gasto">Gasto (apropiaciones)</option>
            <option value="ingreso">Ingreso</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rubro origen (contra-crédito)</label>
            <select className="input-field" value={rubroOrigenId} onChange={(e) => setRubroOrigenId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {rubros.map((r) => <option key={r.id} value={r.id}>{r.rubro_codigo} — {r.rubro_nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rubro destino (crédito)</label>
            <select className="input-field" value={rubroDestinoId} onChange={(e) => setRubroDestinoId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {rubros.map((r) => <option key={r.id} value={r.id}>{r.rubro_codigo} — {r.rubro_nombre}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
            <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar traspaso"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalReservaGob({ rps, onClose, onCreado }: { rps: RpGob[]; onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState(0);
  const [fechaConstitucion, setFechaConstitucion] = useState(new Date().toISOString().slice(0, 10));
  const [rpId, setRpId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/reservas", { vigenciaFiscal, descripcion, valor, fechaConstitucion, rpId: rpId || null });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo constituir la reserva.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva reserva presupuestal (saldo inicial)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de constitución</label>
            <input className="input-field" type="date" value={fechaConstitucion} onChange={(e) => setFechaConstitucion(e.target.value)} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">RP asociado (opcional)</label>
          <select className="input-field" value={rpId} onChange={(e) => setRpId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Sin RP asociado</option>
            {rps.map((r) => <option key={r.id} value={r.id}>RP-{r.numero} — {r.descripcion}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Constituyendo..." : "Constituir reserva"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalCxpGob({ obligaciones, onClose, onCreado }: { obligaciones: ObligacionGob[]; onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState(0);
  const [fechaConstitucion, setFechaConstitucion] = useState(new Date().toISOString().slice(0, 10));
  const [obligacionId, setObligacionId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/cuentas-por-pagar", { vigenciaFiscal, descripcion, valor, fechaConstitucion, obligacionId: obligacionId || null });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo constituir la cuenta por pagar.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva cuenta por pagar presupuestal (saldo inicial)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de constitución</label>
            <input className="input-field" type="date" value={fechaConstitucion} onChange={(e) => setFechaConstitucion(e.target.value)} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Obligación de origen (opcional)</label>
          <select className="input-field" value={obligacionId} onChange={(e) => setObligacionId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Sin obligación asociada</option>
            {obligaciones.map((o) => <option key={o.id} value={o.id}>Obligación #{o.numero} — {o.descripcion}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
          <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Constituyendo..." : "Constituir cuenta por pagar"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalFuenteFinanciacionGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/fuentes-financiacion", { codigo, nombre });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear la fuente de financiación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva fuente de financiación</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
          <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear fuente"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalDescuentoPresupuestalGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState<"porcentaje" | "valor_fijo">("porcentaje");
  const [valor, setValor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/presupuesto/descuentos", { codigo, nombre, tipo, valor });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el descuento.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo descuento presupuestal</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
          <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
              <option value="porcentaje">Porcentaje</option>
              <option value="valor_fijo">Valor fijo</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor {tipo === "porcentaje" ? "(%)" : ""}</label>
            <input className="input-field" type="number" min={0} max={tipo === "porcentaje" ? 100 : undefined} step={tipo === "porcentaje" ? "0.1" : "1000"} value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear descuento"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Nómina Pública
const TIPO_CONTRATO_LABEL_GOB: Record<string, string> = {
  termino_indefinido: "Término indefinido", termino_fijo: "Término fijo", obra_labor: "Obra o labor", aprendizaje: "Aprendizaje",
};

function TabNominaGob({ empleados, contratos, periodos, liquidaciones, cuentasBancarias, puedeCrear, puedeLiquidar, puedePagar, recargar }: {
  empleados: EmpleadoGob[]; contratos: ContratoGob[]; periodos: PeriodoGob[]; liquidaciones: LiquidacionGob[];
  cuentasBancarias: CuentaBancariaGob[]; puedeCrear: boolean; puedeLiquidar: boolean; puedePagar: boolean; recargar: () => void;
}) {
  const [tabInterno, setTabInterno] = useState<"empleados" | "periodos" | "liquidaciones">("empleados");
  const [modalEmpAbierto, setModalEmpAbierto] = useState(false);
  const [modalPerAbierto, setModalPerAbierto] = useState(false);
  const [modalLiqAbierto, setModalLiqAbierto] = useState(false);
  const [pagandoId, setPagandoId] = useState<number | null>(null);
  const [cuentaPago, setCuentaPago] = useState<number | "">("");
  const [errorPago, setErrorPago] = useState<string | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);
  const [desdeLiq, setDesdeLiq] = useState("");
  const [hastaLiq, setHastaLiq] = useState("");
  const liquidacionesFiltradas = liquidaciones.filter((l) => {
    if (desdeLiq && l.fecha_fin < desdeLiq) return false;
    if (hastaLiq && l.fecha_fin > hastaLiq) return false;
    return true;
  });

  const pagar = async (liquidacionId: number) => {
    if (!cuentaPago) { setErrorPago("Seleccione la cuenta bancaria de origen del pago."); return; }
    setErrorPago(null);
    try {
      await api.post(`/sigepro-ep/nomina/liquidaciones/${liquidacionId}/pagar`, { cuentaBancariaId: Number(cuentaPago) });
      setPagandoId(null);
      recargar();
    } catch (err) {
      setErrorPago(err instanceof ApiError ? err.message : "No se pudo pagar la liquidación.");
    }
  };

  // Certificado laboral "a quien interese" (Art. 57 num. 7 CST) para un
  // empleado público — mismo motor y documento que el núcleo privado.
  const descargarCertificado = async (emp: EmpleadoGob, formato: "pdf" | "docx") => {
    const clave = `cert-${emp.id}-${formato}`;
    setDescargando(clave);
    setErrorDescarga(null);
    try {
      await descargarArchivo(`/sigepro-ep/nomina/empleados/${emp.id}/certificado-laboral?formato=${formato}`, `certificado_laboral_ep_${emp.numeroDocumento}.${formato}`);
    } catch (err) {
      setErrorDescarga(err instanceof ApiError ? err.message : `No se pudo generar el certificado laboral de ${emp.nombre}.`);
    } finally {
      setDescargando(null);
    }
  };

  // Desprendible de pago de una liquidación ya generada.
  const descargarDesprendible = async (liq: LiquidacionGob, formato: "pdf" | "xlsx" | "docx") => {
    const clave = `desp-${liq.id}-${formato}`;
    setDescargando(clave);
    setErrorDescarga(null);
    try {
      await descargarArchivo(
        `/sigepro-ep/nomina/liquidaciones/${liq.id}/desprendible?formato=${formato}`,
        `desprendible_nomina_ep_${liq.id}_${liq.anio}${String(liq.mes).padStart(2, "0")}.${formato}`,
      );
    } catch (err) {
      setErrorDescarga(err instanceof ApiError ? err.message : `No se pudo generar el desprendible de pago de ${liq.empleado_nombre}.`);
    } finally {
      setDescargando(null);
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Empleados, contratos y períodos son las mismas tablas genéricas de la Fase 8 privada (sin dependencia del
        plan de cuentas) — solo la liquidación cambia: se contabiliza contra el CGC en vez del PUC, y el pasivo
        laboral se consolida contra una única cuenta ("25 — Beneficios a los Empleados").
      </p>

      <Tabs
        tabs={[
          { key: "empleados", label: "Empleados y contratos" },
          { key: "periodos", label: "Períodos" },
          { key: "liquidaciones", label: "Liquidaciones y pago" },
        ]}
        activo={tabInterno} onChange={(k) => setTabInterno(k as any)}
      />

      {tabInterno === "empleados" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Empleados públicos</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalEmpAbierto(true)}>+ Nuevo empleado</button>}
          </div>
          {errorDescarga && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorDescarga}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Nombre</th><th className="table-th">Documento</th><th className="table-th">Cargo</th>
                <th className="table-th">Contrato</th><th className="table-th">Salario base</th><th className="table-th">Estado</th><th className="table-th"></th>
              </tr></thead>
              <tbody>
                {empleados.map((e) => {
                  const contrato = contratos.find((c) => c.empleadoId === e.id && c.estado === "activo");
                  return (
                    <tr key={e.id} className="border-b border-borderc last:border-0">
                      <td className="table-td">{e.nombre}</td>
                      <td className="table-td text-xs">{e.tipoDocumento} {e.numeroDocumento}</td>
                      <td className="table-td text-xs">{e.cargo || "—"}</td>
                      <td className="table-td text-xs">{contrato ? TIPO_CONTRATO_LABEL_GOB[contrato.tipoContrato] : "Sin contrato"}</td>
                      <td className="table-td">{contrato ? money(contrato.salarioBase) : "—"}</td>
                      <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{e.estado}</span></td>
                      <td className="table-td text-right space-x-2 whitespace-nowrap">
                        <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `cert-${e.id}-pdf`} onClick={() => descargarCertificado(e, "pdf")}>
                          {descargando === `cert-${e.id}-pdf` ? "Generando..." : "Certificado PDF"}
                        </button>
                        <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `cert-${e.id}-docx`} onClick={() => descargarCertificado(e, "docx")}>
                          {descargando === `cert-${e.id}-docx` ? "Generando..." : "Word"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {empleados.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin empleados registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "periodos" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Períodos de nómina</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalPerAbierto(true)}>+ Nuevo período</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Período</th><th className="table-th">Desde</th><th className="table-th">Hasta</th><th className="table-th">Estado</th>
              </tr></thead>
              <tbody>
                {periodos.map((p) => (
                  <tr key={p.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{p.anio}-{String(p.mes).padStart(2, "0")}</td>
                    <td className="table-td text-xs">{p.fechaInicio}</td>
                    <td className="table-td text-xs">{p.fechaFin}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{p.estado}</span></td>
                  </tr>
                ))}
                {periodos.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin períodos creados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "liquidaciones" && (
        <div>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Liquidaciones</h3>
            {puedeLiquidar && <button className="btn-primary" onClick={() => setModalLiqAbierto(true)}>+ Liquidar empleado</button>}
          </div>
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde (cierre de período)</label>
                  <input type="date" className="input-field" value={desdeLiq} onChange={(e) => setDesdeLiq(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaLiq} onChange={(e) => setHastaLiq(e.target.value)} /></div>
                {(desdeLiq || hastaLiq) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeLiq(""); setHastaLiq(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/nomina/liquidaciones/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdeLiq || undefined, hasta: hastaLiq || undefined }}
                nombreBase={`nomina_consolidada_ep${desdeLiq ? `_${desdeLiq}` : ""}${hastaLiq ? `_${hastaLiq}` : ""}`}
                label="Descargar consolidado"
              />
            </div>
          </div>
          {errorPago && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorPago}</div>}
          {errorDescarga && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorDescarga}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Empleado</th><th className="table-th">Período</th><th className="table-th">Devengado</th>
                <th className="table-th">Deducciones</th><th className="table-th">Neto</th><th className="table-th">Estado</th>
                <th className="table-th">Desprendible</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {liquidacionesFiltradas.map((l) => (
                  <tr key={l.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{l.empleado_nombre}</td>
                    <td className="table-td text-xs">{l.anio}-{String(l.mes).padStart(2, "0")}</td>
                    <td className="table-td">{money(l.total_devengado)}</td>
                    <td className="table-td">{money(l.total_deducciones)}</td>
                    <td className="table-td font-semibold">{money(l.neto_pagado)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{l.estado}</span></td>
                    <td className="table-td whitespace-nowrap space-x-2">
                      <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `desp-${l.id}-pdf`} onClick={() => descargarDesprendible(l, "pdf")}>
                        {descargando === `desp-${l.id}-pdf` ? "Generando..." : "PDF"}
                      </button>
                      <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `desp-${l.id}-xlsx`} onClick={() => descargarDesprendible(l, "xlsx")}>
                        {descargando === `desp-${l.id}-xlsx` ? "Generando..." : "Excel"}
                      </button>
                    </td>
                    <td className="table-td">
                      {l.estado === "liquidada" && puedePagar && (
                        pagandoId === l.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPago} onChange={(e) => setCuentaPago(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => pagar(l.id)}>Pagar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagandoId(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagandoId(l.id); setErrorPago(null); }}>Pagar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {liquidacionesFiltradas.length === 0 && (
                  <tr><td colSpan={8} className="table-td text-center text-textmuted py-6">
                    {liquidaciones.length === 0 ? "Sin liquidaciones registradas." : "Ninguna liquidación en el rango de fechas seleccionado."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalEmpAbierto && <ModalEmpleadoGob onClose={() => setModalEmpAbierto(false)} onCreado={() => { setModalEmpAbierto(false); recargar(); }} />}
      {modalPerAbierto && <ModalPeriodoGob onClose={() => setModalPerAbierto(false)} onCreado={() => { setModalPerAbierto(false); recargar(); }} />}
      {modalLiqAbierto && (
        <ModalLiquidarGob empleados={empleados} periodos={periodos} onClose={() => setModalLiqAbierto(false)} onCreado={() => { setModalLiqAbierto(false); recargar(); }} />
      )}
    </div>
  );
}

function ModalEmpleadoGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [nombre, setNombre] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("CC");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [fechaIngreso, setFechaIngreso] = useState(new Date().toISOString().slice(0, 10));
  const [cargo, setCargo] = useState("");
  const [salarioBase, setSalarioBase] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const empleado = await api.post("/sigepro-ep/nomina/empleados", { nombre, tipoDocumento, numeroDocumento, fechaIngreso, cargo });
      await api.post("/sigepro-ep/nomina/contratos", {
        empleadoId: empleado.id, tipoContrato: "termino_indefinido", tipoSalario: "fijo", salarioBase, fechaInicio: fechaIngreso,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el empleado.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo empleado público</h3>
        <p className="text-xs text-textmuted">Crea el empleado y su contrato a término indefinido en un solo paso.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre completo</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo documento</label>
            <select className="input-field" value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)}>
              <option value="CC">Cédula de ciudadanía</option>
              <option value="CE">Cédula de extranjería</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Número documento</label>
            <input className="input-field" value={numeroDocumento} onChange={(e) => setNumeroDocumento(e.target.value)} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cargo</label>
          <input className="input-field" value={cargo} onChange={(e) => setCargo(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de ingreso</label>
            <input className="input-field" type="date" value={fechaIngreso} onChange={(e) => setFechaIngreso(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Salario base</label>
            <input className="input-field" type="number" min={0} step="1000" value={salarioBase} onChange={(e) => setSalarioBase(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear empleado"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalPeriodoGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [fechaInicio, setFechaInicio] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/nomina/periodos", { anio, mes, fechaInicio, fechaFin });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el período.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo período de nómina</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Año</label>
            <input className="input-field" type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Mes</label>
            <input className="input-field" type="number" min={1} max={12} value={mes} onChange={(e) => setMes(Number(e.target.value))} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha inicio</label>
            <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha fin</label>
            <input className="input-field" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear período"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalLiquidarGob({ empleados, periodos, onClose, onCreado }: {
  empleados: EmpleadoGob[]; periodos: PeriodoGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [empleadoId, setEmpleadoId] = useState<number | "">("");
  const [periodoId, setPeriodoId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const liquidar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empleadoId || !periodoId) { setError("Seleccione el empleado y el período."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/nomina/liquidaciones", { empleadoId: Number(empleadoId), periodoId: Number(periodoId) });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo liquidar la nómina.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={liquidar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Liquidar nómina de un empleado</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Empleado</label>
          <select className="input-field" value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {empleados.filter((e) => e.estado === "activo").map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Período</label>
          <select className="input-field" value={periodoId} onChange={(e) => setPeriodoId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {periodos.map((p) => <option key={p.id} value={p.id}>{p.anio}-{String(p.mes).padStart(2, "0")}</option>)}
          </select>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Liquidando..." : "Liquidar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Activos Fijos públicos
const CATEGORIA_LABEL_GOB: Record<string, string> = {
  maquinaria: "Maquinaria", vehiculo: "Vehículo", equipo_computo: "Equipo de cómputo",
  muebles_enseres: "Muebles y enseres", edificio: "Edificio", otro: "Otro",
};

function TabActivosGob({ activos, cuentasBancarias, puedeCrear, puedeDepreciar, puedeBaja, recargar }: {
  activos: ActivoGob[]; cuentasBancarias: CuentaBancariaGob[]; puedeCrear: boolean; puedeDepreciar: boolean; puedeBaja: boolean; recargar: () => void;
}) {
  const [modalActivoAbierto, setModalActivoAbierto] = useState(false);
  const [modalDepAbierto, setModalDepAbierto] = useState(false);
  const [bajandoId, setBajandoId] = useState<number | null>(null);
  const [motivoBaja, setMotivoBaja] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [desdeAdq, setDesdeAdq] = useState("");
  const [hastaAdq, setHastaAdq] = useState("");

  const darDeBaja = async (activoId: number) => {
    if (!motivoBaja.trim()) { setError("Indique el motivo de la baja."); return; }
    setError(null);
    try {
      await api.post(`/sigepro-ep/activos/${activoId}/baja`, { motivo: motivoBaja });
      setBajandoId(null); setMotivoBaja("");
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo dar de baja el activo.");
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Adquisición, depreciación en línea recta y baja — mismo motor que Activos Fijos privado, contabilizando
        contra el CGC. Esta entrega solo soporta adquisición de <strong>contado</strong> (sin crédito a proveedor) y
        sin traslado entre centros de costo — ambos son conceptos que SIGEPRO-EP no tiene todavía.
      </p>
      <div className="flex justify-between items-end mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800">Activos fijos públicos</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {puedeDepreciar && <button className="btn-ghost" onClick={() => setModalDepAbierto(true)}>Ejecutar depreciación de período</button>}
          {puedeCrear && <button className="btn-primary" onClick={() => setModalActivoAbierto(true)}>+ Nuevo activo</button>}
        </div>
      </div>
      <div className="card p-4 mb-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Adquiridos desde</label>
              <input type="date" className="input-field" value={desdeAdq} onChange={(e) => setDesdeAdq(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
              <input type="date" className="input-field" value={hastaAdq} onChange={(e) => setHastaAdq(e.target.value)} /></div>
            {(desdeAdq || hastaAdq) && (
              <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeAdq(""); setHastaAdq(""); }}>Limpiar</button>
            )}
          </div>
          <DescargaReporteEP
            endpoint="/sigepro-ep/activos/exportar" formatos={["pdf", "xlsx", "docx"]}
            params={{ desde: desdeAdq || undefined, hasta: hastaAdq || undefined }}
            nombreBase={`activos_fijos_ep${desdeAdq ? `_${desdeAdq}` : ""}${hastaAdq ? `_${hastaAdq}` : ""}`}
            label="Descargar listado"
          />
        </div>
      </div>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Categoría</th>
            <th className="table-th">Adquisición</th><th className="table-th">Depreciación acum.</th><th className="table-th">Valor en libros</th>
            <th className="table-th">Estado</th><th className="table-th">Acción</th>
          </tr></thead>
          <tbody>
            {activos.map((a) => (
              <tr key={a.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{a.codigo}</td>
                <td className="table-td">{a.nombre}</td>
                <td className="table-td text-xs">{CATEGORIA_LABEL_GOB[a.categoria] || a.categoria}</td>
                <td className="table-td">{money(a.valor_adquisicion)}</td>
                <td className="table-td">{money(a.depreciacionAcumulada)}</td>
                <td className="table-td font-semibold">{money(a.valorLibros)}</td>
                <td className="table-td"><span className={`badge ${a.estado === "activo" ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>{a.estado}</span></td>
                <td className="table-td">
                  {a.estado === "activo" && puedeBaja && (
                    bajandoId === a.id ? (
                      <div className="flex items-center gap-1">
                        <input className="input-field text-xs py-1 w-32" placeholder="Motivo" value={motivoBaja} onChange={(e) => setMotivoBaja(e.target.value)} />
                        <button className="btn-primary text-xs px-2 py-1" onClick={() => darDeBaja(a.id)}>Dar de baja</button>
                        <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setBajandoId(null); setMotivoBaja(""); }}>×</button>
                      </div>
                    ) : (
                      <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setBajandoId(a.id); setError(null); }}>Dar de baja</button>
                    )
                  )}
                </td>
              </tr>
            ))}
            {activos.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-6">Sin activos fijos registrados.</td></tr>}
          </tbody>
        </table>
      </div>

      {modalActivoAbierto && (
        <ModalActivoGob cuentasBancarias={cuentasBancarias} onClose={() => setModalActivoAbierto(false)} onCreado={() => { setModalActivoAbierto(false); recargar(); }} />
      )}
      {modalDepAbierto && <ModalDepreciarGob onClose={() => setModalDepAbierto(false)} onCreado={() => { setModalDepAbierto(false); recargar(); }} />}
    </div>
  );
}

function ModalActivoGob({ cuentasBancarias, onClose, onCreado }: { cuentasBancarias: CuentaBancariaGob[]; onClose: () => void; onCreado: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [categoria, setCategoria] = useState("maquinaria");
  const [fechaAdquisicion, setFechaAdquisicion] = useState(new Date().toISOString().slice(0, 10));
  const [valorAdquisicion, setValorAdquisicion] = useState(0);
  const [valorResidual, setValorResidual] = useState(0);
  const [vidaUtilMeses, setVidaUtilMeses] = useState(60);
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cuentaBancariaId) { setError("Seleccione la cuenta bancaria de origen (esta entrega solo soporta compra de contado)."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/activos", {
        codigo, nombre, categoria, fechaAdquisicion, valorAdquisicion, valorResidual, vidaUtilMeses,
        cuentaBancariaId: Number(cuentaBancariaId),
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el activo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo activo fijo público</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
            <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
            <select className="input-field" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              {Object.entries(CATEGORIA_LABEL_GOB).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de adquisición</label>
            <input className="input-field" type="date" value={fechaAdquisicion} onChange={(e) => setFechaAdquisicion(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vida útil (meses)</label>
            <input className="input-field" type="number" min={1} value={vidaUtilMeses} onChange={(e) => setVidaUtilMeses(Number(e.target.value))} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor de adquisición</label>
            <input className="input-field" type="number" min={0} step="1000" value={valorAdquisicion} onChange={(e) => setValorAdquisicion(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor residual</label>
            <input className="input-field" type="number" min={0} step="1000" value={valorResidual} onChange={(e) => setValorResidual(Number(e.target.value))} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta bancaria de origen (compra de contado)</label>
          <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar activo"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalDepreciarGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ activosDepreciados: number; valorTotal: number } | null>(null);

  const ejecutar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post("/sigepro-ep/activos/depreciar", { periodo });
      setResultado(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo ejecutar la depreciación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={ejecutar} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Ejecutar depreciación de período</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Período (AAAA-MM)</label>
          <input className="input-field" type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required />
        </div>
        {resultado && (
          <div className="rounded-md bg-accent-light px-3 py-2 text-sm text-accent-dark">
            {resultado.activosDepreciados} activo(s) depreciados por un total de {money(resultado.valorTotal)}.
          </div>
        )}
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cerrar</button>
          {!resultado && <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Ejecutando..." : "Ejecutar"}</button>}
          {resultado && <button type="button" className="btn-primary" onClick={onCreado}>Listo</button>}
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Auditorías (consistencia interna)
function TabAuditoriasGob({ puedeVerPresupuesto, puedeVerContab }: { puedeVerPresupuesto: boolean; puedeVerContab: boolean }) {
  const [vigencia, setVigencia] = useState(new Date().getFullYear());
  const [desdeDescuadrados, setDesdeDescuadrados] = useState("");
  const [hastaDescuadrados, setHastaDescuadrados] = useState("");

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Verificaciones de consistencia interna (no son formatos exigidos por una entidad externa, sino chequeos que
        la propia entidad usa para revisar su información antes de rendir cuenta) sobre datos reales de Presupuesto
        Público y Contabilidad Pública.
      </p>
      {puedeVerPresupuesto && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal (reportes que la usan)</label>
          <input className="input-field w-32" type="number" value={vigencia} onChange={(e) => setVigencia(Number(e.target.value))} />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {puedeVerPresupuesto && (
          <>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">1. Cuadre presupuestal por rubro</h4>
              <p className="text-xs text-textmuted mb-3">Comprometido ≤ vigente, obligado ≤ comprometido, pagado ≤ obligado.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/auditorias/cuadre-presupuestal" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigencia }} nombreBase={`auditoria_cuadre_presupuestal_ep_${vigencia}`}
              />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">2. CDP sin comprometer (RP)</h4>
              <p className="text-xs text-textmuted mb-3">CDP con saldo disponible que lleva 30+ días sin un RP asociado.</p>
              <DescargaReporteEP endpoint="/sigepro-ep/auditorias/cdp-sin-comprometer" formatos={["xlsx", "pdf"]} nombreBase="auditoria_cdp_sin_comprometer_ep" />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">3. RP sin obligación causada</h4>
              <p className="text-xs text-textmuted mb-3">RP con saldo disponible que lleva 60+ días sin obligación causada.</p>
              <DescargaReporteEP endpoint="/sigepro-ep/auditorias/rp-sin-obligar" formatos={["xlsx", "pdf"]} nombreBase="auditoria_rp_sin_obligar_ep" />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">4. Obligaciones causadas sin pagar</h4>
              <p className="text-xs text-textmuted mb-3">Cuentas por pagar "de hecho" — obligaciones causadas que ni se pagaron ni se constituyeron como CxP.</p>
              <DescargaReporteEP endpoint="/sigepro-ep/auditorias/obligaciones-sin-pagar" formatos={["xlsx", "pdf"]} nombreBase="auditoria_obligaciones_sin_pagar_ep" />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">7. Subejecución de ingresos</h4>
              <p className="text-xs text-textmuted mb-3">Rubros de ingreso con recaudo por debajo del 50% de lo presupuestado.</p>
              <DescargaReporteEP
                endpoint="/sigepro-ep/auditorias/subejecucion-ingresos" formatos={["xlsx", "pdf"]}
                params={{ vigenciaFiscal: vigencia }} nombreBase={`auditoria_subejecucion_ingresos_ep_${vigencia}`}
              />
            </div>
          </>
        )}
        {puedeVerContab && (
          <>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">5. Cuadre del balance general público</h4>
              <p className="text-xs text-textmuted mb-3">Confirma que activo = pasivo + patrimonio a la fecha de corte de hoy.</p>
              <DescargaReporteEP endpoint="/sigepro-ep/auditorias/cuadre-balance" formatos={["xlsx", "pdf"]} nombreBase="auditoria_cuadre_balance_ep" />
            </div>
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-slate-800 mb-1">6. Comprobantes contables descuadrados</h4>
              <p className="text-xs text-textmuted mb-3">Comprobantes donde la suma de débitos no coincide con la suma de créditos. Sin fechas, revisa todo el histórico.</p>
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                  <input type="date" className="input-field" value={desdeDescuadrados} onChange={(e) => setDesdeDescuadrados(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaDescuadrados} onChange={(e) => setHastaDescuadrados(e.target.value)} /></div>
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/auditorias/comprobantes-descuadrados" formatos={["xlsx", "pdf"]}
                params={{ desde: desdeDescuadrados || undefined, hasta: hastaDescuadrados || undefined }}
                nombreBase={`auditoria_comprobantes_descuadrados_ep${desdeDescuadrados ? `_${desdeDescuadrados}` : ""}${hastaDescuadrados ? `_${hastaDescuadrados}` : ""}`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Información Exógena pública
const FORMATO_LABEL_GOB: Record<string, string> = {
  "1001": "1001 — Pagos a terceros",
  "1009": "1009 — Saldo de cuentas por pagar",
  "1406": "1406 — Registros catastrales",
  "1476": "1476 — Registros catastrales e impuesto predial",
  "1481": "1481 — Industria y Comercio (ICA)",
  "2276": "2276 — Certificado de nómina",
};

function TabExogenaGob({ reportes, puedeGenerar, recargar }: { reportes: ReporteExogenaGob[]; puedeGenerar: boolean; recargar: () => void }) {
  const [formato, setFormato] = useState("1001");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [descargando, setDescargando] = useState<string | null>(null);

  const generar = async () => {
    setGenerando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/exogena/generar", { formato, anio });
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo generar el reporte.");
    } finally {
      setGenerando(false);
    }
  };

  const presentar = async (reporteId: number) => {
    try {
      await api.post(`/sigepro-ep/exogena/reportes/${reporteId}/presentar`);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo marcar como presentado.");
    }
  };

  // Descarga del reporte ya generado. "txt" es el archivo real que exige el
  // prevalidador DIAN (delimitado por "|"); xlsx/pdf son para revisión
  // humana antes de presentarlo — mismo contenido, tres presentaciones.
  const descargarReporte = async (r: ReporteExogenaGob, fmt: "txt" | "xlsx" | "pdf") => {
    const clave = `${r.id}-${fmt}`;
    setDescargando(clave);
    setError(null);
    try {
      await descargarArchivo(`/sigepro-ep/exogena/reportes/${r.id}/descargar?formato=${fmt}`, `exogena_${r.formato}_${r.anio_gravable}.${fmt}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `No se pudo descargar el reporte ${r.formato}.`);
    } finally {
      setDescargando(null);
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Las entidades públicas están obligadas a presentar información exógena sin importar el monto de sus
        ingresos (Art. 1.3.1.1, numerales 9, 10 y 16, Resolución DIAN 000227 de 2025). Esta entrega soporta 6
        formatos, verificados contra la estructura real de la DIAN (4 archivos ya presentados por una entidad
        pública, y el prevalidador oficial "Gobernaciones y Alcaldías"): <strong>1001</strong> (pagos a terceros,
        desde los comprobantes contabilizados), <strong>1009</strong> (saldo de cuentas por pagar al 31-12, desde
        Presupuesto), <strong>1406</strong> y <strong>1476</strong> (registros catastrales e impuesto predial,
        desde Impuestos Territoriales), <strong>1481</strong> (Industria y Comercio, desde Impuestos
        Territoriales) y <strong>2276</strong> (certificado de nómina pública, versión reducida). Los demás
        formatos dependen de motores que SIGEPRO-EP no tiene todavía (retención en la fuente pública, IVA,
        CxC con saldo por tercero, vehículos, resoluciones administrativas).
      </p>

      {puedeGenerar && (
        <div className="card p-4 mb-4 flex items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Formato</label>
            <select className="input-field" value={formato} onChange={(e) => setFormato(e.target.value)}>
              {Object.entries(FORMATO_LABEL_GOB).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Año gravable</label>
            <input className="input-field" type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} />
          </div>
          <button className="btn-primary" onClick={generar} disabled={generando}>{generando ? "Generando..." : "Generar / regenerar"}</button>
        </div>
      )}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Formato</th><th className="table-th">Año</th><th className="table-th">Registros</th>
            <th className="table-th">Valor total</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
          </tr></thead>
          <tbody>
            {reportes.map((r) => (
              <tr key={r.id} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs">{FORMATO_LABEL_GOB[r.formato] || r.formato}</td>
                <td className="table-td">{r.anio_gravable}</td>
                <td className="table-td">{r.cantidad_registros}</td>
                <td className="table-td font-semibold">{money(r.valor_total)}</td>
                <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{r.estado}</span></td>
                <td className="table-td whitespace-nowrap space-x-2">
                  <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `${r.id}-txt`} onClick={() => descargarReporte(r, "txt")}>
                    {descargando === `${r.id}-txt` ? "Generando..." : "TXT (DIAN)"}
                  </button>
                  <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `${r.id}-xlsx`} onClick={() => descargarReporte(r, "xlsx")}>
                    {descargando === `${r.id}-xlsx` ? "Generando..." : "Excel"}
                  </button>
                  <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `${r.id}-pdf`} onClick={() => descargarReporte(r, "pdf")}>
                    {descargando === `${r.id}-pdf` ? "Generando..." : "PDF"}
                  </button>
                  {r.estado === "generado" && puedeGenerar && (
                    <button className="btn-ghost text-xs px-2 py-1" onClick={() => presentar(r.id)}>Marcar presentado</button>
                  )}
                </td>
              </tr>
            ))}
            {reportes.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin reportes generados todavía.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Impuestos Territoriales
const TIPO_PREDIO_LABEL: Record<string, string> = {
  residencial: "Residencial", comercial: "Comercial", industrial: "Industrial",
  lote_no_edificado: "Lote no edificado", rural: "Rural", dotacional: "Dotacional",
};
const TIPO_ACTIVIDAD_ICA_LABEL: Record<string, string> = { industrial: "Industrial", comercial: "Comercial", servicios: "Servicios" };
const TIPO_GESTION_COBRO_LABEL: Record<string, string> = { llamada: "Llamada", carta: "Carta", correo: "Correo", visita: "Visita", otro: "Otro" };
const ESTADO_ACUERDO_BADGE: Record<string, string> = {
  vigente: "bg-slate-100 text-slate-600", cumplido: "bg-accent-light text-accent-dark", incumplido: "bg-red-50 text-red-700",
};
const ESTADO_RETEICA_BADGE: Record<string, string> = {
  pendiente: "bg-slate-100 text-slate-600", pagado: "bg-accent-light text-accent-dark", anulado: "bg-red-50 text-red-700",
};
const MODULO_TERRITORIAL_LABEL: Record<string, string> = { predial: "Predial", ica: "ICA" };

function TabTerritorialGob({
  predios, liquidacionesPredial, contribuyentesIca, liquidacionesIca,
  acuerdosPago, gestionesCobro, deterioroCartera, prescripciones, tablaIntereses, conceptosPredial, estratosPredial,
  terceros, cuentasBancarias, puedeCrear, puedeLiquidar, puedePagar, puedeAjustar, recargar,
}: {
  predios: PredioGob[]; liquidacionesPredial: LiquidacionPredialGob[]; contribuyentesIca: ContribuyenteIcaGob[]; liquidacionesIca: LiquidacionIcaGob[];
  acuerdosPago: AcuerdoPagoGob[]; gestionesCobro: GestionCobroGob[]; deterioroCartera: DeterioroCarteraGob[]; prescripciones: PrescripcionCarteraGob[];
  tablaIntereses: TablaInteresGob[]; conceptosPredial: ConceptoPredialGob[]; estratosPredial: EstratoPredialGob[];
  terceros: TerceroGob[]; cuentasBancarias: CuentaBancariaGob[];
  puedeCrear: boolean; puedeLiquidar: boolean; puedePagar: boolean; puedeAjustar: boolean; recargar: () => void;
}) {
  const [tabInterno, setTabInterno] = useState<
    "predial" | "ica" | "cartera" | "acuerdos" | "cobro" | "deterioro" | "conceptos" | "intereses" | "pazysalvo" | "reteica" | "informes"
  >("predial");
  const [modalPredioAbierto, setModalPredioAbierto] = useState(false);
  const [modalLiqPredialAbierto, setModalLiqPredialAbierto] = useState(false);
  const [modalContribAbierto, setModalContribAbierto] = useState(false);
  const [modalLiqIcaAbierto, setModalLiqIcaAbierto] = useState(false);
  const [pagando, setPagando] = useState<{ tipo: "predial" | "ica"; id: number } | null>(null);
  const [cuentaPago, setCuentaPago] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);

  // ---- Filtros de fecha para los reportes exportables (Desde/Hasta) —
  // uno por sub-sección, ya que cada una filtra sobre columnas distintas
  // en el backend (ver sigepro_ep_territorial_routes.py).
  const [desdePredial, setDesdePredial] = useState("");
  const [hastaPredial, setHastaPredial] = useState("");
  const [desdeIca, setDesdeIca] = useState("");
  const [hastaIca, setHastaIca] = useState("");
  const [desdeAcuerdos, setDesdeAcuerdos] = useState("");
  const [hastaAcuerdos, setHastaAcuerdos] = useState("");
  const [desdeCobro, setDesdeCobro] = useState("");
  const [hastaCobro, setHastaCobro] = useState("");

  const pagar = async () => {
    if (!pagando || !cuentaPago) { setError("Seleccione la cuenta bancaria de destino del recaudo."); return; }
    setError(null);
    try {
      const ruta = pagando.tipo === "predial" ? `/sigepro-ep/territorial/predial/liquidaciones/${pagando.id}/pagar` : `/sigepro-ep/territorial/ica/liquidaciones/${pagando.id}/pagar`;
      await api.post(ruta, { cuentaBancariaId: Number(cuentaPago) });
      setPagando(null);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el recaudo.");
    }
  };

  // ---- Cartera (por módulo, bajo demanda)
  const [carteraModulo, setCarteraModulo] = useState<"predial" | "ica">("predial");
  const [carteraData, setCarteraData] = useState<CarteraItemGob[] | null>(null);
  const consultarCartera = async (modulo: "predial" | "ica") => {
    setCarteraModulo(modulo);
    setCarteraData(null);
    try { setCarteraData(await api.get(`/sigepro-ep/territorial/${modulo}/cartera`)); } catch { setCarteraData([]); }
  };

  // ---- Acuerdos de pago
  const [modalAcuerdoAbierto, setModalAcuerdoAbierto] = useState(false);
  const [pagandoCuota, setPagandoCuota] = useState<number | null>(null);
  const [cuentaPagoCuota, setCuentaPagoCuota] = useState<number | "">("");
  const [errorAcuerdo, setErrorAcuerdo] = useState<string | null>(null);
  const pagarCuota = async (acuerdoId: number) => {
    if (!cuentaPagoCuota) { setErrorAcuerdo("Seleccione la cuenta bancaria de destino del recaudo."); return; }
    setErrorAcuerdo(null);
    try {
      await api.post(`/sigepro-ep/territorial/acuerdos-pago/${acuerdoId}/cuotas`, { cuentaBancariaId: Number(cuentaPagoCuota) });
      setPagandoCuota(null);
      recargar();
    } catch (err) {
      setErrorAcuerdo(err instanceof ApiError ? err.message : "No se pudo registrar el pago de la cuota.");
    }
  };

  // ---- Gestión de cobro
  const [modalGestionAbierto, setModalGestionAbierto] = useState(false);

  // ---- Deterioro y prescripciones
  const [modalDeterioroAbierto, setModalDeterioroAbierto] = useState(false);
  const [modalPrescripcionAbierto, setModalPrescripcionAbierto] = useState(false);

  // ---- Tabla de intereses
  const [modalTasaInteresAbierto, setModalTasaInteresAbierto] = useState(false);

  // ---- Conceptos y tarifas (predial)
  const [subtabConceptos, setSubtabConceptos] = useState<"conceptos" | "porpredio" | "estratos" | "generales" | "porpredio-tarifa">("conceptos");
  const [modalConceptoAbierto, setModalConceptoAbierto] = useState(false);
  const [modalConceptoPredioAbierto, setModalConceptoPredioAbierto] = useState(false);
  const [modalEstratoAbierto, setModalEstratoAbierto] = useState(false);
  const [modalTarifaGeneralAbierto, setModalTarifaGeneralAbierto] = useState(false);
  const [modalTarifaPredioAbierto, setModalTarifaPredioAbierto] = useState(false);
  const [predioConceptosId, setPredioConceptosId] = useState<number | "">("");
  const [conceptosPorPredioData, setConceptosPorPredioData] = useState<ConceptoPredioGob[] | null>(null);
  const consultarConceptosPredio = async (predioId: number) => {
    setConceptosPorPredioData(null);
    try { setConceptosPorPredioData(await api.get(`/sigepro-ep/territorial/predial/predios/${predioId}/conceptos`)); } catch { setConceptosPorPredioData([]); }
  };
  const [vigenciaTarifaGeneral, setVigenciaTarifaGeneral] = useState(new Date().getFullYear());
  const [tarifasGeneralesData, setTarifasGeneralesData] = useState<TarifaGeneralPredialGob[] | null>(null);
  const consultarTarifasGenerales = async () => {
    setTarifasGeneralesData(null);
    try { setTarifasGeneralesData(await api.get(`/sigepro-ep/territorial/predial/tarifas-generales?vigenciaFiscal=${vigenciaTarifaGeneral}`)); } catch { setTarifasGeneralesData([]); }
  };
  const [predioTarifaId, setPredioTarifaId] = useState<number | "">("");
  const [tarifasPorPredioData, setTarifasPorPredioData] = useState<TarifaPredioGob[] | null>(null);
  const consultarTarifasPorPredio = async (predioId: number) => {
    setTarifasPorPredioData(null);
    try { setTarifasPorPredioData(await api.get(`/sigepro-ep/territorial/predial/tarifas-predio?predioId=${predioId}`)); } catch { setTarifasPorPredioData([]); }
  };

  // ---- Paz y salvo
  const [pazPredioId, setPazPredioId] = useState<number | "">("");
  const [pazResultado, setPazResultado] = useState<PazYSalvoGob | null>(null);
  const [errorPaz, setErrorPaz] = useState<string | null>(null);
  const consultarPazYSalvo = async () => {
    if (!pazPredioId) { setErrorPaz("Seleccione el predio."); return; }
    setErrorPaz(null);
    setPazResultado(null);
    try {
      setPazResultado(await api.get(`/sigepro-ep/territorial/predial/predios/${pazPredioId}/paz-y-salvo`));
    } catch (err) {
      setErrorPaz(err instanceof ApiError ? err.message : "No se pudo consultar el estado de paz y salvo.");
    }
  };

  // ---- Rete ICA
  const [modalReteicaAbierto, setModalReteicaAbierto] = useState(false);
  const [reteicaData, setReteicaData] = useState<ReteIcaGob[] | null>(null);
  const [periodoReteica, setPeriodoReteica] = useState("");
  const [pagandoReteica, setPagandoReteica] = useState<number | null>(null);
  const [cuentaPagoReteica, setCuentaPagoReteica] = useState<number | "">("");
  const [errorReteica, setErrorReteica] = useState<string | null>(null);
  const consultarReteica = async () => {
    setErrorReteica(null);
    try {
      setReteicaData(await api.get(`/sigepro-ep/territorial/ica/reteica${periodoReteica ? `?periodo=${periodoReteica}` : ""}`));
    } catch (err) {
      setReteicaData([]);
      setErrorReteica(err instanceof ApiError ? err.message : "No se pudo consultar Rete ICA.");
    }
  };
  const pagarReteica = async (id: number) => {
    if (!cuentaPagoReteica) { setErrorReteica("Seleccione la cuenta bancaria de destino."); return; }
    setErrorReteica(null);
    try {
      await api.post(`/sigepro-ep/territorial/ica/reteica/${id}/pagar`, { cuentaBancariaId: Number(cuentaPagoReteica) });
      setPagandoReteica(null);
      consultarReteica();
    } catch (err) {
      setErrorReteica(err instanceof ApiError ? err.message : "No se pudo registrar el pago.");
    }
  };

  // ---- Informes (contraloría + morosos)
  const [informesModulo, setInformesModulo] = useState<"predial" | "ica">("predial");
  const [informesVigencia, setInformesVigencia] = useState(new Date().getFullYear());
  const [informeContraloria, setInformeContraloria] = useState<InformeContraloriaGob | null>(null);
  const [informeMorosos, setInformeMorosos] = useState<CarteraItemGob[] | null>(null);
  const [errorInformes, setErrorInformes] = useState<string | null>(null);
  const consultarInformes = async () => {
    setErrorInformes(null);
    try {
      const [ic, im] = await Promise.all([
        api.get(`/sigepro-ep/territorial/${informesModulo}/informes/contraloria?vigenciaFiscal=${informesVigencia}`),
        api.get(`/sigepro-ep/territorial/${informesModulo}/informes/morosos`),
      ]);
      setInformeContraloria(ic);
      setInformeMorosos(im);
    } catch (err) {
      setErrorInformes(err instanceof ApiError ? err.message : "No se pudo generar el informe.");
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-4">
        Solo aplica a entidades territoriales (alcaldías/gobernaciones). Predial verificado contra la Ley 44 de 1990
        Art. 4 (tarifa 5‰-16‰ general, 1‰-16‰ para estratos 1-3 de bajo valor, hasta 33‰ lotes no edificados); ICA
        verificado contra la Ley 14 de 1983 / Decreto 1333 de 1986 (2‰-7‰ industrial, 2‰-10‰ comercial/servicios).
        Cada liquidación valida la tarifa capturada contra el rango legal correspondiente. La liquidación reconoce el
        ingreso y la cuenta por cobrar (causación); el recaudo posterior solo mueve caja.
      </p>

      <Tabs
        tabs={[
          { key: "predial", label: "Predial" },
          { key: "ica", label: "Industria y Comercio (ICA)" },
          { key: "cartera", label: "Cartera" },
          { key: "acuerdos", label: "Acuerdos de Pago" },
          { key: "cobro", label: "Gestión de Cobro" },
          { key: "deterioro", label: "Deterioro y Prescripciones" },
          { key: "conceptos", label: "Conceptos y Tarifas" },
          { key: "intereses", label: "Tabla de Intereses" },
          { key: "pazysalvo", label: "Paz y Salvo" },
          { key: "reteica", label: "Rete ICA" },
          { key: "informes", label: "Informes" },
        ]}
        activo={tabInterno} onChange={(k) => setTabInterno(k as any)}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 my-3">{error}</div>}

      {tabInterno === "predial" && (
        <div>
          <div className="flex justify-between items-end mb-3 mt-3">
            <h3 className="text-sm font-semibold text-slate-800">Predios</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalPredioAbierto(true)}>+ Nuevo predio</button>}
          </div>
          <div className="card overflow-hidden mb-4">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Cédula catastral</th><th className="table-th">Dirección</th><th className="table-th">Propietario</th>
                <th className="table-th">Tipo</th><th className="table-th">Estrato</th><th className="table-th">Avalúo</th>
              </tr></thead>
              <tbody>
                {predios.map((p) => (
                  <tr key={p.id} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs">{p.cedula_catastral}</td>
                    <td className="table-td text-xs">{p.direccion}</td>
                    <td className="table-td text-xs">{p.propietario_nombre}</td>
                    <td className="table-td text-xs">{TIPO_PREDIO_LABEL[p.tipo_predio] || p.tipo_predio}</td>
                    <td className="table-td">{p.estrato ?? "—"}</td>
                    <td className="table-td">{money(p.avaluo_catastral)}</td>
                  </tr>
                ))}
                {predios.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin predios registrados.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Liquidaciones de predial</h3>
            {puedeLiquidar && <button className="btn-primary" onClick={() => setModalLiqPredialAbierto(true)}>+ Liquidar predial</button>}
          </div>
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Liquidadas desde</label>
                  <input type="date" className="input-field" value={desdePredial} onChange={(e) => setDesdePredial(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaPredial} onChange={(e) => setHastaPredial(e.target.value)} /></div>
                {(desdePredial || hastaPredial) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdePredial(""); setHastaPredial(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/territorial/predial/liquidaciones/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdePredial || undefined, hasta: hastaPredial || undefined }}
                nombreBase={`predial_liquidaciones_ep${desdePredial ? `_${desdePredial}` : ""}${hastaPredial ? `_${hastaPredial}` : ""}`}
                label="Descargar liquidaciones"
              />
            </div>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Predio</th><th className="table-th">Vigencia</th><th className="table-th">Tarifa</th>
                <th className="table-th">Valor liquidado</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {liquidacionesPredial.map((l) => (
                  <tr key={l.id} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs">{l.cedula_catastral}</td>
                    <td className="table-td">{l.vigencia_fiscal}</td>
                    <td className="table-td">{l.tarifa_por_mil}‰</td>
                    <td className="table-td font-semibold">{money(l.valor_liquidado)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{l.estado}</span></td>
                    <td className="table-td">
                      {l.estado === "pendiente" && puedePagar && (
                        pagando?.tipo === "predial" && pagando.id === l.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPago} onChange={(e) => setCuentaPago(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={pagar}>Recaudar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagando(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagando({ tipo: "predial", id: l.id }); setError(null); }}>Recaudar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {liquidacionesPredial.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin liquidaciones de predial registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "ica" && (
        <div>
          <div className="flex justify-between items-end mb-3 mt-3">
            <h3 className="text-sm font-semibold text-slate-800">Contribuyentes de ICA</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalContribAbierto(true)}>+ Nuevo contribuyente</button>}
          </div>
          <div className="card overflow-hidden mb-4">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Contribuyente</th><th className="table-th">Actividad</th><th className="table-th">Tipo</th>
              </tr></thead>
              <tbody>
                {contribuyentesIca.map((c) => (
                  <tr key={c.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{c.tercero_nombre}</td>
                    <td className="table-td text-xs">{c.actividad_economica_nombre}</td>
                    <td className="table-td text-xs">{TIPO_ACTIVIDAD_ICA_LABEL[c.tipo_actividad] || c.tipo_actividad}</td>
                  </tr>
                ))}
                {contribuyentesIca.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-6">Sin contribuyentes registrados.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Liquidaciones de ICA</h3>
            {puedeLiquidar && <button className="btn-primary" onClick={() => setModalLiqIcaAbierto(true)}>+ Liquidar ICA</button>}
          </div>
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Liquidadas desde</label>
                  <input type="date" className="input-field" value={desdeIca} onChange={(e) => setDesdeIca(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaIca} onChange={(e) => setHastaIca(e.target.value)} /></div>
                {(desdeIca || hastaIca) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeIca(""); setHastaIca(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/territorial/ica/liquidaciones/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdeIca || undefined, hasta: hastaIca || undefined }}
                nombreBase={`ica_liquidaciones_ep${desdeIca ? `_${desdeIca}` : ""}${hastaIca ? `_${hastaIca}` : ""}`}
                label="Descargar liquidaciones"
              />
            </div>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Contribuyente</th><th className="table-th">Vigencia</th><th className="table-th">Ingresos base</th>
                <th className="table-th">Tarifa</th><th className="table-th">Valor liquidado</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {liquidacionesIca.map((l) => (
                  <tr key={l.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{l.tercero_nombre}</td>
                    <td className="table-td">{l.vigencia_fiscal}</td>
                    <td className="table-td">{money(l.ingresos_brutos_base)}</td>
                    <td className="table-td">{l.tarifa_por_mil}‰</td>
                    <td className="table-td font-semibold">{money(l.valor_liquidado)}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{l.estado}</span></td>
                    <td className="table-td">
                      {l.estado === "pendiente" && puedePagar && (
                        pagando?.tipo === "ica" && pagando.id === l.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPago} onChange={(e) => setCuentaPago(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={pagar}>Recaudar</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagando(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagando({ tipo: "ica", id: l.id }); setError(null); }}>Recaudar</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {liquidacionesIca.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin liquidaciones de ICA registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "cartera" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">
            Liquidaciones pendientes con antigüedad de mora e interés causado estimado según la Tabla de Intereses
            configurada para esa vigencia — el interés es informativo, no se contabiliza automáticamente.
          </p>
          <div className="flex items-end gap-2 mb-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
              <select className="input-field" value={carteraModulo} onChange={(e) => setCarteraModulo(e.target.value as any)}>
                <option value="predial">Predial</option>
                <option value="ica">ICA</option>
              </select>
            </div>
            <button className="btn-primary" onClick={() => consultarCartera(carteraModulo)}>Consultar</button>
          </div>
          {carteraData && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc">
                  <th className="table-th">{carteraModulo === "predial" ? "Predio" : "Contribuyente"}</th><th className="table-th">Vigencia</th>
                  <th className="table-th">Valor liquidado</th><th className="table-th">Días mora</th><th className="table-th">Interés causado</th>
                </tr></thead>
                <tbody>
                  {carteraData.map((c) => (
                    <tr key={c.id} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs">{carteraModulo === "predial" ? (c.cedula_catastral || c.direccion) : c.tercero_nombre}</td>
                      <td className="table-td">{c.vigencia_fiscal}</td>
                      <td className="table-td font-semibold">{money(c.valor_liquidado)}</td>
                      <td className="table-td">{c.diasMora}</td>
                      <td className="table-td">{money(c.interesCausado)}</td>
                    </tr>
                  ))}
                  {carteraData.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin cartera pendiente en este módulo.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tabInterno === "acuerdos" && (
        <div>
          <div className="flex justify-between items-end mb-3 mt-3">
            <h3 className="text-sm font-semibold text-slate-800">Acuerdos de pago</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalAcuerdoAbierto(true)}>+ Nuevo acuerdo de pago</button>}
          </div>
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                  <input type="date" className="input-field" value={desdeAcuerdos} onChange={(e) => setDesdeAcuerdos(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaAcuerdos} onChange={(e) => setHastaAcuerdos(e.target.value)} /></div>
                {(desdeAcuerdos || hastaAcuerdos) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeAcuerdos(""); setHastaAcuerdos(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/territorial/acuerdos-pago/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdeAcuerdos || undefined, hasta: hastaAcuerdos || undefined }}
                nombreBase={`acuerdos_pago_ep${desdeAcuerdos ? `_${desdeAcuerdos}` : ""}${hastaAcuerdos ? `_${hastaAcuerdos}` : ""}`}
                label="Descargar acuerdos"
              />
            </div>
          </div>
          {errorAcuerdo && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorAcuerdo}</div>}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Módulo</th><th className="table-th">Referencia</th><th className="table-th">Cuotas</th>
                <th className="table-th">Valor cuota</th><th className="table-th">Pagadas</th><th className="table-th">Fecha</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
              </tr></thead>
              <tbody>
                {acuerdosPago.map((a) => (
                  <tr key={a.id} className="border-b border-borderc last:border-0">
                    <td className="table-td text-xs">{MODULO_TERRITORIAL_LABEL[a.modulo]}</td>
                    <td className="table-td text-xs">{a.referencia || `#${a.liquidacion_id}`}</td>
                    <td className="table-td">{a.numero_cuotas}</td>
                    <td className="table-td font-semibold">{money(a.valor_cuota)}</td>
                    <td className="table-td">{a.cuotas_pagadas} / {a.numero_cuotas}</td>
                    <td className="table-td text-xs">{a.fecha_acuerdo}</td>
                    <td className="table-td"><span className={`badge ${ESTADO_ACUERDO_BADGE[a.estado]}`}>{a.estado}</span></td>
                    <td className="table-td">
                      {a.estado === "vigente" && puedePagar && (
                        pagandoCuota === a.id ? (
                          <div className="flex items-center gap-1">
                            <select className="input-field text-xs py-1" value={cuentaPagoCuota} onChange={(e) => setCuentaPagoCuota(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">Cuenta...</option>
                              {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                            </select>
                            <button className="btn-primary text-xs px-2 py-1" onClick={() => pagarCuota(a.id)}>Pagar cuota</button>
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagandoCuota(null)}>×</button>
                          </div>
                        ) : (
                          <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagandoCuota(a.id); setErrorAcuerdo(null); }}>Pagar cuota</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {acuerdosPago.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-6">Sin acuerdos de pago registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "cobro" && (
        <div>
          <div className="flex justify-between items-end mb-3 mt-3">
            <h3 className="text-sm font-semibold text-slate-800">Gestión de cobro</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalGestionAbierto(true)}>+ Nueva gestión</button>}
          </div>
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                  <input type="date" className="input-field" value={desdeCobro} onChange={(e) => setDesdeCobro(e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                  <input type="date" className="input-field" value={hastaCobro} onChange={(e) => setHastaCobro(e.target.value)} /></div>
                {(desdeCobro || hastaCobro) && (
                  <button type="button" className="btn-ghost text-xs" onClick={() => { setDesdeCobro(""); setHastaCobro(""); }}>Limpiar</button>
                )}
              </div>
              <DescargaReporteEP
                endpoint="/sigepro-ep/territorial/gestion-cobro/exportar" formatos={["pdf", "xlsx", "docx"]}
                params={{ desde: desdeCobro || undefined, hasta: hastaCobro || undefined }}
                nombreBase={`gestion_cobro_ep${desdeCobro ? `_${desdeCobro}` : ""}${hastaCobro ? `_${hastaCobro}` : ""}`}
                label="Descargar bitácora"
              />
            </div>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Módulo</th><th className="table-th">Referencia</th><th className="table-th">Fecha</th>
                <th className="table-th">Tipo</th><th className="table-th">Observaciones</th>
              </tr></thead>
              <tbody>
                {gestionesCobro.map((g) => (
                  <tr key={g.id} className="border-b border-borderc last:border-0">
                    <td className="table-td text-xs">{MODULO_TERRITORIAL_LABEL[g.modulo]}</td>
                    <td className="table-td text-xs">{g.referencia || `#${g.liquidacion_id}`}</td>
                    <td className="table-td text-xs">{g.fecha}</td>
                    <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{TIPO_GESTION_COBRO_LABEL[g.tipo_gestion] || g.tipo_gestion}</span></td>
                    <td className="table-td text-xs">{g.observaciones || "—"}</td>
                  </tr>
                ))}
                {gestionesCobro.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin gestiones de cobro registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "deterioro" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">
            Deterioro de cartera provisiona contablemente una liquidación pendiente sin anularla; Prescripción
            (Art. 817 E.T.) anula la liquidación de forma <strong>irreversible</strong> — úsela solo cuando la
            cartera ya no es exigible legalmente.
          </p>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Deterioro de cartera</h3>
            {puedeAjustar && <button className="btn-primary" onClick={() => setModalDeterioroAbierto(true)}>+ Registrar deterioro</button>}
          </div>
          <div className="card overflow-hidden mb-6">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Módulo</th><th className="table-th">Liquidación</th><th className="table-th">Valor</th><th className="table-th">Fecha</th><th className="table-th">Comprobante</th>
              </tr></thead>
              <tbody>
                {deterioroCartera.map((d) => (
                  <tr key={d.id} className="border-b border-borderc last:border-0">
                    <td className="table-td text-xs">{MODULO_TERRITORIAL_LABEL[d.modulo]}</td>
                    <td className="table-td text-xs">#{d.liquidacion_id}</td>
                    <td className="table-td font-semibold">{money(d.valor)}</td>
                    <td className="table-td text-xs">{d.fecha}</td>
                    <td className="table-td text-xs">{d.comprobante_id ? `#${d.comprobante_id}` : "—"}</td>
                  </tr>
                ))}
                {deterioroCartera.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin deterioros registrados.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Prescripciones (irreversible)</h3>
            {puedeAjustar && <button className="btn-primary" onClick={() => setModalPrescripcionAbierto(true)}>+ Prescribir cartera</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Módulo</th><th className="table-th">Liquidación</th><th className="table-th">Valor</th><th className="table-th">Fecha</th><th className="table-th">Motivo</th><th className="table-th">Comprobante</th>
              </tr></thead>
              <tbody>
                {prescripciones.map((p) => (
                  <tr key={p.id} className="border-b border-borderc last:border-0">
                    <td className="table-td text-xs">{MODULO_TERRITORIAL_LABEL[p.modulo]}</td>
                    <td className="table-td text-xs">#{p.liquidacion_id}</td>
                    <td className="table-td font-semibold">{money(p.valor)}</td>
                    <td className="table-td text-xs">{p.fecha}</td>
                    <td className="table-td text-xs">{p.motivo || "—"}</td>
                    <td className="table-td text-xs">{p.comprobante_id ? `#${p.comprobante_id}` : "—"}</td>
                  </tr>
                ))}
                {prescripciones.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-6">Sin prescripciones registradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "conceptos" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">Recargos/descuentos, estratos y tarifas del impuesto predial.</p>
          <Tabs
            tabs={[
              { key: "conceptos", label: "Conceptos" },
              { key: "porpredio", label: "Conceptos por predio" },
              { key: "estratos", label: "Estratos" },
              { key: "generales", label: "Tarifas generales" },
              { key: "porpredio-tarifa", label: "Tarifas por predio" },
            ]}
            activo={subtabConceptos} onChange={(k) => setSubtabConceptos(k as any)}
          />

          {subtabConceptos === "conceptos" && (
            <div>
              <div className="flex justify-between items-end mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Conceptos (recargos/descuentos)</h3>
                {puedeCrear && <button className="btn-primary" onClick={() => setModalConceptoAbierto(true)}>+ Nuevo concepto</button>}
              </div>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-borderc"><th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Tipo</th><th className="table-th">Activo</th></tr></thead>
                  <tbody>
                    {conceptosPredial.map((c) => (
                      <tr key={c.id} className="border-b border-borderc last:border-0">
                        <td className="table-td font-mono text-xs">{c.codigo}</td>
                        <td className="table-td">{c.nombre}</td>
                        <td className="table-td text-xs capitalize">{c.tipo}</td>
                        <td className="table-td">{c.activo ? "Sí" : "No"}</td>
                      </tr>
                    ))}
                    {conceptosPredial.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin conceptos configurados.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subtabConceptos === "porpredio" && (
            <div>
              <p className="text-xs text-textmuted mb-3">El recargo/descuento aplicado aquí se suma o resta automáticamente al liquidar esa vigencia del predio.</p>
              <div className="flex justify-between items-end mb-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1 max-w-sm">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Predio</label>
                    <select className="input-field" value={predioConceptosId} onChange={(e) => setPredioConceptosId(e.target.value ? Number(e.target.value) : "")}>
                      <option value="">Seleccionar...</option>
                      {predios.map((p) => <option key={p.id} value={p.id}>{p.cedula_catastral} — {p.direccion}</option>)}
                    </select>
                  </div>
                  <button className="btn-primary" onClick={() => predioConceptosId && consultarConceptosPredio(Number(predioConceptosId))} disabled={!predioConceptosId}>Consultar</button>
                </div>
                {puedeCrear && predioConceptosId && (
                  <button className="btn-ghost" onClick={() => setModalConceptoPredioAbierto(true)}>+ Aplicar concepto</button>
                )}
              </div>
              {conceptosPorPredioData && (
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Vigencia</th><th className="table-th">Concepto</th><th className="table-th">Tipo</th><th className="table-th">Valor</th></tr></thead>
                    <tbody>
                      {conceptosPorPredioData.map((c) => (
                        <tr key={c.id} className="border-b border-borderc last:border-0">
                          <td className="table-td">{c.vigencia_fiscal}</td>
                          <td className="table-td text-xs">{c.codigo} — {c.nombre}</td>
                          <td className="table-td text-xs capitalize">{c.tipo}</td>
                          <td className="table-td font-semibold">{c.tipo === "recargo" ? "+" : "-"}{money(c.valor)}</td>
                        </tr>
                      ))}
                      {conceptosPorPredioData.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin conceptos aplicados a este predio.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {subtabConceptos === "estratos" && (
            <div>
              <div className="flex justify-between items-end mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Estratos (catálogo, tarifa sugerida)</h3>
                {puedeCrear && <button className="btn-primary" onClick={() => setModalEstratoAbierto(true)}>+ Configurar estrato</button>}
              </div>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-borderc"><th className="table-th">Estrato</th><th className="table-th">Descripción</th><th className="table-th">Tarifa sugerida (‰)</th></tr></thead>
                  <tbody>
                    {estratosPredial.map((e) => (
                      <tr key={e.id} className="border-b border-borderc last:border-0">
                        <td className="table-td">{e.estrato}</td>
                        <td className="table-td text-xs">{e.descripcion || "—"}</td>
                        <td className="table-td">{e.tarifa_sugerida_por_mil != null ? `${e.tarifa_sugerida_por_mil}‰` : "—"}</td>
                      </tr>
                    ))}
                    {estratosPredial.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-6">Sin estratos configurados.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subtabConceptos === "generales" && (
            <div>
              <div className="flex justify-between items-end mb-3">
                <div className="flex items-end gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
                    <input className="input-field" type="number" value={vigenciaTarifaGeneral} onChange={(e) => setVigenciaTarifaGeneral(Number(e.target.value))} />
                  </div>
                  <button className="btn-primary" onClick={consultarTarifasGenerales}>Consultar</button>
                </div>
                {puedeCrear && <button className="btn-ghost" onClick={() => setModalTarifaGeneralAbierto(true)}>+ Configurar tarifa general</button>}
              </div>
              {tarifasGeneralesData && (
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-borderc">
                      <th className="table-th">Vigencia</th><th className="table-th">Tipo de predio</th><th className="table-th">Estrato desde</th><th className="table-th">Estrato hasta</th><th className="table-th">Tarifa (‰)</th>
                    </tr></thead>
                    <tbody>
                      {tarifasGeneralesData.map((t) => (
                        <tr key={t.id} className="border-b border-borderc last:border-0">
                          <td className="table-td">{t.vigencia_fiscal}</td>
                          <td className="table-td text-xs">{TIPO_PREDIO_LABEL[t.tipo_predio] || t.tipo_predio}</td>
                          <td className="table-td">{t.estrato_desde ?? "—"}</td>
                          <td className="table-td">{t.estrato_hasta ?? "—"}</td>
                          <td className="table-td font-semibold">{t.tarifa_por_mil}‰</td>
                        </tr>
                      ))}
                      {tarifasGeneralesData.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin tarifas generales para esa vigencia.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {subtabConceptos === "porpredio-tarifa" && (
            <div>
              <div className="flex justify-between items-end mb-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1 max-w-sm">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Predio</label>
                    <select className="input-field" value={predioTarifaId} onChange={(e) => setPredioTarifaId(e.target.value ? Number(e.target.value) : "")}>
                      <option value="">Seleccionar...</option>
                      {predios.map((p) => <option key={p.id} value={p.id}>{p.cedula_catastral} — {p.direccion}</option>)}
                    </select>
                  </div>
                  <button className="btn-primary" onClick={() => predioTarifaId && consultarTarifasPorPredio(Number(predioTarifaId))} disabled={!predioTarifaId}>Consultar</button>
                </div>
                {puedeCrear && <button className="btn-ghost" onClick={() => setModalTarifaPredioAbierto(true)}>+ Configurar tarifa por predio</button>}
              </div>
              {tarifasPorPredioData && (
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Vigencia</th><th className="table-th">Tarifa (‰)</th><th className="table-th">Motivo</th></tr></thead>
                    <tbody>
                      {tarifasPorPredioData.map((t) => (
                        <tr key={t.id} className="border-b border-borderc last:border-0">
                          <td className="table-td">{t.vigencia_fiscal}</td>
                          <td className="table-td font-semibold">{t.tarifa_por_mil}‰</td>
                          <td className="table-td text-xs">{t.motivo || "—"}</td>
                        </tr>
                      ))}
                      {tarifasPorPredioData.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-6">Sin tarifas específicas para este predio.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tabInterno === "intereses" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">
            Tasa mensual de interés de mora por vigencia fiscal, usada solo para el cálculo informativo de Cartera/
            Boletín de Deudores Morosos — no se contabiliza automáticamente.
          </p>
          <div className="flex justify-between items-end mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Tabla de intereses</h3>
            {puedeCrear && <button className="btn-primary" onClick={() => setModalTasaInteresAbierto(true)}>+ Configurar tasa</button>}
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc"><th className="table-th">Vigencia</th><th className="table-th">Tasa mensual (%)</th></tr></thead>
              <tbody>
                {tablaIntereses.map((t) => (
                  <tr key={t.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{t.vigencia_fiscal}</td>
                    <td className="table-td font-semibold">{t.tasa_mensual}%</td>
                  </tr>
                ))}
                {tablaIntereses.length === 0 && <tr><td colSpan={2} className="table-td text-center text-textmuted py-6">Sin tasas configuradas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tabInterno === "pazysalvo" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">Certificado de paz y salvo del impuesto predial de un predio.</p>
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1 max-w-sm">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Predio</label>
              <select className="input-field" value={pazPredioId} onChange={(e) => setPazPredioId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Seleccionar...</option>
                {predios.map((p) => <option key={p.id} value={p.id}>{p.cedula_catastral} — {p.direccion}</option>)}
              </select>
            </div>
            <button className="btn-primary" onClick={consultarPazYSalvo}>Consultar</button>
          </div>
          {errorPaz && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorPaz}</div>}
          {pazResultado && (
            <div className={`card p-6 border-2 ${pazResultado.pazYSalvo ? "border-accent" : "border-red-300"}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs text-textmuted">{pazResultado.predio.cedula_catastral} — {pazResultado.predio.direccion}</div>
                  <div className="text-sm text-slate-700">{pazResultado.predio.propietario_nombre}</div>
                </div>
                <span className={`badge text-sm ${pazResultado.pazYSalvo ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
                  {pazResultado.pazYSalvo ? "A PAZ Y SALVO" : "NO está a paz y salvo"}
                </span>
              </div>
              {!pazResultado.pazYSalvo && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 mb-2">Vigencias pendientes</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Vigencia</th><th className="table-th">Valor</th></tr></thead>
                    <tbody>
                      {pazResultado.vigenciasPendientes.map((v) => (
                        <tr key={v.vigenciaFiscal} className="border-b border-borderc last:border-0">
                          <td className="table-td">{v.vigenciaFiscal}</td>
                          <td className="table-td font-semibold">{money(v.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pazResultado.vigenciasPagadas.length > 0 && (
                <p className="text-xs text-textmuted mt-3">Vigencias pagadas: {pazResultado.vigenciasPagadas.join(", ")}.</p>
              )}
            </div>
          )}
        </div>
      )}

      {tabInterno === "reteica" && (
        <div>
          <p className="text-xs text-textmuted mb-3 mt-3">
            Declaración de Rete ICA: el contribuyente actúa como agente autorretenedor y declara la retención de
            ICA que le practicaron sus propios clientes.
          </p>
          <div className="flex justify-between items-end mb-3">
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Periodo (AAAA-MM, opcional)</label>
                <input className="input-field" placeholder="2026-08" value={periodoReteica} onChange={(e) => setPeriodoReteica(e.target.value)} />
              </div>
              <button className="btn-primary" onClick={consultarReteica}>Consultar</button>
            </div>
            {puedeLiquidar && <button className="btn-ghost" onClick={() => setModalReteicaAbierto(true)}>+ Declarar Rete ICA</button>}
          </div>
          {errorReteica && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorReteica}</div>}
          {reteicaData && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc">
                  <th className="table-th">Contribuyente</th><th className="table-th">Periodo</th><th className="table-th">Base</th>
                  <th className="table-th">Tarifa</th><th className="table-th">Retenido</th><th className="table-th">Estado</th><th className="table-th">Acción</th>
                </tr></thead>
                <tbody>
                  {reteicaData.map((r) => (
                    <tr key={r.id} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs">{r.tercero_nombre}</td>
                      <td className="table-td">{r.periodo}</td>
                      <td className="table-td">{money(r.base_retencion)}</td>
                      <td className="table-td">{r.tarifa_por_mil}‰</td>
                      <td className="table-td font-semibold">{money(r.valor_retenido)}</td>
                      <td className="table-td"><span className={`badge ${ESTADO_RETEICA_BADGE[r.estado]}`}>{r.estado}</span></td>
                      <td className="table-td">
                        {r.estado === "pendiente" && puedePagar && (
                          pagandoReteica === r.id ? (
                            <div className="flex items-center gap-1">
                              <select className="input-field text-xs py-1" value={cuentaPagoReteica} onChange={(e) => setCuentaPagoReteica(e.target.value ? Number(e.target.value) : "")}>
                                <option value="">Cuenta...</option>
                                {cuentasBancarias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                              </select>
                              <button className="btn-primary text-xs px-2 py-1" onClick={() => pagarReteica(r.id)}>Pagar</button>
                              <button className="btn-ghost text-xs px-2 py-1" onClick={() => setPagandoReteica(null)}>×</button>
                            </div>
                          ) : (
                            <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setPagandoReteica(r.id); setErrorReteica(null); }}>Pagar</button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {reteicaData.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-6">Sin declaraciones de Rete ICA para ese filtro.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tabInterno === "informes" && (
        <div>
          <div className="flex items-end gap-2 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
              <select className="input-field" value={informesModulo} onChange={(e) => setInformesModulo(e.target.value as any)}>
                <option value="predial">Predial</option>
                <option value="ica">ICA</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
              <input className="input-field" type="number" value={informesVigencia} onChange={(e) => setInformesVigencia(Number(e.target.value))} />
            </div>
            <button className="btn-primary" onClick={consultarInformes}>Generar</button>
          </div>
          {errorInformes && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorInformes}</div>}
          {informeContraloria && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Informe de Contraloría — vigencia {informeContraloria.vigenciaFiscal}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="card p-4"><div className="text-xs text-textmuted">Liquidaciones</div><div className="text-lg font-bold text-slate-800">{informeContraloria.totalLiquidaciones}</div></div>
                <div className="card p-4"><div className="text-xs text-textmuted">Total liquidado</div><div className="text-lg font-bold text-slate-800">{money(informeContraloria.totalLiquidado)}</div></div>
                <div className="card p-4"><div className="text-xs text-textmuted">Total recaudado</div><div className="text-lg font-bold text-slate-800">{money(informeContraloria.totalRecaudado)}</div></div>
                <div className="card p-4"><div className="text-xs text-textmuted">Total pendiente</div><div className="text-lg font-bold text-slate-800">{money(informeContraloria.totalPendiente)}</div></div>
                <div className="card p-4"><div className="text-xs text-textmuted">Total anulado</div><div className="text-lg font-bold text-slate-800">{money(informeContraloria.totalAnulado)}</div></div>
                <div className="card p-4"><div className="text-xs text-textmuted">% de recaudo</div><div className="text-lg font-bold text-accent-dark">{informeContraloria.porcentajeRecaudo}%</div></div>
              </div>
            </div>
          )}
          {informeMorosos && (
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Boletín de Deudores Morosos</h3>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-borderc">
                    <th className="table-th">{informesModulo === "predial" ? "Predio" : "Contribuyente"}</th><th className="table-th">Vigencia</th>
                    <th className="table-th">Valor liquidado</th><th className="table-th">Días mora</th><th className="table-th">Interés causado</th>
                  </tr></thead>
                  <tbody>
                    {informeMorosos.map((m) => (
                      <tr key={m.id} className="border-b border-borderc last:border-0">
                        <td className="table-td text-xs">{informesModulo === "predial" ? (m.cedula_catastral || m.direccion) : m.tercero_nombre}</td>
                        <td className="table-td">{m.vigencia_fiscal}</td>
                        <td className="table-td font-semibold">{money(m.valor_liquidado)}</td>
                        <td className="table-td">{m.diasMora}</td>
                        <td className="table-td">{money(m.interesCausado)}</td>
                      </tr>
                    ))}
                    {informeMorosos.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin deudores morosos en este módulo.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {modalPredioAbierto && (
        <ModalPredioGob terceros={terceros} recargarTerceros={recargar} onClose={() => setModalPredioAbierto(false)} onCreado={() => { setModalPredioAbierto(false); recargar(); }} />
      )}
      {modalLiqPredialAbierto && (
        <ModalLiquidarPredialGob predios={predios} onClose={() => setModalLiqPredialAbierto(false)} onCreado={() => { setModalLiqPredialAbierto(false); recargar(); }} />
      )}
      {modalContribAbierto && (
        <ModalContribuyenteIcaGob terceros={terceros} recargarTerceros={recargar} onClose={() => setModalContribAbierto(false)} onCreado={() => { setModalContribAbierto(false); recargar(); }} />
      )}
      {modalLiqIcaAbierto && (
        <ModalLiquidarIcaGob contribuyentes={contribuyentesIca} onClose={() => setModalLiqIcaAbierto(false)} onCreado={() => { setModalLiqIcaAbierto(false); recargar(); }} />
      )}
      {modalAcuerdoAbierto && (
        <ModalAcuerdoPagoGob
          liquidacionesPredial={liquidacionesPredial} liquidacionesIca={liquidacionesIca}
          onClose={() => setModalAcuerdoAbierto(false)} onCreado={() => { setModalAcuerdoAbierto(false); recargar(); }}
        />
      )}
      {modalGestionAbierto && (
        <ModalGestionCobroGob
          liquidacionesPredial={liquidacionesPredial} liquidacionesIca={liquidacionesIca}
          onClose={() => setModalGestionAbierto(false)} onCreado={() => { setModalGestionAbierto(false); recargar(); }}
        />
      )}
      {modalDeterioroAbierto && (
        <ModalDeterioroCarteraGob
          liquidacionesPredial={liquidacionesPredial} liquidacionesIca={liquidacionesIca}
          onClose={() => setModalDeterioroAbierto(false)} onCreado={() => { setModalDeterioroAbierto(false); recargar(); }}
        />
      )}
      {modalPrescripcionAbierto && (
        <ModalPrescripcionGob
          liquidacionesPredial={liquidacionesPredial} liquidacionesIca={liquidacionesIca}
          onClose={() => setModalPrescripcionAbierto(false)} onCreado={() => { setModalPrescripcionAbierto(false); recargar(); }}
        />
      )}
      {modalTasaInteresAbierto && (
        <ModalTasaInteresGob onClose={() => setModalTasaInteresAbierto(false)} onCreado={() => { setModalTasaInteresAbierto(false); recargar(); }} />
      )}
      {modalConceptoAbierto && (
        <ModalConceptoPredialGob onClose={() => setModalConceptoAbierto(false)} onCreado={() => { setModalConceptoAbierto(false); recargar(); }} />
      )}
      {modalConceptoPredioAbierto && predioConceptosId && (
        <ModalConceptoPredioGob
          predioId={Number(predioConceptosId)} conceptos={conceptosPredial}
          onClose={() => setModalConceptoPredioAbierto(false)}
          onCreado={() => { setModalConceptoPredioAbierto(false); consultarConceptosPredio(Number(predioConceptosId)); }}
        />
      )}
      {modalEstratoAbierto && (
        <ModalEstratoGob onClose={() => setModalEstratoAbierto(false)} onCreado={() => { setModalEstratoAbierto(false); recargar(); }} />
      )}
      {modalTarifaGeneralAbierto && (
        <ModalTarifaGeneralGob onClose={() => setModalTarifaGeneralAbierto(false)} onCreado={() => { setModalTarifaGeneralAbierto(false); consultarTarifasGenerales(); }} />
      )}
      {modalTarifaPredioAbierto && predioTarifaId && (
        <ModalTarifaPredioGob
          predioId={Number(predioTarifaId)}
          onClose={() => setModalTarifaPredioAbierto(false)}
          onCreado={() => { setModalTarifaPredioAbierto(false); consultarTarifasPorPredio(Number(predioTarifaId)); }}
        />
      )}
      {modalReteicaAbierto && (
        <ModalReteicaGob contribuyentes={contribuyentesIca} onClose={() => setModalReteicaAbierto(false)} onCreado={() => { setModalReteicaAbierto(false); consultarReteica(); recargar(); }} />
      )}
    </div>
  );
}

function ModalPredioGob({ terceros, recargarTerceros, onClose, onCreado }: {
  terceros: TerceroGob[]; recargarTerceros: () => void; onClose: () => void; onCreado: () => void;
}) {
  const [cedulaCatastral, setCedulaCatastral] = useState("");
  const [direccion, setDireccion] = useState("");
  const [propietarioTerceroId, setPropietarioTerceroId] = useState<number | "">("");
  const [modalTerceroAbierto, setModalTerceroAbierto] = useState(false);
  const [tipoPredio, setTipoPredio] = useState("residencial");
  const [estrato, setEstrato] = useState(3);
  const [avaluoCatastral, setAvaluoCatastral] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!propietarioTerceroId) { setError("Seleccione el propietario."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/predios", {
        cedulaCatastral, direccion, propietarioTerceroId: Number(propietarioTerceroId), tipoPredio,
        estrato: ["residencial", "rural"].includes(tipoPredio) ? estrato : null, avaluoCatastral,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el predio.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo predio</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cédula catastral</label>
          <input className="input-field" value={cedulaCatastral} onChange={(e) => setCedulaCatastral(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Dirección</label>
          <input className="input-field" value={direccion} onChange={(e) => setDireccion(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Propietario (tercero)</label>
          <div className="flex gap-2">
            <select className="input-field" value={propietarioTerceroId} onChange={(e) => setPropietarioTerceroId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {terceros.map((t) => <option key={t.id} value={t.id}>{t.nombre} ({t.numero_documento})</option>)}
            </select>
            <button type="button" className="btn-ghost text-xs whitespace-nowrap" onClick={() => setModalTerceroAbierto(true)}>+ Nuevo</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de predio</label>
            <select className="input-field" value={tipoPredio} onChange={(e) => setTipoPredio(e.target.value)}>
              {Object.entries(TIPO_PREDIO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {["residencial", "rural"].includes(tipoPredio) && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Estrato</label>
              <select className="input-field" value={estrato} onChange={(e) => setEstrato(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Avalúo catastral</label>
          <input className="input-field" type="number" min={0} step="1000" value={avaluoCatastral} onChange={(e) => setAvaluoCatastral(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar predio"}</button>
        </div>
      </form>
      {modalTerceroAbierto && (
        <ModalTerceroGob onClose={() => setModalTerceroAbierto(false)} onCreado={(id) => { setPropietarioTerceroId(id); recargarTerceros(); setModalTerceroAbierto(false); }} />
      )}
    </div>
  );
}

function ModalLiquidarPredialGob({ predios, onClose, onCreado }: { predios: PredioGob[]; onClose: () => void; onCreado: () => void }) {
  const [predioId, setPredioId] = useState<number | "">("");
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [tarifaPorMil, setTarifaPorMil] = useState(6.5);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const liquidar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!predioId) { setError("Seleccione el predio."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/liquidaciones", { predioId: Number(predioId), vigenciaFiscal, tarifaPorMil });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo liquidar el predial.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={liquidar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Liquidar predial</h3>
        <p className="text-xs text-textmuted">
          Tarifa legal (Ley 44 de 1990 Art. 4): 5‰-16‰ general; 1‰-16‰ para residencial/rural estratos 1-3; hasta
          33‰ lotes no edificados.
        </p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Predio</label>
          <select className="input-field" value={predioId} onChange={(e) => setPredioId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {predios.map((p) => <option key={p.id} value={p.id}>{p.cedula_catastral} — {p.direccion}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa (por mil)</label>
            <input className="input-field" type="number" min={0} step="0.1" value={tarifaPorMil} onChange={(e) => setTarifaPorMil(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Liquidando..." : "Liquidar"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalContribuyenteIcaGob({ terceros, recargarTerceros, onClose, onCreado }: {
  terceros: TerceroGob[]; recargarTerceros: () => void; onClose: () => void; onCreado: () => void;
}) {
  const [terceroId, setTerceroId] = useState<number | "">("");
  const [modalTerceroAbierto, setModalTerceroAbierto] = useState(false);
  const [actividadEconomicaCodigo, setActividadEconomicaCodigo] = useState("");
  const [actividadEconomicaNombre, setActividadEconomicaNombre] = useState("");
  const [tipoActividad, setTipoActividad] = useState("comercial");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!terceroId) { setError("Seleccione el tercero."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/ica/contribuyentes", {
        terceroId: Number(terceroId), actividadEconomicaCodigo: actividadEconomicaCodigo || null, actividadEconomicaNombre, tipoActividad,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el contribuyente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo contribuyente de ICA</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tercero</label>
          <div className="flex gap-2">
            <select className="input-field" value={terceroId} onChange={(e) => setTerceroId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Seleccionar...</option>
              {terceros.map((t) => <option key={t.id} value={t.id}>{t.nombre} ({t.numero_documento})</option>)}
            </select>
            <button type="button" className="btn-ghost text-xs whitespace-nowrap" onClick={() => setModalTerceroAbierto(true)}>+ Nuevo</button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Actividad económica</label>
          <input className="input-field" value={actividadEconomicaNombre} onChange={(e) => setActividadEconomicaNombre(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Código CIIU (opcional)</label>
            <input className="input-field" value={actividadEconomicaCodigo} onChange={(e) => setActividadEconomicaCodigo(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de actividad</label>
            <select className="input-field" value={tipoActividad} onChange={(e) => setTipoActividad(e.target.value)}>
              {Object.entries(TIPO_ACTIVIDAD_ICA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar contribuyente"}</button>
        </div>
      </form>
      {modalTerceroAbierto && (
        <ModalTerceroGob onClose={() => setModalTerceroAbierto(false)} onCreado={(id) => { setTerceroId(id); recargarTerceros(); setModalTerceroAbierto(false); }} />
      )}
    </div>
  );
}

function ModalLiquidarIcaGob({ contribuyentes, onClose, onCreado }: { contribuyentes: ContribuyenteIcaGob[]; onClose: () => void; onCreado: () => void }) {
  const [contribuyenteId, setContribuyenteId] = useState<number | "">("");
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [ingresosBrutosBase, setIngresosBrutosBase] = useState(0);
  const [tarifaPorMil, setTarifaPorMil] = useState(7);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const liquidar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contribuyenteId) { setError("Seleccione el contribuyente."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/ica/liquidaciones", { contribuyenteId: Number(contribuyenteId), vigenciaFiscal, ingresosBrutosBase, tarifaPorMil });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo liquidar el ICA.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={liquidar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Liquidar ICA</h3>
        <p className="text-xs text-textmuted">Tarifa legal (Ley 14 de 1983 / Decreto 1333 de 1986): 2‰-7‰ industrial; 2‰-10‰ comercial/servicios.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Contribuyente</label>
          <select className="input-field" value={contribuyenteId} onChange={(e) => setContribuyenteId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {contribuyentes.map((c) => <option key={c.id} value={c.id}>{c.tercero_nombre} — {c.actividad_economica_nombre}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa (por mil)</label>
            <input className="input-field" type="number" min={0} step="0.1" value={tarifaPorMil} onChange={(e) => setTarifaPorMil(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Ingresos brutos base</label>
          <input className="input-field" type="number" min={0} step="1000" value={ingresosBrutosBase} onChange={(e) => setIngresosBrutosBase(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Liquidando..." : "Liquidar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---- Alineación con TransforWeb: modales de Acuerdos de Pago, Gestión de Cobro,
// Deterioro/Prescripciones, Tabla de Intereses, Conceptos/Estratos/Tarifas
// (predial) y Declaración Rete ICA.

function ModalAcuerdoPagoGob({ liquidacionesPredial, liquidacionesIca, onClose, onCreado }: {
  liquidacionesPredial: LiquidacionPredialGob[]; liquidacionesIca: LiquidacionIcaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [modulo, setModulo] = useState<"predial" | "ica">("predial");
  const [liquidacionId, setLiquidacionId] = useState<number | "">("");
  const [numeroCuotas, setNumeroCuotas] = useState(2);
  const [fechaAcuerdo, setFechaAcuerdo] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const pendientes = modulo === "predial"
    ? liquidacionesPredial.filter((l) => l.estado === "pendiente")
    : liquidacionesIca.filter((l) => l.estado === "pendiente");

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!liquidacionId) { setError("Seleccione la liquidación pendiente a formalizar."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/acuerdos-pago", { modulo, liquidacionId: Number(liquidacionId), numeroCuotas, fechaAcuerdo });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo formalizar el acuerdo de pago.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo acuerdo de pago</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
          <select className="input-field" value={modulo} onChange={(e) => { setModulo(e.target.value as any); setLiquidacionId(""); }}>
            <option value="predial">Predial</option>
            <option value="ica">ICA</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Liquidación pendiente</label>
          <select className="input-field" value={liquidacionId} onChange={(e) => setLiquidacionId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {modulo === "predial"
              ? (pendientes as LiquidacionPredialGob[]).map((l) => <option key={l.id} value={l.id}>{l.cedula_catastral} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)
              : (pendientes as LiquidacionIcaGob[]).map((l) => <option key={l.id} value={l.id}>{l.tercero_nombre} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Número de cuotas</label>
            <input className="input-field" type="number" min={1} value={numeroCuotas} onChange={(e) => setNumeroCuotas(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha del acuerdo</label>
            <input className="input-field" type="date" value={fechaAcuerdo} onChange={(e) => setFechaAcuerdo(e.target.value)} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Formalizando..." : "Formalizar acuerdo"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalGestionCobroGob({ liquidacionesPredial, liquidacionesIca, onClose, onCreado }: {
  liquidacionesPredial: LiquidacionPredialGob[]; liquidacionesIca: LiquidacionIcaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [modulo, setModulo] = useState<"predial" | "ica">("predial");
  const [liquidacionId, setLiquidacionId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [tipoGestion, setTipoGestion] = useState("llamada");
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const liquidaciones = modulo === "predial" ? liquidacionesPredial : liquidacionesIca;

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!liquidacionId) { setError("Seleccione la liquidación."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/gestion-cobro", { modulo, liquidacionId: Number(liquidacionId), fecha, tipoGestion, observaciones: observaciones || null });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar la gestión de cobro.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva gestión de cobro</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
          <select className="input-field" value={modulo} onChange={(e) => { setModulo(e.target.value as any); setLiquidacionId(""); }}>
            <option value="predial">Predial</option>
            <option value="ica">ICA</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Liquidación</label>
          <select className="input-field" value={liquidacionId} onChange={(e) => setLiquidacionId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {modulo === "predial"
              ? (liquidaciones as LiquidacionPredialGob[]).map((l) => <option key={l.id} value={l.id}>{l.cedula_catastral} — vigencia {l.vigencia_fiscal}</option>)
              : (liquidaciones as LiquidacionIcaGob[]).map((l) => <option key={l.id} value={l.id}>{l.tercero_nombre} — vigencia {l.vigencia_fiscal}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de gestión</label>
            <select className="input-field" value={tipoGestion} onChange={(e) => setTipoGestion(e.target.value)}>
              {Object.entries(TIPO_GESTION_COBRO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones (opcional)</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar gestión"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalDeterioroCarteraGob({ liquidacionesPredial, liquidacionesIca, onClose, onCreado }: {
  liquidacionesPredial: LiquidacionPredialGob[]; liquidacionesIca: LiquidacionIcaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [modulo, setModulo] = useState<"predial" | "ica">("predial");
  const [liquidacionId, setLiquidacionId] = useState<number | "">("");
  const [valor, setValor] = useState(0);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const pendientes = modulo === "predial"
    ? liquidacionesPredial.filter((l) => l.estado === "pendiente")
    : liquidacionesIca.filter((l) => l.estado === "pendiente");

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!liquidacionId) { setError("Seleccione la liquidación pendiente a provisionar."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/deterioro-cartera", { modulo, liquidacionId: Number(liquidacionId), valor, fecha });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el deterioro de cartera.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Registrar deterioro de cartera</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
          <select className="input-field" value={modulo} onChange={(e) => { setModulo(e.target.value as any); setLiquidacionId(""); }}>
            <option value="predial">Predial</option>
            <option value="ica">ICA</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Liquidación pendiente</label>
          <select className="input-field" value={liquidacionId} onChange={(e) => setLiquidacionId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {modulo === "predial"
              ? (pendientes as LiquidacionPredialGob[]).map((l) => <option key={l.id} value={l.id}>{l.cedula_catastral} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)
              : (pendientes as LiquidacionIcaGob[]).map((l) => <option key={l.id} value={l.id}>{l.tercero_nombre} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor (no puede superar el valor liquidado)</label>
            <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar deterioro"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalPrescripcionGob({ liquidacionesPredial, liquidacionesIca, onClose, onCreado }: {
  liquidacionesPredial: LiquidacionPredialGob[]; liquidacionesIca: LiquidacionIcaGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [modulo, setModulo] = useState<"predial" | "ica">("predial");
  const [liquidacionId, setLiquidacionId] = useState<number | "">("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const pendientes = modulo === "predial"
    ? liquidacionesPredial.filter((l) => l.estado === "pendiente")
    : liquidacionesIca.filter((l) => l.estado === "pendiente");

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!liquidacionId) { setError("Seleccione la liquidación pendiente a prescribir."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/prescripciones", { modulo, liquidacionId: Number(liquidacionId), fecha, motivo });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar la prescripción.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Prescribir cartera</h3>
        <p className="text-xs text-red-700 font-semibold">
          Acción irreversible (Art. 817 E.T.): anula la liquidación de forma definitiva. Úsela solo cuando la
          cartera ya perdió su exigibilidad legal.
        </p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
          <select className="input-field" value={modulo} onChange={(e) => { setModulo(e.target.value as any); setLiquidacionId(""); }}>
            <option value="predial">Predial</option>
            <option value="ica">ICA</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Liquidación pendiente</label>
          <select className="input-field" value={liquidacionId} onChange={(e) => setLiquidacionId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {modulo === "predial"
              ? (pendientes as LiquidacionPredialGob[]).map((l) => <option key={l.id} value={l.id}>{l.cedula_catastral} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)
              : (pendientes as LiquidacionIcaGob[]).map((l) => <option key={l.id} value={l.id}>{l.tercero_nombre} — vigencia {l.vigencia_fiscal} — {money(l.valor_liquidado)}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo</label>
            <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: 5 años sin gestión de cobro" required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Prescribiendo..." : "Confirmar prescripción (irreversible)"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalTasaInteresGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [tasaMensual, setTasaMensual] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/tabla-intereses", { vigenciaFiscal, tasaMensual });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo configurar la tasa de interés.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Configurar tasa de interés</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
          <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tasa mensual (%)</label>
          <input className="input-field" type="number" min={0} step="0.01" value={tasaMensual} onChange={(e) => setTasaMensual(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar (vigencia existente se actualiza)"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalConceptoPredialGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState<"recargo" | "descuento">("recargo");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/conceptos", { codigo, nombre, tipo });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el concepto.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo concepto predial</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
          <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
          <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value as any)}>
            <option value="recargo">Recargo</option>
            <option value="descuento">Descuento</option>
          </select>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear concepto"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalConceptoPredioGob({ predioId, conceptos, onClose, onCreado }: {
  predioId: number; conceptos: ConceptoPredialGob[]; onClose: () => void; onCreado: () => void;
}) {
  const [conceptoId, setConceptoId] = useState<number | "">("");
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [valor, setValor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conceptoId) { setError("Seleccione el concepto a aplicar."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/sigepro-ep/territorial/predial/predios/${predioId}/conceptos`, { conceptoId: Number(conceptoId), vigenciaFiscal, valor });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo aplicar el concepto.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Aplicar concepto al predio</h3>
        <p className="text-xs text-textmuted">Se aplica automáticamente al liquidar esa vigencia del predio.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Concepto</label>
          <select className="input-field" value={conceptoId} onChange={(e) => setConceptoId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {conceptos.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre} ({c.tipo})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
            <input className="input-field" type="number" min={0} step="1000" value={valor} onChange={(e) => setValor(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Aplicando..." : "Aplicar concepto"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalEstratoGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [estrato, setEstrato] = useState(1);
  const [descripcion, setDescripcion] = useState("");
  const [tarifaSugeridaPorMil, setTarifaSugeridaPorMil] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/estratos", { estrato, descripcion: descripcion || null, tarifaSugeridaPorMil: tarifaSugeridaPorMil === "" ? null : tarifaSugeridaPorMil });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo configurar el estrato.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Configurar estrato</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Estrato</label>
          <select className="input-field" value={estrato} onChange={(e) => setEstrato(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (opcional)</label>
          <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa sugerida (por mil, opcional)</label>
          <input className="input-field" type="number" min={0} step="0.1" value={tarifaSugeridaPorMil} onChange={(e) => setTarifaSugeridaPorMil(e.target.value ? Number(e.target.value) : "")} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar (estrato existente se actualiza)"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalTarifaGeneralGob({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [tipoPredio, setTipoPredio] = useState("residencial");
  const [estratoDesde, setEstratoDesde] = useState<number | "">("");
  const [estratoHasta, setEstratoHasta] = useState<number | "">("");
  const [tarifaPorMil, setTarifaPorMil] = useState(6.5);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/tarifas-generales", {
        vigenciaFiscal, tipoPredio, estratoDesde: estratoDesde === "" ? null : estratoDesde,
        estratoHasta: estratoHasta === "" ? null : estratoHasta, tarifaPorMil,
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo configurar la tarifa general.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Configurar tarifa general predial</h3>
        <p className="text-xs text-textmuted">Rango de estrato opcional — deje ambos en blanco para que aplique a todo el tipo de predio.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de predio</label>
            <select className="input-field" value={tipoPredio} onChange={(e) => setTipoPredio(e.target.value)}>
              {Object.entries(TIPO_PREDIO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Estrato desde</label>
            <select className="input-field" value={estratoDesde} onChange={(e) => setEstratoDesde(e.target.value ? Number(e.target.value) : "")}>
              <option value="">—</option>
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Estrato hasta</label>
            <select className="input-field" value={estratoHasta} onChange={(e) => setEstratoHasta(e.target.value ? Number(e.target.value) : "")}>
              <option value="">—</option>
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa (‰)</label>
            <input className="input-field" type="number" min={0} step="0.1" value={tarifaPorMil} onChange={(e) => setTarifaPorMil(Number(e.target.value))} required />
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Configurar tarifa"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalTarifaPredioGob({ predioId, onClose, onCreado }: { predioId: number; onClose: () => void; onCreado: () => void }) {
  const [vigenciaFiscal, setVigenciaFiscal] = useState(new Date().getFullYear());
  const [tarifaPorMil, setTarifaPorMil] = useState(6.5);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/predial/tarifas-predio", { predioId, vigenciaFiscal, tarifaPorMil, motivo: motivo || null });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo configurar la tarifa por predio.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Configurar tarifa específica del predio</h3>
        <p className="text-xs text-textmuted">Tiene prioridad sobre la tarifa general y la sugerida por estrato al liquidar este predio.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia fiscal</label>
            <input className="input-field" type="number" value={vigenciaFiscal} onChange={(e) => setVigenciaFiscal(Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa (‰)</label>
            <input className="input-field" type="number" min={0} step="0.1" value={tarifaPorMil} onChange={(e) => setTarifaPorMil(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo (opcional)</label>
          <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Configurar tarifa"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalReteicaGob({ contribuyentes, onClose, onCreado }: { contribuyentes: ContribuyenteIcaGob[]; onClose: () => void; onCreado: () => void }) {
  const [contribuyenteId, setContribuyenteId] = useState<number | "">("");
  const [periodo, setPeriodo] = useState("");
  const [baseRetencion, setBaseRetencion] = useState(0);
  const [tarifaPorMil, setTarifaPorMil] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contribuyenteId) { setError("Seleccione el contribuyente."); return; }
    if (!periodo.trim()) { setError("El periodo (AAAA-MM) es obligatorio."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/sigepro-ep/territorial/ica/reteica", { contribuyenteId: Number(contribuyenteId), periodo, baseRetencion, tarifaPorMil });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo declarar la Rete ICA.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Declarar Rete ICA</h3>
        <p className="text-xs text-textmuted">Tarifa legal (Ley 14 de 1983 / Decreto 1333 de 1986): 2‰-7‰ industrial; 2‰-10‰ comercial/servicios.</p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Contribuyente</label>
          <select className="input-field" value={contribuyenteId} onChange={(e) => setContribuyenteId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {contribuyentes.map((c) => <option key={c.id} value={c.id}>{c.tercero_nombre} — {c.actividad_economica_nombre}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Periodo (AAAA-MM)</label>
            <input className="input-field" placeholder="2026-08" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa (por mil)</label>
            <input className="input-field" type="number" min={0} step="0.1" value={tarifaPorMil} onChange={(e) => setTarifaPorMil(Number(e.target.value))} required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Base de retención</label>
          <input className="input-field" type="number" min={0} step="1000" value={baseRetencion} onChange={(e) => setBaseRetencion(Number(e.target.value))} required />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Declarando..." : "Declarar"}</button>
        </div>
      </form>
    </div>
  );
}
